// src/pages/nafed/branch/StateBranchDashboard.js
// BUILD: STATE_BRANCH_NORMALIZED_PACS_GODOWN_REPORT_V10_2026_08_24

import React, { useEffect, useMemo, useRef, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../../../firebaseConfig";

import {
  getNafedDashboardData,
  formatMetricNumber,
  formatCapacity,
  formatCurrency,
} from "../NafedDashboardDataEngine";
import {
  prepareSignatureImage,
  printHiringAssurance,
} from "./hiringAssuranceUtils";

const LOGO_PATH = "/images/nafed-logo.png";

const MODULES = [
  {
    key: "overview",
    title: "Dashboard Overview",
    desc: "State live MIS and analytics.",
    icon: "▣",
    color: "green",
  },
  {
    key: "stateRegistrarApproval",
    title: "State Registrar Approval",
    desc: "Approve State Registrar and enable authorised login.",
    icon: "SR",
    color: "blue",
  },
  {
    key: "pmcApproval",
    title: "PMC Approval",
    desc: "Approve PMC agencies.",
    icon: "✓",
    color: "green",
    path: "/nafed/branch/pmc-approval",
  },
  {
    key: "pacsAllocation",
    title: "PACS–PMC Allocation",
    desc: "Allocate or re-allocate each approved PACS to an approved PMC.",
    icon: "↔",
    color: "orange",
  },
  {
    key: "applications",
    title: "PACS Applications",
    desc: "View all PACS applications handled by PMCs in this State Branch.",
    icon: "▤",
    color: "blue",
  },
  {
    key: "pacsDocuments",
    title: "PACS Documents",
    desc: "View every PACS document, missing-file status and PMC scrutiny stage.",
    icon: "DOC",
    color: "blue",
  },
  {
    key: "registrationMis",
    title: "Registration MIS",
    desc: "Live register of PACS, PMCs and approved technical vendors.",
    icon: "MIS",
    color: "purple",
  },
  {
    key: "mouExecution",
    title: "MOU Status & Copies",
    desc: "Read-only tracking and download access for PMC-PACS MOU copies.",
    icon: "MOU",
    color: "teal",
  },
  {
    key: "hiringAssurance",
    title: "Hiring Assurance",
    desc: "Upload the authorised State Branch signature and publish an official assurance letter directly to the PACS dashboard.",
    icon: "HA",
    color: "green",
  },
  {
    key: "dpr",
    title: "DPR Status",
    desc: "DPR, survey and estimate tracking.",
    icon: "▦",
    color: "purple",
    path: "/nafed/branch/dpr",
  },
  {
    key: "tender",
    title: "Tender Status",
    desc: "Tender progress and contractor status.",
    icon: "▥",
    color: "orange",
    path: "/nafed/branch/tender",
  },
  {
    key: "construction",
    title: "Construction Progress",
    desc: "Progress monitoring and milestones.",
    icon: "▨",
    color: "blue",
    path: "/nafed/branch/construction",
  },
  {
    key: "raBills",
    title: "RA Bills",
    desc: "RA bill monitoring and status.",
    icon: "₹",
    color: "pink",
    path: "/nafed/branch/ra-bills",
  },
  {
    key: "stateReports",
    title: "State Reports",
    desc: "State project reports.",
    icon: "▰",
    color: "teal",
    path: "/nafed/branch/state-reports",
  },
  {
    key: "pmcReports",
    title: "PMC Reports",
    desc: "PMC-wise reports.",
    icon: "▧",
    color: "green",
    path: "/nafed/branch/pmc-reports",
  },
  {
    key: "mis",
    title: "MIS Reports",
    desc: "State MIS dashboard.",
    icon: "▦",
    color: "purple",
    path: "/nafed/branch/mis",
  },
  {
    key: "notifications",
    title: "Notifications",
    desc: "Alerts and mail queue.",
    icon: "◉",
    color: "orange",
    path: "/nafed/branch/notifications",
  },
];

const normalise = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase();

const recordSources = (record = {}) =>
  [
    record,
    record.pacs,
    record.pacsDetails,
    record.society,
    record.societyDetails,
    record.applicant,
    record.applicantDetails,
    record.applicationData,
    record.formData,
    record.projectDetails,
    record.basicDetails,
    record.registrationDetails,
    record.profile,
  ].filter((source) => source && typeof source === "object");

const scalarValue = (value) =>
  typeof value === "string" || typeof value === "number"
    ? String(value).trim()
    : "";

const firstRecordValue = (record = {}, fields = []) => {
  for (const source of recordSources(record)) {
    for (const field of fields) {
      const value = scalarValue(source?.[field]);
      if (value) return value;
    }
  }
  return "";
};

const collectRecordValues = (record = {}, fields = []) => {
  const values = new Set();
  recordSources(record).forEach((source) => {
    fields.forEach((field) => {
      const value = scalarValue(source?.[field]);
      if (value) values.add(value);
    });
  });
  return Array.from(values);
};

const PACS_NAME_FIELDS = [
  "pacsName",
  "pacs",
  "pacsOrganizationName",
  "pacsSocietyName",
  "societyName",
  "societyFullName",
  "society",
  "cooperativeName",
  "cooperativeSocietyName",
  "institutionName",
  "organizationName",
  "applicantName",
  "name",
];

const projectState = (project = {}) =>
  firstRecordValue(project, [
    "state",
    "stateName",
    "projectState",
    "pacsState",
  ]);
const projectName = (project = {}) =>
  firstRecordValue(project, [...PACS_NAME_FIELDS, "projectName"]) ||
  "PACS Project";
const parseCapacityValue = (rawValue) => {
  let value = rawValue;
  if (value && typeof value === "object") {
    value =
      value.value ??
      value.mt ??
      value.capacityMt ??
      value.capacityMT ??
      value.capacity ??
      value.label ??
      0;
  }
  if (typeof value === "string") {
    value = value.replace(/,/g, "").replace(/[^0-9.-]/g, "");
  }
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : 0;
};

// Supports old and new application schemas without modifying stored records.
const projectCapacity = (project = {}) => {
  const sources = [
    project,
    project.projectDetails,
    project.applicationData,
    project.formData,
    project.godownDetails,
    project.technicalDetails,
    project.project,
  ].filter(Boolean);
  const fields = [
    "proposedCapacityMt",
    "proposedCapacityMT",
    "capacityMt",
    "capacityMT",
    "storageCapacityMt",
    "storageCapacityMT",
    "projectCapacityMt",
    "projectCapacityMT",
    "approvedCapacityMt",
    "approvedCapacityMT",
    "sanctionedCapacityMt",
    "sanctionedCapacityMT",
    "godownCapacityMt",
    "godownCapacityMT",
    "storageCapacity",
    "godownCapacity",
    "selectedCapacity",
    "capacityValue",
    "capacity",
  ];

  for (const source of sources) {
    for (const field of fields) {
      const resolved = parseCapacityValue(source?.[field]);
      if (resolved > 0) return resolved;
    }
  }
  return 0;
};
const REMOVED_VALUES = new Set([
  "DELETED",
  "ARCHIVED",
  "REMOVED",
  "TRASHED",
  "SOFT_DELETED",
  "PURGED",
]);

const upper = (value = "") =>
  String(value || "")
    .trim()
    .toUpperCase();

// One mandatory guard used by every counter, card, table, report and MOU list.
const isDeletedProject = (record = {}) => {
  const statusFields = [
    record.status,
    record.applicationStatus,
    record.registrationStatus,
    record.recordStatus,
    record.deletionStatus,
    record.pacsStatus,
    record.archiveStatus,
    record.syncStatus,
  ];
  return (
    record.isDeleted === true ||
    record.deleted === true ||
    record.archived === true ||
    record.isArchived === true ||
    record.pacsDeleted === true ||
    record.pacsIsDeleted === true ||
    Boolean(record.deletedAt) ||
    Boolean(record.deletedByUid) ||
    Boolean(record.archivedAt) ||
    Boolean(record.removedAt) ||
    statusFields.some((value) => REMOVED_VALUES.has(upper(value)))
  );
};

const pacsKeys = (record = {}) =>
  collectRecordValues(record, [
    "pacsUid",
    "pacsUserUid",
    "pacsUserId",
    "pacsId",
    "pacsProfileId",
    "societyUid",
    "societyId",
    "ownerUid",
    "ownerId",
    "createdByUid",
    "createdBy",
    "submittedByUid",
    "applicantUid",
    "userUid",
    "userId",
    "pacsRegistrationNo",
    "pacsRegistrationNumber",
    "registrationNumber",
    "registrationNo",
    "pacsEmail",
    "officialEmail",
    "email",
  ])
    .map(normalise)
    .filter(Boolean);

// Allocation records were created by multiple versions of the portal. This
// compatibility key-set connects every supported legacy/new PACS identity
// without deleting or rewriting any old Firestore document.
const allocationPacsKeys = (record = {}) =>
  collectRecordValues(record, [
    "id",
    "uid",
    "pacsUid",
    "pacsUserUid",
    "pacsUserId",
    "pacsId",
    "pacsProfileId",
    "allocatedPacsUid",
    "allocatedPacsId",
    "targetPacsUid",
    "societyUid",
    "societyId",
    "ownerUid",
    "ownerId",
    "createdByUid",
    "createdBy",
    "submittedByUid",
    "applicantUid",
    "userUid",
    "userId",
    "pacsRegistrationNo",
    "pacsRegistrationNumber",
    "registrationNumber",
    "registrationNo",
    "pacsEmail",
    "email",
    "officialEmail",
    "pacsMobile",
    "mobile",
    "phone",
    ...PACS_NAME_FIELDS,
  ])
    .map(normalise)
    .filter(Boolean);

// Strong identity keys are used only for de-duplication. Generic names are
// combined with location so two different societies with similar names are
// never merged accidentally.
const pacsDedupeKeys = (record = {}) => {
  const name = normalise(
    firstRecordValue(record, [...PACS_NAME_FIELDS, "name"]),
  );
  const state = normalise(
    firstRecordValue(record, [
      "state",
      "stateName",
      "projectState",
      "pacsState",
    ]),
  );
  const district = normalise(
    firstRecordValue(record, ["district", "districtName", "projectDistrict"]),
  );
  const registration = normalise(
    firstRecordValue(record, [
      "pacsRegistrationNo",
      "pacsRegistrationNumber",
      "registrationNumber",
      "registrationNo",
    ]),
  );
  const email = normalise(
    firstRecordValue(record, ["pacsEmail", "email", "officialEmail"]),
  );
  const mobile = normalise(
    firstRecordValue(record, ["pacsMobile", "mobile", "phone"]),
  ).replace(/\D/g, "");

  return [
    ...collectRecordValues(record, [
      "uid",
      "pacsUid",
      "pacsUserUid",
      "pacsUserId",
      "pacsId",
      "pacsProfileId",
      "societyUid",
      "societyId",
      "ownerUid",
      "createdByUid",
      "submittedByUid",
      "applicantUid",
      "userUid",
    ]).map((value) => `uid:${normalise(value)}`),
    registration && `reg:${registration}`,
    email && `email:${email}`,
    mobile.length >= 8 && `mobile:${mobile}`,
    name && `name-location:${name}|${state}|${district}`,
  ].filter(Boolean);
};

const mergeDefined = (primary = {}, secondary = {}) => {
  const merged = { ...secondary, ...primary };
  Object.keys(merged).forEach((key) => {
    if (
      primary[key] === undefined ||
      primary[key] === null ||
      primary[key] === ""
    ) {
      merged[key] = secondary[key];
    }
  });
  return merged;
};

// PMC records can exist in legacy and current schemas at the same time.
// Collapse only strong identity matches so the portal never shows the same
// approved agency twice while still preserving distinct agencies.
const pmcDedupeKeys = (record = {}) => {
  const registration = normalise(
    record.registrationNumber ||
      record.registrationNo ||
      record.pmcRegistrationNo ||
      record.cin ||
      record.gstin,
  );
  const email = normalise(
    record.email || record.officialEmail || record.contactEmail,
  );
  const mobile = normalise(
    record.mobile || record.phone || record.contactMobile,
  ).replace(/\D/g, "");
  const name = normalise(
    record.organizationName ||
      record.agencyName ||
      record.pmcName ||
      record.name,
  );

  return [
    record.uid && `uid:${normalise(record.uid)}`,
    registration && `reg:${registration}`,
    email && `email:${email}`,
    mobile.length >= 8 && `mobile:${mobile}`,
    name && `name:${name}`,
  ].filter(Boolean);
};

const dedupePmcRecords = (records = []) => {
  const groups = new Map();
  const aliases = new Map();

  records
    .slice()
    .sort(
      (a, b) =>
        toMillis(a.updatedAt || a.approvedAt || a.createdAt) -
        toMillis(b.updatedAt || b.approvedAt || b.createdAt),
    )
    .forEach((record) => {
      if (isDeletedProject(record)) return;
      const keys = pmcDedupeKeys(record);
      const existingId = keys.map((key) => aliases.get(key)).find(Boolean);
      const groupId = String(
        existingId || record.uid || record.id || keys[0] || "",
      );
      if (!groupId) return;
      const current = groups.get(groupId);
      const merged = current ? mergeDefined(record, current) : record;
      groups.set(groupId, {
        ...merged,
        sourcePmcIds: Array.from(
          new Set(
            [
              ...(current?.sourcePmcIds || []),
              record.id,
              record.uid,
              merged.id,
              merged.uid,
            ].filter(Boolean),
          ),
        ),
      });
      keys.forEach((key) => aliases.set(key, groupId));
    });

  return Array.from(groups.values());
};

const pmcIdentityKeys = (record = {}) =>
  [
    record.id,
    record.uid,
    ...(record.sourcePmcIds || []),
    firstRecordValue(record, [
      "registrationNumber",
      "registrationNo",
      "pmcRegistrationNo",
      "cin",
      "gstin",
    ]),
    firstRecordValue(record, ["email", "officialEmail", "contactEmail"]),
    firstRecordValue(record, [
      "mobile",
      "phone",
      "contactMobile",
      "mobileNumber",
    ]),
  ]
    .map(normalise)
    .filter(Boolean);

/*
 * The same approved PMC profile was previously named "Sun Shine Overseas".
 * Treat that historical brand and the current Shine Agri organisation name as
 * one identity so every live screen immediately shows the current master name.
 */
const canonicalPmcNameKey = (value = "") => {
  const key = normalise(value).replace(/[^a-z0-9]+/g, " ").trim();
  if (
    key.includes("sun shine overseas") ||
    key.includes("sunshine overseas") ||
    (key.includes("shine agri allied") && key.includes("marketing"))
  ) {
    return "shine-agri-allied-current-profile";
  }
  return key;
};

const isLegacyShineAgriPmcName = (value = "") => {
  const key = normalise(value).replace(/[^a-z0-9]+/g, " ").trim();
  return key.includes("sun shine overseas") || key.includes("sunshine overseas");
};

const hasLegacyShineAgriReference = (record = {}) =>
  isLegacyShineAgriPmcName(
    firstRecordValue(record, [
    "allocatedPmcName",
    "assignedPmcName",
    "pmcName",
    "pmcAgencyName",
    "pmcOrganizationName",
    ]),
  );

const resolveCurrentPmc = (reference = {}, approvedPmcs = []) => {
  const referenceKeys = new Set(
    [
      reference.allocatedPmcUid,
      reference.allocatedPmcId,
      reference.pmcUid,
      reference.pmcId,
      reference.allocatedPmcEmail,
      reference.pmcEmail,
      reference.allocatedPmcMobile,
      reference.pmcMobile,
    ]
      .map(normalise)
      .filter(Boolean),
  );
  const identityMatch = approvedPmcs.find((pmc) =>
    pmcIdentityKeys(pmc).some((key) => referenceKeys.has(key)),
  );
  if (identityMatch) return identityMatch;

  const legacyName = canonicalPmcNameKey(
    reference.allocatedPmcName || reference.pmcName || reference.pmcAgencyName,
  );
  return legacyName
    ? approvedPmcs.find(
        (pmc) => canonicalPmcNameKey(entityName(pmc)) === legacyName,
      ) ||
        null
    : null;
};

const dedupePacsRecords = (records = []) => {
  const groups = new Map();
  const aliases = new Map();

  records
    .slice()
    .sort(
      (a, b) =>
        toMillis(a.updatedAt || a.approvedAt || a.createdAt) -
        toMillis(b.updatedAt || b.approvedAt || b.createdAt),
    )
    .forEach((record) => {
      if (isDeletedProject(record)) return;
      const keys = pacsDedupeKeys(record);
      const existingId = keys.map((key) => aliases.get(key)).find(Boolean);
      const groupId = String(
        existingId || record.uid || record.id || keys[0] || "",
      );
      if (!groupId) return;
      const current = groups.get(groupId);
      const merged = current ? mergeDefined(record, current) : record;
      groups.set(groupId, {
        ...merged,
        sourcePacsIds: Array.from(
          new Set(
            [
              ...(current?.sourcePacsIds || []),
              record.id || record.uid,
              merged.id || merged.uid,
            ].filter(Boolean),
          ),
        ),
      });
      keys.forEach((key) => aliases.set(key, groupId));
    });

  return Array.from(groups.values());
};

const hasPmcReference = (record = {}) =>
  Boolean(
    record.allocatedPmcUid ||
    record.allocatedPmcId ||
    record.pmcUid ||
    record.pmcId ||
    record.allocatedPmcName ||
    record.pmcName,
  );

const isActivePmcAllocation = (record = {}) => {
  const status = upper(record.status || record.allocationStatus || "ACTIVE");
  return (
    !isDeletedProject(record) &&
    record.isActive !== false &&
    !["UNALLOCATED", "REMOVED", "CANCELLED", "DELETED", "ARCHIVED"].includes(
      status,
    ) &&
    hasPmcReference(record)
  );
};

const isNafedProject = (project = {}) =>
  project.nafedProject === true ||
  normalise(project.projectSource) === "nafed" ||
  project.applicationPortal === "NAFED_PACS_APPLY" ||
  upper(project.applicationId).startsWith("NAFED-");

const entityName = (record = {}) =>
  firstRecordValue(record, [
    "organizationName",
    "societyName",
    "pacsName",
    "pacs",
    "agencyName",
    "companyName",
    "cooperativeName",
    "cooperativeSocietyName",
    "institutionName",
    "name",
    "email",
  ]) || "Record";

const entityState = (record = {}) =>
  firstRecordValue(record, ["state", "stateName", "projectState", "pacsState"]);

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (Number.isFinite(value?.seconds)) return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const canonicalStage = (record = {}) => {
  const raw = upper(
    record.currentStage ||
      record.stage ||
      record.workflowStage ||
      record.projectStatus ||
      record.applicationStatus ||
      record.rawStatus ||
      record.status ||
      "APPLIED",
  );
  if (raw.includes("COMPLETE") || raw.includes("COMMISSION"))
    return "COMPLETED";
  if (
    raw.includes("CONSTRUCTION") ||
    raw.includes("WORK_START") ||
    raw.includes("EXECUTION") ||
    raw.includes("RA_BILL")
  )
    return "CONSTRUCTION";
  if (
    raw.includes("LOA") ||
    raw.includes("WORK_ORDER") ||
    raw.includes("CONTRACTOR")
  )
    return "LOA";
  if (raw.includes("TENDER") || raw.includes("BID")) return "TENDER";
  if (
    raw.includes("DPR") ||
    raw.includes("ESTIMATE") ||
    raw.includes("DESIGN") ||
    raw.includes("SOIL") ||
    raw.includes("SURVEY")
  )
    return "DPR";
  if (raw.includes("FIELD") || raw.includes("SITE_VISIT")) return "FIELD_VISIT";
  return "APPLIED";
};

const PACS_MANDATORY_DOCUMENTS = [
  {
    id: "COMMITTEE_RESOLUTION",
    code: "RES",
    title: "District Registrar Application & Managing Committee Resolution",
  },
  { id: "MARGIN_MONEY", code: "MMC", title: "Margin Money Certificate" },
  {
    id: "STATUTORY_PROCEEDINGS",
    code: "SPC",
    title: "No Statutory Proceedings Certificate",
  },
  {
    id: "NO_IRREGULARITY",
    code: "NIC",
    title: "No Financial Irregularity Certificate",
  },
  {
    id: "CONTRIBUTION_UNDERTAKING",
    code: "GIU",
    title: "Godown Insurance / Project Contribution Undertaking",
  },
];

const pacsDocumentUrl = (record = {}) =>
  String(
    record.url ||
      record.downloadURL ||
      record.downloadUrl ||
      record.fileUrl ||
      record.signedFileUrl ||
      "",
  ).trim();

const safePacsDocumentUrl = (value) => {
  try {
    const parsed = new URL(String(value || "").trim());
    return ["https:", "http:"].includes(parsed.protocol) ? parsed.href : "";
  } catch {
    return "";
  }
};

const downloadMouCopy = async (value, fileName = "MOU_COPY.pdf") => {
  const url = safePacsDocumentUrl(value);
  if (!url) return;
  try {
    const response = await fetch(url, { cache: "no-store" });
    if (!response.ok) throw new Error("MOU copy could not be downloaded.");
    const blobUrl = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName.replace(/[^a-zA-Z0-9._-]/g, "_");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
  } catch (error) {
    console.warn("Direct MOU download failed; opening the authorised copy.", error);
    window.open(url, "_blank", "noopener,noreferrer");
  }
};

const activePacsDocument = (project = {}, type) => {
  if (project.pmcDocumentOverrides?.[type]?.action === "REMOVED") return null;
  const sources = [
    ["PMC replacement", project.pmcVerificationDocuments?.[type]],
    ["PACS signed", project.signedPacsDocuments?.[type]],
    ["System generated", project.generatedDocuments?.[type]],
  ];
  for (const [source, record] of sources) {
    const url = pacsDocumentUrl(record);
    if (url) return { ...record, url, source };
  }
  return null;
};

const pacsDocumentDefinitions = (project = {}) => {
  const mandatoryIds = new Set(PACS_MANDATORY_DOCUMENTS.map((item) => item.id));
  const discovered = new Set();
  [
    project.generatedDocuments,
    project.signedPacsDocuments,
    project.pmcVerificationDocuments,
  ].forEach((recordMap) =>
    Object.keys(recordMap || {}).forEach((key) => discovered.add(key)),
  );
  const extras = [...discovered]
    .filter((id) => !mandatoryIds.has(id))
    .map((id) => {
      const record = activePacsDocument(project, id) || {};
      return {
        id,
        code: String(record.code || id.slice(0, 3)).toUpperCase(),
        title:
          record.title ||
          record.documentTitle ||
          id.replaceAll("_", " "),
        optional: true,
      };
    });
  return [...PACS_MANDATORY_DOCUMENTS, ...extras];
};

const documentDeskStatus = (project = {}) => {
  const available = PACS_MANDATORY_DOCUMENTS.filter((item) =>
    activePacsDocument(project, item.id),
  ).length;
  const stage = upper(
    project.documentStage ||
      project.pmcDecision ||
      project.pmcVerificationStatus ||
      project.currentStage,
  );
  if (
    available === PACS_MANDATORY_DOCUMENTS.length &&
    (stage.includes("APPROVED") || stage.includes("READY_FOR_MOU"))
  ) return "APPROVED";
  if (stage.includes("QUERY") || stage.includes("INCOMPLETE")) return "QUERY";
  if (available === PACS_MANDATORY_DOCUMENTS.length) return "READY";
  return "MISSING";
};

function PacsDocumentMonitoringDesk({ projects = [], onOpenMou }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectedId, setSelectedId] = useState("");

  const records = useMemo(
    () =>
      projects
        .filter((project) => !isDeletedProject(project))
        .map((project) => {
          const definitions = pacsDocumentDefinitions(project);
          const available = definitions.filter((item) =>
            activePacsDocument(project, item.id),
          ).length;
          const mandatoryAvailable = PACS_MANDATORY_DOCUMENTS.filter((item) =>
            activePacsDocument(project, item.id),
          ).length;
          return {
            ...project,
            documentDefinitions: definitions,
            availableDocumentCount: available,
            mandatoryAvailable,
            mandatoryMissing:
              PACS_MANDATORY_DOCUMENTS.length - mandatoryAvailable,
            deskStatus: documentDeskStatus(project),
          };
        })
        .sort((left, right) =>
          projectName(left).localeCompare(projectName(right)),
        ),
    [projects],
  );

  const visible = records.filter((project) => {
    const text = [
      projectName(project),
      project.applicationId,
      project.id,
      project.district,
      project.state,
      project.allocatedPmcName,
      project.pmcName,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return (
      (!search.trim() || text.includes(search.trim().toLowerCase())) &&
      (statusFilter === "ALL" || project.deskStatus === statusFilter)
    );
  });

  useEffect(() => {
    if (selectedId && records.some((project) => project.id === selectedId)) return;
    setSelectedId(records[0]?.id || "");
  }, [records, selectedId]);

  const selected = records.find((project) => project.id === selectedId) || null;
  const rows = selected
    ? selected.documentDefinitions.map((definition) => ({
        ...definition,
        record: activePacsDocument(selected, definition.id),
      }))
    : [];
  const completeCount = records.filter(
    (project) => project.mandatoryMissing === 0,
  ).length;
  const approvedCount = records.filter(
    (project) => project.deskStatus === "APPROVED",
  ).length;
  const missingTotal = records.reduce(
    (total, project) => total + project.mandatoryMissing,
    0,
  );

  return (
    <section className="pacs-document-monitor">
      <style>{PACS_DOCUMENT_MONITOR_STYLE}</style>
      <div className="pdm-kpis">
        <div><span>Total PACS Applications</span><b>{records.length}</b></div>
        <div><span>Complete Document Sets</span><b>{completeCount}</b></div>
        <div><span>PMC Approved for MOU</span><b>{approvedCount}</b></div>
        <div className={missingTotal ? "warning" : "ready"}><span>Mandatory Files Missing</span><b>{missingTotal}</b></div>
      </div>
      <div className="pdm-toolbar">
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search PACS, application, district or PMC..."
        />
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          <option value="ALL">All Document Status</option>
          <option value="MISSING">Mandatory Missing</option>
          <option value="READY">Complete / Awaiting PMC</option>
          <option value="QUERY">Query / Incomplete</option>
          <option value="APPROVED">PMC Approved for MOU</option>
        </select>
      </div>
      <div className="pdm-layout">
        <aside>
          <div className="pdm-list-head">PACS DOCUMENT FILES · {visible.length}</div>
          {visible.map((project) => (
            <button
              type="button"
              key={project.id}
              className={selectedId === project.id ? "selected" : ""}
              onClick={() => setSelectedId(project.id)}
            >
              <b>{projectName(project)}</b>
              <span>{project.applicationId || project.id}</span>
              <small>{project.district || "District pending"} · {project.mandatoryAvailable}/5 mandatory</small>
              <em className={project.deskStatus.toLowerCase()}>{project.deskStatus}</em>
            </button>
          ))}
          {!visible.length && <div className="pdm-empty">No PACS matches this filter.</div>}
        </aside>
        <main>
          {!selected ? (
            <div className="pdm-empty">Select a PACS document file.</div>
          ) : (
            <>
              <div className="pdm-file-head">
                <div>
                  <small>PACS DOCUMENT &amp; PMC SCRUTINY STATUS</small>
                  <h3>{projectName(selected)}</h3>
                  <p>{selected.applicationId || selected.id} · {selected.district || "-"}, {selected.state || "-"}</p>
                </div>
                <span className={`pdm-stage ${selected.deskStatus.toLowerCase()}`}>
                  {selected.deskStatus}
                </span>
              </div>
              <div className="pdm-facts">
                <span>Available Documents<b>{selected.availableDocumentCount}</b></span>
                <span>Mandatory Missing<b>{selected.mandatoryMissing}</b></span>
                <span>PMC Decision<b>{selected.pmcDecision || selected.pmcVerificationStatus || "PENDING"}</b></span>
                <span>Current Stage<b>{selected.documentStage || selected.currentStage || "DOCUMENT REVIEW"}</b></span>
              </div>
              {(selected.documentQueryRemarks || selected.pmcDocumentQueryRemarks || selected.pmcRemarks) && (
                <div className="pdm-query">
                  <b>Recorded Query / Remarks</b>
                  <p>{selected.documentQueryRemarks || selected.pmcDocumentQueryRemarks || selected.pmcRemarks}</p>
                </div>
              )}
              <div className="pdm-table-wrap">
                <table>
                  <thead><tr><th>#</th><th>Document</th><th>Availability</th><th>Version / Date</th><th>SHA-256</th><th>Action</th></tr></thead>
                  <tbody>
                    {rows.map((row, index) => {
                      const url = safePacsDocumentUrl(row.record?.url);
                      return (
                        <tr key={row.id} className={url ? "" : "missing"}>
                          <td>{index + 1}</td>
                          <td><b>{row.title}</b><small>{row.code} · {row.optional ? "Additional" : "Mandatory"}</small></td>
                          <td>{url ? <><em className="available">AVAILABLE</em><small>{row.record.source}</small></> : <em className="not-available">MISSING</em>}</td>
                          <td><b>Version {row.record?.signedVersion || row.record?.version || "-"}</b><small>{reportDateText(row.record?.uploadedAt || row.record?.createdAt || row.record?.submittedAt) || "-"}</small></td>
                          <td><code>{row.record?.sha256 ? `${row.record.sha256.slice(0, 12)}…` : "Not available"}</code></td>
                          <td>{url ? <><a href={url} target="_blank" rel="noopener noreferrer">View</a><a href={url} download>Download</a></> : <span>-</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className={`pdm-mou-bar ${selected.mandatoryMissing ? "blocked" : "ready"}`}>
                <div>
                  <b>{selected.mandatoryMissing ? `${selected.mandatoryMissing} mandatory document(s) missing` : "Mandatory document set complete"}</b>
                  <span>{selected.mandatoryMissing ? "PMC/PACS ko missing documents complete karne honge." : "MOU status aur signature desk open karke current execution dekhein."}</span>
                </div>
                <button type="button" onClick={onOpenMou}>Open MOU Status &amp; Signature Desk</button>
              </div>
            </>
          )}
        </main>
      </div>
    </section>
  );
}

const PACS_DOCUMENT_MONITOR_STYLE = `
.pacs-document-monitor{display:grid;gap:13px;color:#142b46}.pdm-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:10px}.pdm-kpis>div{padding:15px;border:1px solid #d1dce7;border-top:4px solid #176b43;border-radius:11px;background:#fff}.pdm-kpis span,.pdm-kpis b{display:block}.pdm-kpis span{color:#64748b;font-size:9px;font-weight:900;text-transform:uppercase}.pdm-kpis b{margin-top:6px;font-size:24px}.pdm-kpis .warning{border-top-color:#d97706;background:#fffaf0}.pdm-kpis .ready{border-top-color:#167344;background:#f1faf4}.pdm-toolbar{display:grid;grid-template-columns:1fr 260px;gap:9px;padding:11px;border:1px solid #d1dce7;border-radius:10px;background:#f5f8fb}.pdm-toolbar input,.pdm-toolbar select{height:42px;padding:0 11px;border:1px solid #b7c7d7;border-radius:8px;background:#fff}.pdm-layout{display:grid;grid-template-columns:330px minmax(0,1fr);gap:12px}.pdm-layout>aside,.pdm-layout>main{min-width:0;overflow:hidden;border:1px solid #d1dce7;border-radius:12px;background:#fff}.pdm-list-head{padding:12px;border-bottom:1px solid #dce5ee;color:#526a82;font-size:9px;font-weight:950}.pdm-layout aside>button{display:block;width:calc(100% - 16px);margin:8px;padding:11px;border:1px solid #d7e0e9;border-radius:8px;background:#fff;color:#173652;text-align:left}.pdm-layout aside>button.selected{border-color:#176b43;background:#eef9f2}.pdm-layout aside b,.pdm-layout aside span,.pdm-layout aside small,.pdm-layout aside em{display:block}.pdm-layout aside span,.pdm-layout aside small{margin-top:4px;color:#6b7e91;font-size:9px}.pdm-layout aside em,.pdm-stage{width:max-content;margin-top:7px;padding:5px 7px;border-radius:999px;background:#fff0d9;color:#995a00;font-size:8px;font-style:normal;font-weight:950}.pdm-layout aside em.approved,.pdm-stage.approved,.pdm-layout aside em.ready,.pdm-stage.ready{background:#e6f7eb;color:#17653b}.pdm-layout aside em.query,.pdm-stage.query{background:#fff0e3;color:#a3520a}.pdm-file-head{display:flex;justify-content:space-between;gap:15px;padding:16px;border-bottom:1px solid #dce5ee;background:linear-gradient(110deg,#0b315e,#176341);color:#fff}.pdm-file-head small{font-size:8px;font-weight:950;letter-spacing:.08em}.pdm-file-head h3{margin:6px 0 4px;font-size:17px}.pdm-file-head p{margin:0;color:#d7eee2;font-size:9px}.pdm-stage{align-self:flex-start;margin:0;background:#fff;color:#173652}.pdm-facts{display:grid;grid-template-columns:repeat(4,1fr);gap:7px;padding:12px}.pdm-facts span{padding:9px;border:1px solid #dce5ee;background:#f7f9fb;color:#64748b;font-size:8px}.pdm-facts b{display:block;margin-top:5px;overflow-wrap:anywhere;color:#183550;font-size:9px}.pdm-query{margin:0 12px 12px;padding:10px;border-left:4px solid #d97706;background:#fff8e8}.pdm-query b{font-size:9px}.pdm-query p{margin:5px 0 0;color:#744b0b;font-size:9px}.pdm-table-wrap{overflow:auto;border-top:1px solid #dce5ee}.pdm-table-wrap table{width:100%;min-width:900px;border-collapse:collapse}.pdm-table-wrap th,.pdm-table-wrap td{padding:10px;border:1px solid #dce5ee;text-align:left;font-size:9px}.pdm-table-wrap th{background:#edf3f8;color:#49647f;font-size:8px;text-transform:uppercase}.pdm-table-wrap td b,.pdm-table-wrap td small{display:block}.pdm-table-wrap td small{margin-top:4px;color:#718296}.pdm-table-wrap tr.missing{background:#fffaf1}.pdm-table-wrap em{display:inline-block;padding:4px 6px;border-radius:999px;font-size:7px;font-style:normal;font-weight:950}.pdm-table-wrap .available{background:#e5f6e9;color:#16713d}.pdm-table-wrap .not-available{background:#ffefde;color:#a75f00}.pdm-table-wrap a{display:inline-block;margin-right:5px;padding:6px 7px;border:1px solid #9eb5cb;border-radius:6px;color:#164f91;text-decoration:none;font-weight:900}.pdm-table-wrap code{font-size:8px}.pdm-empty{padding:32px;text-align:center;color:#718296}.pdm-mou-bar{margin:12px;padding:14px;display:flex;align-items:center;justify-content:space-between;gap:15px;border:1px solid #92cfaa;border-radius:10px;background:#eff9f2}.pdm-mou-bar.blocked{border-color:#e7c06a;background:#fff8e8}.pdm-mou-bar b,.pdm-mou-bar span{display:block}.pdm-mou-bar span{margin-top:4px;color:#66798c;font-size:9px}.pdm-mou-bar button{padding:10px 12px;border:0;border-radius:8px;background:#0b315e;color:#fff;font-size:9px;font-weight:950;cursor:pointer}@media(max-width:1050px){.pdm-layout{grid-template-columns:1fr}.pdm-kpis{grid-template-columns:1fr 1fr}}@media(max-width:650px){.pdm-kpis,.pdm-toolbar,.pdm-facts{grid-template-columns:1fr}.pdm-file-head,.pdm-mou-bar{align-items:flex-start;flex-direction:column}.pdm-mou-bar button{width:100%}}
`;

function StateBranchMouDesk({ projects = [] }) {
  const [filter, setFilter] = useState("ALL");
  const [selectedId, setSelectedId] = useState("");

  const mouDeskStage = (project = {}) => {
    if (
      project.mouFullyExecuted === true ||
      project.twoPartyMouExecuted === true ||
      project.threePartyMouExecuted === true ||
      project.finalMouUrl ||
      project.finalSignedMouUrl ||
      project.mouFinalPdfUrl ||
      ["MOU_COMPLETED", "MOU_FULLY_EXECUTED", "FULLY_EXECUTED"].includes(
        upper(project.status),
      )
    ) {
      return "FULLY_EXECUTED";
    }
    const recorded = upper(project.mouStage || project.mouStatus);
    // Legacy three-party records become read-only PACS-pending records. Their
    // existing files and audit history remain untouched.
    if (
      ["PENDING_STATE_BRANCH_SIGNATURE", "PENDING_BRANCH_SIGNATURE"].includes(
        recorded,
      )
    ) {
      return "PENDING_PACS_SIGNATURE";
    }
    if (recorded) return recorded;
    if (
      [
        project.documentStage,
        project.pmcDecision,
        project.pmcVerificationStatus,
        project.pmcDocumentApproval?.status,
      ].some((value) => upper(value).includes("APPROVED"))
    ) {
      return "PENDING_PMC_SIGNATURE";
    }
    return "DOCUMENTS_PENDING";
  };
  const eligibleProjects = useMemo(
    () =>
      projects.filter(
        (project) =>
          project &&
          project.id &&
          project.isDeleted !== true &&
          !["DELETED", "REMOVED", "ARCHIVED", "TRASHED"].includes(
            upper(project.status),
          ),
      ),
    [projects],
  );

  const visibleProjects = eligibleProjects.filter((project) => {
    const stage = mouDeskStage(project);
    return (
      filter === "ALL" ||
      (filter === "DOCUMENTS" && stage === "DOCUMENTS_PENDING") ||
      (filter === "PMC" && stage === "PENDING_PMC_SIGNATURE") ||
      (filter === "PACS" && stage === "PENDING_PACS_SIGNATURE") ||
      (filter === "EXECUTED" && stage === "FULLY_EXECUTED")
    );
  });

  useEffect(() => {
    if (
      selectedId &&
      eligibleProjects.some((project) => project.id === selectedId)
    )
      return;
    const next =
      eligibleProjects.find(
        (project) => mouDeskStage(project) === "PENDING_PACS_SIGNATURE",
      ) || eligibleProjects[0];
    setSelectedId(next?.id || "");
  }, [eligibleProjects, selectedId]);

  const selected =
    eligibleProjects.find((project) => project.id === selectedId) || null;
  const documentsPendingCount = eligibleProjects.filter(
    (project) => mouDeskStage(project) === "DOCUMENTS_PENDING",
  ).length;
  const pmcPendingCount = eligibleProjects.filter(
    (project) => mouDeskStage(project) === "PENDING_PMC_SIGNATURE",
  ).length;
  const pacsPendingCount = eligibleProjects.filter(
    (project) => mouDeskStage(project) === "PENDING_PACS_SIGNATURE",
  ).length;
  const executedCount = eligibleProjects.filter(
    (project) => mouDeskStage(project) === "FULLY_EXECUTED",
  ).length;

  return (
    <section className="sb-mou-desk">
      <style>{`${STATE_BRANCH_MOU_DESK_STYLE}
        .sb-mou-summary{grid-template-columns:repeat(4,minmax(0,1fr))}
        .sb-mou-complete a+a,.sb-mou-waiting a+a,.sb-mou-neutral a+a{margin-left:8px}
        .mou-copy-download{display:inline-flex;align-items:center;justify-content:center;min-height:40px;margin-left:8px;padding:10px 15px;border:0;border-radius:8px;background:#0b315e;color:#fff;font-size:10px;font-weight:900;cursor:pointer}
        @media(max-width:1280px){.sb-mou-summary{grid-template-columns:repeat(2,1fr)}}
        @media(max-width:650px){.sb-mou-summary{grid-template-columns:1fr}.sb-mou-complete a,.sb-mou-waiting a,.sb-mou-neutral a,.mou-copy-download{width:100%;margin:6px 0 0}}
      `}</style>
      <div className="sb-mou-summary">
        <div>
          <span>Documents Pending</span>
          <b>{documentsPendingCount}</b>
        </div>
        <div>
          <span>Awaiting PMC Signature</span>
          <b>{pmcPendingCount}</b>
        </div>
        <div>
          <span>Awaiting PACS Signature</span>
          <b>{pacsPendingCount}</b>
        </div>
        <div>
          <span>Fully Executed</span>
          <b>{executedCount}</b>
        </div>
        <nav>
          {[
            ["ALL", "All Status"],
            ["DOCUMENTS", "Documents Pending"],
            ["PMC", "With PMC"],
            ["PACS", "With PACS"],
            ["EXECUTED", "Executed"],
          ].map(([value, label]) => (
            <button
              key={value}
              className={filter === value ? "active" : ""}
              onClick={() => setFilter(value)}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      <div className="sb-mou-layout">
        <aside>
          {!visibleProjects.length && (
            <div className="sb-mou-empty">
              No MOU record is available for this filter.
            </div>
          )}
          {visibleProjects.map((project) => {
            const stage = mouDeskStage(project);
            const statusClass =
              stage === "PENDING_PACS_SIGNATURE"
                ? "awaiting"
                : stage === "FULLY_EXECUTED"
                  ? "done"
                  : "neutral";
            return (
              <button
                key={project.id}
                className={selectedId === project.id ? "selected" : ""}
                onClick={() => setSelectedId(project.id)}
              >
                <b>{projectName(project)}</b>
                <span>{project.applicationId || project.id}</span>
                <small>
                  {formatCapacity(projectCapacity(project))} ·{" "}
                  {project.district || "District pending"}
                </small>
                <em className={statusClass}>
                  {stage?.replaceAll("_", " ")}
                </em>
              </button>
            );
          })}
        </aside>

        <main>
          {!selected && (
            <div className="sb-mou-empty">Select a PACS project to view its document-to-MOU status.</div>
          )}
          {selected && mouDeskStage(selected) === "DOCUMENTS_PENDING" && (
            <div className="sb-mou-neutral">
              <b>MOU Not Open — PACS Documents Pending</b>
              <span>
                Mandatory PACS documents aur PMC scrutiny complete hone ke baad
                MOU PMC signature ke liye open hoga.
              </span>
              <button type="button" onClick={() => setFilter("DOCUMENTS")}>
                Documents Pending Status
              </button>
            </div>
          )}
          {selected &&
            mouDeskStage(selected) === "PENDING_PMC_SIGNATURE" && (
              <div className="sb-mou-neutral">
                <b>MOU Awaiting PMC First Signature</b>
                <span>
                  This is a read-only State Branch view. The allocated PMC will
                  sign first and send the MOU directly to PACS.
                </span>
                {(selected.mouDraftUrl ||
                  selected.draftMouUrl ||
                  selected.mouPdfUrl) && (
                  <a
                    href={
                      selected.mouDraftUrl ||
                      selected.draftMouUrl ||
                      selected.mouPdfUrl
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View MOU Draft
                  </a>
                )}
                {(selected.mouDraftUrl ||
                  selected.draftMouUrl ||
                  selected.mouPdfUrl) && (
                  <button
                    type="button"
                    className="mou-copy-download"
                    onClick={() =>
                      downloadMouCopy(
                        selected.mouDraftUrl ||
                          selected.draftMouUrl ||
                          selected.mouPdfUrl,
                        `MOU_DRAFT_${selected.applicationId || selected.id}.pdf`,
                      )
                    }
                  >
                    Download Draft Copy
                  </button>
                )}
              </div>
            )}
          {selected &&
            mouDeskStage(selected) === "PENDING_PACS_SIGNATURE" && (
              <div className="sb-mou-waiting">
                <b>PMC Signed — PACS Final Signature Pending</b>
                <span>
                  State Branch is view-only. The PMC-signed two-party MOU is
                  now with PACS for Chairperson and Secretary signatures.
                </span>
                {(selected.pmcSignedMouUrl ||
                  selected.mouSignatures?.pmc?.signedPdfUrl) && (
                  <a
                    href={
                      selected.pmcSignedMouUrl ||
                      selected.mouSignatures?.pmc?.signedPdfUrl
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View PMC Signed MOU
                  </a>
                )}
              {(selected.pmcSignedMouUrl ||
                selected.mouSignatures?.pmc?.signedPdfUrl) && (
                <button
                  type="button"
                  className="mou-copy-download"
                  onClick={() =>
                    downloadMouCopy(
                      selected.pmcSignedMouUrl ||
                        selected.mouSignatures?.pmc?.signedPdfUrl,
                      `PMC_SIGNED_MOU_${selected.applicationId || selected.id}.pdf`,
                    )
                  }
                >
                  Download PMC Signed Copy
                </button>
              )}
              </div>
            )}
          {selected && mouDeskStage(selected) === "FULLY_EXECUTED" && (
              <div className="sb-mou-complete">
                <b>PMC–PACS Two-Party MOU Fully Executed</b>
                <span>
                  PMC and PACS signatures are complete. State Branch has
                  read-only access to the locked final copy.
                </span>
                {(selected.finalMouUrl ||
                  selected.finalSignedMouUrl ||
                  selected.mouFinalPdfUrl) && (
                  <a
                    href={
                      selected.finalMouUrl ||
                      selected.finalSignedMouUrl ||
                      selected.mouFinalPdfUrl
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Final Signed MOU
                  </a>
                )}
                {(selected.finalMouUrl ||
                  selected.finalSignedMouUrl ||
                  selected.mouFinalPdfUrl) && (
                  <button
                    type="button"
                    className="mou-copy-download"
                    onClick={() =>
                      downloadMouCopy(
                        selected.finalMouUrl ||
                          selected.finalSignedMouUrl ||
                          selected.mouFinalPdfUrl,
                        `FINAL_PMC_PACS_MOU_${selected.applicationId || selected.id}.pdf`,
                      )
                    }
                  >
                    Download Final Copy
                  </button>
                )}
              </div>
            )}
        </main>
      </div>
    </section>
  );
}

const STATE_BRANCH_MOU_DESK_STYLE = `
.sb-mou-desk{--mou-navy:#0b315e;--mou-green:#147548;--mou-ink:#142b46;--mou-muted:#66788c;--mou-line:#d8e2ec;display:grid;gap:18px;color:var(--mou-ink);font-family:Inter,"Segoe UI",Arial,sans-serif}.sb-mou-summary{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:12px}.sb-mou-summary>div{position:relative;min-width:0;min-height:94px;padding:17px 16px 14px 19px;overflow:hidden;border:1px solid var(--mou-line);border-radius:12px;background:#fff;box-shadow:0 5px 16px rgba(11,49,94,.055)}.sb-mou-summary>div:before{content:"";position:absolute;inset:0 auto 0 0;width:4px;background:#8da0b4}.sb-mou-summary>div:after{content:"";position:absolute;right:-18px;bottom:-22px;width:64px;height:64px;border-radius:50%;background:rgba(11,49,94,.035)}.sb-mou-summary>div:nth-child(1):before{background:#e29a20}.sb-mou-summary>div:nth-child(2):before{background:#3478ca}.sb-mou-summary>div:nth-child(3):before{background:#d97706}.sb-mou-summary>div:nth-child(4):before{background:#2563b8}.sb-mou-summary>div:nth-child(5):before{background:#c34242}.sb-mou-summary>div:nth-child(6):before{background:#188452}.sb-mou-summary span,.sb-mou-summary b{display:block;position:relative;z-index:1}.sb-mou-summary span{min-height:26px;color:var(--mou-muted);font-size:10px;line-height:1.35;font-weight:850;text-transform:uppercase;letter-spacing:.045em}.sb-mou-summary b{margin-top:8px;color:var(--mou-navy);font-size:25px;line-height:1;font-weight:950}.sb-mou-summary nav{grid-column:1/-1;display:flex;align-items:center;flex-wrap:wrap;gap:7px;padding:9px;border:1px solid var(--mou-line);border-radius:11px;background:#fff;box-shadow:0 4px 14px rgba(11,49,94,.04)}.sb-mou-summary nav:before{content:"STATUS VIEW";padding:0 9px;color:#708196;font-size:9px;font-weight:950;letter-spacing:.08em}.sb-mou-summary nav button{min-height:34px;padding:7px 12px;border:1px solid #d2dde8;border-radius:7px;background:#f8fafc;color:#3a536d;font-size:10px;font-weight:850;cursor:pointer;transition:.16s ease}.sb-mou-summary nav button:hover{border-color:#91a9c2;background:#f1f5f9}.sb-mou-summary nav button.active{border-color:var(--mou-navy);background:var(--mou-navy);color:#fff;box-shadow:0 4px 12px rgba(11,49,94,.18)}.sb-mou-layout{display:grid;grid-template-columns:335px minmax(0,1fr);align-items:start;gap:16px}.sb-mou-layout>aside{min-width:0;max-height:calc(100vh - 235px);padding:10px;overflow:auto;border:1px solid var(--mou-line);border-radius:13px;background:#f6f9fc;box-shadow:0 6px 18px rgba(11,49,94,.05);scrollbar-width:thin}.sb-mou-layout>aside:before{content:"PACS MOU REGISTER";display:block;padding:5px 5px 12px;color:#61758a;font-size:9px;font-weight:950;letter-spacing:.09em}.sb-mou-layout>main{min-width:0;border:0;background:transparent}.sb-mou-layout aside>button{display:block;width:100%;margin:0 0 8px;padding:13px 13px 12px;border:1px solid #d6e0ea;border-left:4px solid #b5c2cf;border-radius:9px;background:#fff;color:#203b57;text-align:left;cursor:pointer;transition:.16s ease}.sb-mou-layout aside>button:hover{border-color:#9fb4c9;transform:translateX(2px)}.sb-mou-layout aside>button.selected{border-color:#70af8d;border-left-color:var(--mou-green);background:#f0f9f4;box-shadow:0 5px 14px rgba(20,117,72,.08)}.sb-mou-layout aside b,.sb-mou-layout aside span,.sb-mou-layout aside small,.sb-mou-layout aside em{display:block}.sb-mou-layout aside b{color:#173653;font-size:12px;line-height:1.45;font-weight:900}.sb-mou-layout aside span{margin-top:6px;color:#52677d;font-size:10px;font-weight:750}.sb-mou-layout aside small{margin-top:5px;color:#718096;font-size:9px}.sb-mou-layout aside em{width:max-content;max-width:100%;margin-top:9px;padding:5px 8px;border-radius:20px;overflow:hidden;font-size:8px;font-style:normal;font-weight:950;text-overflow:ellipsis;white-space:nowrap}.sb-mou-layout aside em.pending{background:#fff0d9;color:#995a00}.sb-mou-layout aside em.awaiting{background:#e8f1ff;color:#165caa}.sb-mou-layout aside em.returned{background:#fff0f0;color:#a72c2c}.sb-mou-layout aside em.neutral{background:#edf2f7;color:#465f78}.sb-mou-layout aside em.done{background:#e8f8ed;color:#08733a}.sb-mou-empty,.sb-mou-complete,.sb-mou-waiting,.sb-mou-returned,.sb-mou-neutral{padding:42px 28px;border:1px solid #cbd8e5;border-radius:12px;background:#fff;text-align:center;color:#65768a;box-shadow:0 6px 18px rgba(11,49,94,.04)}.sb-mou-complete{border-top:4px solid #1a8753;background:#f3fbf6}.sb-mou-waiting{border-top:4px solid #2e72be;background:#f4f8fe}.sb-mou-returned{border-top:4px solid #c44444;background:#fff7f7}.sb-mou-neutral{border-top:4px solid #71869b;background:#f8fafc}.sb-mou-complete b,.sb-mou-complete span,.sb-mou-waiting b,.sb-mou-waiting span,.sb-mou-returned b,.sb-mou-returned span,.sb-mou-neutral b,.sb-mou-neutral span{display:block}.sb-mou-complete b{color:#116b38;font-size:18px}.sb-mou-waiting b{color:#175da6;font-size:18px}.sb-mou-returned b{color:#a12b2b;font-size:18px}.sb-mou-neutral b{color:#254968;font-size:18px}.sb-mou-complete span,.sb-mou-waiting span,.sb-mou-returned span,.sb-mou-neutral span{max-width:650px;margin:9px auto 18px;font-size:11px;line-height:1.6}.sb-mou-complete a,.sb-mou-waiting a,.sb-mou-returned a,.sb-mou-neutral a,.sb-mou-neutral button{display:inline-flex;align-items:center;justify-content:center;min-height:40px;padding:10px 15px;border:0;border-radius:8px;background:var(--mou-green);color:#fff;text-decoration:none;font-size:10px;font-weight:900;cursor:pointer}.sb-mou-waiting a{background:#175da6}.sb-mou-returned a{background:#a12b2b}.sb-mou-neutral a,.sb-mou-neutral button{background:#254968}@media(max-width:1280px){.sb-mou-summary{grid-template-columns:repeat(3,1fr)}}@media(max-width:980px){.sb-mou-summary{grid-template-columns:repeat(2,1fr)}.sb-mou-layout{grid-template-columns:1fr}.sb-mou-layout>aside{max-height:360px}}@media(max-width:650px){.sb-mou-summary{grid-template-columns:1fr}.sb-mou-summary nav{grid-column:auto}.sb-mou-summary nav:before{width:100%}}
`;

function StatePacsApplications({
  projects = [],
  approvedPmcs = [],
  allocations = [],
  pacsUsers = [],
}) {
  const [search, setSearch] = useState("");
  const [pmcFilter, setPmcFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const pmcValue = (project) =>
    project.allocatedPmcUid ||
    project.pmcUid ||
    project.allocatedPmcId ||
    project.pmcId ||
    "UNALLOCATED";
  const pmcName = (project) =>
    project.allocatedPmcName ||
    project.pmcName ||
    project.pmcAgencyName ||
    "PMC not allocated";
  const approvedPmcName = (pmc) =>
    pmc.organizationName ||
    pmc.agencyName ||
    pmc.companyName ||
    pmc.name ||
    "Approved PMC";
  const districtValue = (project) =>
    project.district ||
    project.districtName ||
    project.projectDistrict ||
    "District pending";
  const stageValue = (project) =>
    project.currentStage ||
    project.applicationStatus ||
    project.rawStatus ||
    project.status ||
    "APPLIED";

  const pmcs = useMemo(() => {
    const records = approvedPmcs.map((pmc) => ({
      uid: pmc.id,
      name: approvedPmcName(pmc),
      email: pmc.email || "",
      mobile: pmc.mobile || pmc.phone || "",
      districts: pmc.allocatedDistricts || pmc.districts || [],
      projects: [],
      capacity: 0,
    }));

    /*
     * pacs_pmc_allocations is the master register for this page. A PACS must
     * remain visible after allocation even when it has not yet submitted a
     * godown_projects application. Project/profile data only enriches that
     * master row; it never decides whether the allocated PACS is counted.
     */
    const activeAllocations = allocations
      .filter(isActivePmcAllocation)
      .sort(
        (a, b) =>
          toMillis(b.updatedAt || b.allocatedAt || b.createdAt) -
          toMillis(a.updatedAt || a.allocatedAt || a.createdAt),
      );

    const identityKeys = (record = {}) =>
      Array.from(new Set(pacsDedupeKeys(record)));
    const recordsMatchPacs = (left = {}, right = {}) => {
      const leftKeys = new Set(identityKeys(left));
      return identityKeys(right).some((key) => leftKeys.has(key));
    };
    const representedPacsKeys = new Set();
    const rememberPacs = (...sources) =>
      sources
        .filter(Boolean)
        .flatMap(identityKeys)
        .forEach((key) => representedPacsKeys.add(key));
    const alreadyRepresented = (...sources) =>
      sources
        .filter(Boolean)
        .flatMap(identityKeys)
        .some((key) => representedPacsKeys.has(key));

    const linkedProfileFor = (source = {}) =>
      pacsUsers.find((pacs) => recordsMatchPacs(source, pacs)) || null;
    const linkedProjectFor = (source = {}, profile = null) =>
      projects
        .filter(
          (project) =>
            (Array.isArray(source.projectIds) &&
              source.projectIds.some(
                (projectId) => normalise(projectId) === normalise(project.id),
              )) ||
            recordsMatchPacs(source, project) ||
            (profile && recordsMatchPacs(profile, project)),
        )
        .sort(
          (a, b) =>
            toMillis(b.updatedAt || b.createdAt || b.submittedAt) -
            toMillis(a.updatedAt || a.createdAt || a.submittedAt),
        )[0] || null;

    const appendPacsRow = ({ allocation = null, profile = null, project = null }) => {
      const reference = allocation || profile || project || {};
      const combinedReference = {
        ...(profile || {}),
        ...(project || {}),
        ...(allocation || {}),
      };
      const currentPmc = resolveCurrentPmc(combinedReference, approvedPmcs);
      const currentPmcUid = normalise(
        currentPmc?.id || pmcValue(combinedReference),
      );
      const currentPmcName = canonicalPmcNameKey(
        currentPmc
          ? approvedPmcName(currentPmc)
          : pmcName(combinedReference),
      );
      const record = records.find(
        (pmc) =>
          normalise(pmc.uid) === currentPmcUid ||
          canonicalPmcNameKey(pmc.name) === currentPmcName,
      );
      if (!record) return false;

      const hasApplication = Boolean(project?.id);
      const rowId = hasApplication
        ? project.id
        : `ALLOCATED_${allocation?.id || profile?.id || reference.id || record.projects.length}`;
      const pacsDisplaySource = profile || project || allocation || {};
      const row = {
        ...(profile || {}),
        ...(project || {}),
        ...(allocation || {}),
        id: rowId,
        projectDocumentId: project?.id || "",
        allocationRecordId: allocation?.id || "",
        applicationId: project?.applicationId || "",
        applicationRecordAvailable: hasApplication,
        pacsName: firstRecordValue(pacsDisplaySource, PACS_NAME_FIELDS),
        societyName: firstRecordValue(pacsDisplaySource, [
          "societyName",
          "pacsName",
          "organizationName",
          "name",
        ]),
        organizationName: firstRecordValue(pacsDisplaySource, [
          "organizationName",
          "societyName",
          "pacsName",
          "name",
        ]),
        pacsRegistrationNo: firstRecordValue(combinedReference, [
          "pacsRegistrationNo",
          "pacsRegistrationNumber",
          "registrationNumber",
          "registrationNo",
        ]),
        currentStage: hasApplication
          ? stageValue(project)
          : "PMC_ALLOCATED_APPLICATION_AWAITED",
        capacityMt: hasApplication
          ? Number(projectCapacity(project) || 0)
          : 0,
        allocatedPmcUid: record.uid,
        allocatedPmcId: record.uid,
        pmcUid: record.uid,
        pmcId: record.uid,
        allocatedPmcName: record.name,
        assignedPmcName: record.name,
        pmcName: record.name,
        pmcAgencyName: record.name,
      };
      record.projects.push(row);
      record.capacity += Number(row.capacityMt || 0);
      rememberPacs(allocation, profile, project, row);
      return true;
    };

    // Keep only the newest active allocation for each PACS.
    const seenAllocationKeys = new Set();
    activeAllocations.forEach((allocation) => {
      const strongKeys = pacsDedupeKeys(allocation);
      if (strongKeys.some((key) => seenAllocationKeys.has(key))) return;
      strongKeys.forEach((key) => seenAllocationKeys.add(key));
      const profile = linkedProfileFor(allocation);
      const project = linkedProjectFor(allocation, profile);
      appendPacsRow({ allocation, profile, project });
    });

    // Compatibility: some older builds saved allocation only on the PACS
    // profile. Include those PACS if an official allocation record is absent.
    pacsUsers
      .filter((pacs) => !isDeletedProject(pacs) && hasPmcReference(pacs))
      .forEach((profile) => {
        if (alreadyRepresented(profile)) return;
        appendPacsRow({
          profile,
          project: linkedProjectFor(profile, profile),
        });
      });

    // Final compatibility for project-only legacy allocation fields.
    projects.forEach((project) => {
      if (alreadyRepresented(project) || !hasPmcReference(project)) return;
      appendPacsRow({
        profile: linkedProfileFor(project),
        project,
      });
    });

    return records.sort((a, b) => a.name.localeCompare(b.name));
  }, [projects, approvedPmcs, allocations, pacsUsers]);

  const totalAllocatedPacs = pmcs.reduce(
    (total, pmc) => total + pmc.projects.length,
    0,
  );

  const selectedPmc = pmcs.find((pmc) => pmc.uid === pmcFilter) || null;
  const selectedProjects = selectedPmc?.projects || [];

  const districts = useMemo(
    () => Array.from(new Set(selectedProjects.map(districtValue))).sort(),
    [selectedProjects],
  );
  const statuses = useMemo(
    () => Array.from(new Set(selectedProjects.map(stageValue))).sort(),
    [selectedProjects],
  );

  useEffect(() => {
    setDistrictFilter("ALL");
    setStatusFilter("ALL");
    setSearch("");
  }, [pmcFilter]);

  const filtered = selectedProjects.filter((project) => {
    const haystack = [
      project.applicationId,
      project.id,
      projectName(project),
      pmcName(project),
      districtValue(project),
      stageValue(project),
    ]
      .join(" ")
      .toLowerCase();
    return (
      (districtFilter === "ALL" || districtValue(project) === districtFilter) &&
      (statusFilter === "ALL" || stageValue(project) === statusFilter) &&
      haystack.includes(search.trim().toLowerCase())
    );
  });

  const totalCapacity = filtered.reduce(
    (sum, project) => sum + Number(projectCapacity(project) || 0),
    0,
  );
  const csvCell = (value) => `"${String(value ?? "").replaceAll('"', '""')}"`;

  const downloadCsv = () => {
    if (!selectedPmc) return;
    const rows = [
      [
        "Application ID",
        "PACS / Society",
        "Registration No.",
        "District",
        "Taluka",
        "Village",
        "PMC",
        "Capacity (MT)",
        "Current Stage",
      ],
      ...filtered.map((project) => [
        project.applicationRecordAvailable
          ? project.applicationId || project.projectDocumentId || project.id
          : "Not Submitted",
        projectName(project),
        project.pacsRegistrationNo || project.registrationNumber || "",
        districtValue(project),
        project.taluka || project.talukaName || "",
        project.village || project.villageName || "",
        pmcName(project),
        project.applicationRecordAvailable ? projectCapacity(project) : "",
        stageValue(project).replaceAll("_", " "),
      ]),
    ];
    const csv =
      "\uFEFF" + rows.map((row) => row.map(csvCell).join(",")).join("\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `${selectedPmc.name.replace(/[^a-z0-9]+/gi, "_")}_PACS_Report.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const printReport = () => {
    if (!selectedPmc) return;
    const escapeHtml = (value) =>
      String(value ?? "").replace(
        /[&<>"']/g,
        (character) =>
          ({
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            '"': "&quot;",
            "'": "&#039;",
          })[character],
      );
    const rows = filtered
      .map(
        (project, index) =>
          `<tr><td>${index + 1}</td><td>${escapeHtml(project.applicationRecordAvailable ? project.applicationId || project.projectDocumentId || project.id : "Not Submitted")}</td><td>${escapeHtml(projectName(project))}</td><td>${escapeHtml(districtValue(project))}</td><td>${escapeHtml(project.applicationRecordAvailable ? `${projectCapacity(project)} MT` : "-")}</td><td>${escapeHtml(stageValue(project).replaceAll("_", " "))}</td></tr>`,
      )
      .join("");
    const report = window.open("", "_blank", "width=1100,height=800");
    if (!report) return;
    report.document.write(
      `<!doctype html><html><head><title>PMC PACS Report</title><style>@page{size:A4 landscape;margin:14mm}body{font-family:Arial;color:#172b45}h1{font-size:20px;margin:0}h2{font-size:15px;margin:7px 0}.meta{margin:12px 0;padding:10px;border:1px solid #aaa}table{width:100%;border-collapse:collapse;font-size:10px}th,td{padding:8px;border:1px solid #555;text-align:left}th{background:#e9eef3}.foot{margin-top:15px;font-size:9px;color:#555}</style></head><body><h1>NAFED State Branch — PMC-wise Allocated PACS Report</h1><h2>${escapeHtml(selectedPmc.name)}</h2><div class="meta">Allocated PACS: <b>${filtered.length}</b> &nbsp; | &nbsp; Applied Capacity: <b>${escapeHtml(formatCapacity(totalCapacity))}</b> &nbsp; | &nbsp; Generated: ${escapeHtml(new Date().toLocaleString("en-IN"))}</div><table><thead><tr><th>#</th><th>Application</th><th>PACS / Society</th><th>District</th><th>Capacity</th><th>Current Stage</th></tr></thead><tbody>${rows || '<tr><td colspan="6">No records</td></tr>'}</tbody></table><div class="foot">System-generated monitoring report.</div><script>window.onload=()=>window.print();<\/script></body></html>`,
    );
    report.document.close();
  };

  return (
    <section className="state-pacs">
      <style>{STATE_PACS_STYLE}</style>
      <div className="state-pacs-kpis">
        <div>
          <span>State PMCs</span>
          <b>{pmcs.length}</b>
        </div>
        <div>
          <span>Allocated PACS</span>
          <b>{totalAllocatedPacs}</b>
        </div>
        <div>
          <span>Selected PMC PACS</span>
          <b>{selectedPmc ? selectedProjects.length : "—"}</b>
        </div>
        <div>
          <span>Selected Capacity</span>
          <b>{selectedPmc ? formatCapacity(selectedPmc.capacity) : "—"}</b>
        </div>
      </div>

      <div className="pmc-selector-head">
        <div>
          <b>Step 1 — Select PMC</b>
          <span>Choose a PMC to view every PACS allocated to it.</span>
        </div>
      </div>
      <div className="pmc-selector">
        {!pmcs.length && (
          <div className="no-pmc">
            No PMC-linked PACS allocation is available.
          </div>
        )}
        {pmcs.map((pmc) => (
          <button
            key={pmc.uid}
            className={pmcFilter === pmc.uid ? "active" : ""}
            onClick={() => setPmcFilter(pmc.uid)}
          >
            <span>{pmc.name}</span>
            <b>{pmc.projects.length} PACS</b>
            <small>{formatCapacity(pmc.capacity)}</small>
          </button>
        ))}
      </div>

      {!selectedPmc && (
        <div className="select-pmc-message">
          <b>Select a PMC first</b>
          <span>
            The PMC's PACS list, stages and report options will appear here.
          </span>
        </div>
      )}

      {selectedPmc && (
        <>
          <div className="selected-pmc-head">
            <div>
              <small>SELECTED PMC</small>
              <h3>{selectedPmc.name}</h3>
              <p>
                {selectedProjects.length} allocated PACS ·{" "}
                {formatCapacity(selectedPmc.capacity)}
              </p>
            </div>
            <div>
              <button onClick={downloadCsv}>Download Excel/CSV</button>
              <button onClick={printReport}>Print / Save PDF</button>
            </div>
          </div>
          <div className="state-pacs-filters">
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search application or PACS"
            />
            <select
              value={districtFilter}
              onChange={(event) => setDistrictFilter(event.target.value)}
            >
              <option value="ALL">All Districts</option>
              {districts.map((district) => (
                <option key={district} value={district}>
                  {district}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="ALL">All Stages</option>
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {status.replaceAll("_", " ")}
                </option>
              ))}
            </select>
            <div className="filter-result">
              Showing <b>{filtered.length}</b> of {selectedProjects.length}
            </div>
          </div>

          <div className="state-pacs-table">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Application</th>
                  <th>PACS / Society</th>
                  <th>District / Taluka</th>
                  <th>Capacity</th>
                  <th>Current Work Stage</th>
                </tr>
              </thead>
              <tbody>
                {!filtered.length && (
                  <tr>
                    <td colSpan="6" className="no-record">
                      No PACS application matches the selected filters.
                    </td>
                  </tr>
                )}
                {filtered.map((project, index) => (
                  <tr key={project.id}>
                    <td>{index + 1}</td>
                    <td>
                      <b>
                        {project.applicationRecordAvailable
                          ? project.applicationId ||
                            project.projectDocumentId ||
                            project.id
                          : "Not Submitted"}
                      </b>
                      <small>
                        {project.applicationRecordAvailable
                          ? project.projectName || "NAFED Grain Storage Project"
                          : "PMC allocation complete · application awaited"}
                      </small>
                    </td>
                    <td>
                      <b>{projectName(project)}</b>
                      <small>
                        {project.pacsRegistrationNo ||
                          project.registrationNumber ||
                          "-"}
                      </small>
                    </td>
                    <td>
                      <b>{districtValue(project)}</b>
                      <small>
                        {project.taluka ||
                          project.talukaName ||
                          project.village ||
                          project.villageName ||
                          "-"}
                      </small>
                    </td>
                    <td>
                      {project.applicationRecordAvailable
                        ? formatCapacity(projectCapacity(project))
                        : "—"}
                    </td>
                    <td>
                      <span className="state-stage">
                        {stageValue(project).replaceAll("_", " ")}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function PacsPmcAllocationDesk({
  pacsUsers = [],
  projects = [],
  approvedPmcs = [],
  allocations = [],
  branchState = "",
}) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [districtFilter, setDistrictFilter] = useState("ALL");
  const [selectedPacsId, setSelectedPacsId] = useState("");
  const [selectedPmcId, setSelectedPmcId] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [messageTone, setMessageTone] = useState("success");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const pmcName = (pmc = {}) => entityName(pmc);
  const pmcMobile = (pmc = {}) =>
    pmc.mobile || pmc.phone || pmc.contactMobile || "";
  const pmcEmail = (pmc = {}) =>
    pmc.email || pmc.officialEmail || pmc.contactEmail || "";

  const pacsDistrict = (pacs = {}) =>
    firstRecordValue(pacs, [
      "district",
      "districtName",
      "locationDistrict",
      "projectDistrict",
      "registeredDistrict",
    ]) ||
    (pacs.projects || [])
      .map((project) =>
        firstRecordValue(project, [
          "district",
          "districtName",
          "locationDistrict",
          "projectDistrict",
        ]),
      )
      .find(Boolean) ||
    "District Pending";

  const pacsTaluka = (pacs = {}) =>
    firstRecordValue(pacs, [
      "taluka",
      "talukaName",
      "block",
      "blockName",
      "tehsil",
    ]) ||
    (pacs.projects || [])
      .map((project) =>
        firstRecordValue(project, [
          "taluka",
          "talukaName",
          "block",
          "blockName",
          "tehsil",
        ]),
      )
      .find(Boolean) ||
    "";

  const pacsVillage = (pacs = {}) =>
    firstRecordValue(pacs, [
      "village",
      "villageName",
      "locationVillage",
      "projectVillage",
    ]) ||
    (pacs.projects || [])
      .map((project) =>
        firstRecordValue(project, [
          "village",
          "villageName",
          "locationVillage",
          "projectVillage",
        ]),
      )
      .find(Boolean) ||
    "";

  const rows = useMemo(() => {
    const map = new Map();
    const aliases = new Map();
    const hiddenAliases = new Set(
      allocations
        .filter(
          (allocation) =>
            allocation.deskVisibility === "HIDDEN" ||
            allocation.hiddenFromAllocationDesk === true ||
            upper(allocation.status) === "ARCHIVED_FROM_DESK",
        )
        .flatMap((allocation) => [
          ...allocationPacsKeys(allocation),
          ...pacsDedupeKeys(allocation),
        ]),
    );

    const isHidden = (record) =>
      [...allocationPacsKeys(record), ...pacsDedupeKeys(record)].some((key) =>
        hiddenAliases.has(key),
      );

    const resolveGroup = (record, fallbackId) => {
      const identityKeys = pacsDedupeKeys(record);
      const existingId = identityKeys
        .map((key) => aliases.get(key))
        .find(Boolean);
      const requestedId =
        existingId ||
        record.uid ||
        record.pacsUid ||
        record.pacsUserUid ||
        record.pacsUserId ||
        record.pacsId ||
        record.societyUid ||
        fallbackId;
      return { id: String(requestedId || ""), identityKeys };
    };

    const registerAliases = (groupId, record, identityKeys = []) => {
      [
        ...identityKeys,
        ...allocationPacsKeys(record).map((key) => `legacy:${key}`),
      ].forEach((key) => aliases.set(key, groupId));
    };

    const findExistingGroup = (record, identityKeys = []) =>
      [
        ...identityKeys,
        ...allocationPacsKeys(record).map((key) => `legacy:${key}`),
      ]
        .map((key) => aliases.get(key))
        .find(Boolean);

    pacsUsers.forEach((pacs) => {
      if (isDeletedProject(pacs) || isHidden(pacs)) return;
      const identityKeys = pacsDedupeKeys(pacs);
      const matchedId = findExistingGroup(pacs, identityKeys);
      const resolved = resolveGroup(pacs, pacs.id);
      const groupId = matchedId || resolved.id;
      if (!groupId) return;
      const existing = map.get(groupId);
      const sourceUserId = String(pacs.id || pacs.uid || "");
      const merged = mergeDefined(existing || {}, pacs);
      map.set(groupId, {
        ...merged,
        id: groupId,
        uid: existing?.uid || pacs.uid || pacs.id || groupId,
        sourceUser: true,
        sourceUserIds: Array.from(
          new Set(
            [...(existing?.sourceUserIds || []), sourceUserId].filter(Boolean),
          ),
        ),
        projects: existing?.projects || [],
      });
      registerAliases(groupId, pacs, identityKeys);
    });

    projects.forEach((project) => {
      if (isDeletedProject(project) || isHidden(project)) return;
      const identityKeys = pacsDedupeKeys(project);
      const matchedId = findExistingGroup(project, identityKeys);
      const resolved = resolveGroup(project, `PROJECT_${project.id}`);
      const groupId = matchedId || resolved.id;
      if (!groupId) return;
      const existing = map.get(groupId);
      if (!existing) {
        map.set(groupId, {
          id: groupId,
          uid:
            project.pacsUid ||
            project.pacsUserUid ||
            project.pacsId ||
            project.pacsUserId ||
            project.createdByUid ||
            groupId,
          sourceUser: false,
          sourceUserIds: [],
          organizationName: projectName(project),
          societyName: projectName(project),
          email: project.pacsEmail || project.email || "",
          mobile: project.pacsMobile || project.mobile || "",
          registrationNumber:
            project.pacsRegistrationNo || project.registrationNumber || "",
          state: projectState(project),
          district: project.district || project.districtName || "",
          taluka: project.taluka || project.talukaName || "",
          village: project.village || project.villageName || "",
          projects: [],
        });
      }
      const group = map.get(groupId);
      if (!group.projects.some((item) => item.id === project.id)) {
        group.projects.push(project);
      }
      registerAliases(groupId, project, identityKeys);
    });

    // Allocation-only legacy PACS remain visible, but only one canonical row
    // is produced even when multiple allocation documents describe the same PACS.
    allocations.filter(isActivePmcAllocation).forEach((allocation) => {
      if (isHidden(allocation)) return;
      const identityKeys = pacsDedupeKeys(allocation);
      const matchedId = findExistingGroup(allocation, identityKeys);
      const resolved = resolveGroup(allocation, allocation.id);
      const groupId = matchedId || resolved.id;
      if (!groupId) return;
      if (!map.has(groupId)) {
        map.set(groupId, {
          id: groupId,
          uid:
            allocation.pacsUid ||
            allocation.pacsUserUid ||
            allocation.pacsUserId ||
            allocation.pacsId ||
            allocation.societyUid ||
            allocation.id,
          sourceUser: false,
          sourceUserIds: [],
          organizationName:
            allocation.organizationName ||
            allocation.pacsName ||
            allocation.societyName ||
            "Allocated PACS",
          societyName:
            allocation.societyName || allocation.pacsName || "Allocated PACS",
          email: allocation.pacsEmail || allocation.email || "",
          mobile:
            allocation.pacsMobile ||
            allocation.mobile ||
            allocation.phone ||
            "",
          registrationNumber:
            allocation.pacsRegistrationNo ||
            allocation.registrationNumber ||
            allocation.registrationNo ||
            "",
          state: allocation.state || allocation.stateName || branchState,
          district: allocation.district || allocation.districtName || "",
          taluka: allocation.taluka || allocation.talukaName || "",
          village: allocation.village || allocation.villageName || "",
          projects: [],
        });
      }
      registerAliases(groupId, allocation, identityKeys);
    });

    return Array.from(map.values())
      .map((pacs) => {
        const keys = new Set([
          ...allocationPacsKeys(pacs),
          ...pacsDedupeKeys(pacs),
        ]);
        const activeAllocation = allocations
          .filter(
            (item) =>
              isActivePmcAllocation(item) &&
              [...allocationPacsKeys(item), ...pacsDedupeKeys(item)].some(
                (key) => keys.has(key),
              ),
          )
          .sort(
            (a, b) =>
              toMillis(b.updatedAt || b.allocatedAt || b.createdAt) -
              toMillis(a.updatedAt || a.allocatedAt || a.createdAt),
          )[0];
        const projectAllocation = pacs.projects.find(hasPmcReference);
        const allocatedPmcUid =
          activeAllocation?.allocatedPmcUid ||
          activeAllocation?.allocatedPmcId ||
          activeAllocation?.pmcUid ||
          activeAllocation?.pmcId ||
          pacs.allocatedPmcUid ||
          pacs.allocatedPmcId ||
          pacs.pmcUid ||
          pacs.pmcId ||
          projectAllocation?.allocatedPmcUid ||
          projectAllocation?.allocatedPmcId ||
          projectAllocation?.pmcUid ||
          projectAllocation?.pmcId ||
          "";
        const allocationPmcName =
          activeAllocation?.allocatedPmcName ||
          activeAllocation?.pmcName ||
          pacs.allocatedPmcName ||
          pacs.pmcName ||
          projectAllocation?.allocatedPmcName ||
          projectAllocation?.pmcName ||
          "";
        const linkedPmc = resolveCurrentPmc(
          {
            ...(projectAllocation || {}),
            ...pacs,
            ...(activeAllocation || {}),
            allocatedPmcUid,
            allocatedPmcName: allocationPmcName,
          },
          approvedPmcs,
        );
        return {
          ...pacs,
          activeAllocation,
          allocatedPmcSourceUid: allocatedPmcUid,
          allocatedPmcUid: linkedPmc?.id || allocatedPmcUid,
          allocatedPmcName: linkedPmc ? pmcName(linkedPmc) : allocationPmcName,
          linkedPmc,
        };
      })
      .sort((a, b) => entityName(a).localeCompare(entityName(b)));
  }, [pacsUsers, projects, allocations, approvedPmcs, branchState]);

  const visibleRows = rows.filter((pacs) => {
    const allocated = Boolean(pacs.allocatedPmcUid || pacs.allocatedPmcName);
    const haystack = [
      entityName(pacs),
      pacs.email,
      pacs.mobile,
      pacs.registrationNumber,
      pacs.registrationNo,
      pacsDistrict(pacs),
      pacsTaluka(pacs),
      pacsVillage(pacs),
      pacs.allocatedPmcName,
    ]
      .join(" ")
      .toLowerCase();
    return (
      (filter === "ALL" ||
        (filter === "ALLOCATED" && allocated) ||
        (filter === "UNALLOCATED" && !allocated)) &&
      (districtFilter === "ALL" ||
        normalise(pacsDistrict(pacs)) === normalise(districtFilter)) &&
      haystack.includes(search.trim().toLowerCase())
    );
  });

  const districts = useMemo(
    () =>
      Array.from(
        new Set(
          rows
            .map(pacsDistrict)
            .filter(
              (district) =>
                district && normalise(district) !== "district pending",
            ),
        ),
      ).sort((a, b) => a.localeCompare(b, "en", { sensitivity: "base" })),
    [rows],
  );

  const districtSummary = useMemo(
    () =>
      districts.map((district) => {
        const districtRows = rows.filter(
          (pacs) => normalise(pacsDistrict(pacs)) === normalise(district),
        );
        const allocated = districtRows.filter(
          (pacs) => pacs.allocatedPmcUid || pacs.allocatedPmcName,
        ).length;
        return {
          district,
          total: districtRows.length,
          allocated,
          pending: Math.max(districtRows.length - allocated, 0),
        };
      }),
    [districts, rows],
  );

  const totalPages = Math.max(Math.ceil(visibleRows.length / PAGE_SIZE), 1);
  const pagedRows = visibleRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const duplicateProfilesMerged = rows.reduce(
    (total, item) => total + Math.max((item.sourceUserIds?.length || 1) - 1, 0),
    0,
  );

  useEffect(() => {
    setPage(1);
  }, [search, filter, districtFilter]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const selectedPacs =
    rows.find((item) => item.id === selectedPacsId) || visibleRows[0] || null;

  useEffect(() => {
    if (!selectedPacs) {
      setSelectedPacsId("");
      setSelectedPmcId("");
      return;
    }
    if (selectedPacs.id !== selectedPacsId) setSelectedPacsId(selectedPacs.id);
    setSelectedPmcId(selectedPacs.allocatedPmcUid || "");
  }, [selectedPacs?.id, selectedPacs?.allocatedPmcUid]);

  const relatedProjects = (pacs) => {
    const keys = new Set([
      ...allocationPacsKeys(pacs),
      ...pacsDedupeKeys(pacs),
    ]);
    return projects.filter((project) => {
      const linked = [
        ...allocationPacsKeys(project),
        ...pacsDedupeKeys(project),
      ].some((key) => keys.has(key));
      return (
        linked ||
        (!keys.size &&
          normalise(projectName(project)) === normalise(entityName(pacs)))
      );
    });
  };

  const saveAudit = async (action, pacs, pmc = null) => {
    try {
      await addDoc(collection(db, "nafed_activity"), {
        action,
        targetUid: pacs.uid || pacs.id,
        targetRole: "PACS",
        targetName: entityName(pacs),
        state: branchState,
        allocatedPmcUid: pmc?.id || "",
        allocatedPmcName: pmc ? pmcName(pmc) : "",
        performedByUid: auth.currentUser?.uid || "",
        performedByRole: "STATE_BRANCH",
        createdAt: serverTimestamp(),
      });
    } catch (error) {
      console.warn("Allocation audit could not be written:", error);
    }
  };

  const showMessage = (text, tone = "success") => {
    setMessageTone(tone);
    setMessage(text);
  };

  const propagateAllocationBestEffort = async (
    pacs,
    linkedProjects,
    fields,
    profileWorkflowFields = {},
  ) => {
    const warnings = [];

    if (pacs.sourceUser && pacs.sourceUserIds?.length) {
      try {
        const userBatch = writeBatch(db);
        pacs.sourceUserIds.forEach((userId) => {
          userBatch.set(
            doc(db, "nafed_users", userId),
            { ...fields, ...profileWorkflowFields },
            { merge: true },
          );
        });
        await userBatch.commit();
      } catch (error) {
        console.warn("PACS profile sync skipped:", error);
        warnings.push("PACS profile");
      }
    }

    if (linkedProjects.length) {
      try {
        const projectBatch = writeBatch(db);
        linkedProjects.forEach((project) => {
          projectBatch.set(doc(db, "godown_projects", project.id), fields, {
            merge: true,
          });
        });
        await projectBatch.commit();
      } catch (error) {
        console.warn("Linked application sync skipped:", error);
        warnings.push("linked applications");
      }
    }

    return warnings;
  };

  const queueAllocationCommunications = async (pacs, pmc, linkedProjects) => {
    const warnings = [];
    const pacsUid = pacs.uid || pacs.id;
    const pacsName = entityName(pacs);
    const currentPmcName = pmcName(pmc);
    const pacsEmail = firstRecordValue(pacs, [
      "email",
      "pacsEmail",
      "officialEmail",
      "contactEmail",
    ]);
    const pacsMobile = firstRecordValue(pacs, [
      "mobile",
      "phone",
      "pacsMobile",
      "contactMobile",
    ]);
    const registrationNumber = firstRecordValue(pacs, [
      "pacsRegistrationNo",
      "pacsRegistrationNumber",
      "registrationNumber",
      "registrationNo",
    ]);
    const location = [
      pacsVillage(pacs),
      pacsTaluka(pacs),
      pacsDistrict(pacs),
      branchState,
    ]
      .filter(Boolean)
      .join(", ");
    const pmcRecipientEmail = pmcEmail(pmc);
    const pmcPortalUrl = `${window.location.origin}/nafed/pmc`;
    const pacsPortalUrl = `${window.location.origin}/nafed/pacs`;

    const emailLayout = ({ heading, intro, details, steps, portalUrl }) => `
      <div style="margin:0;padding:24px;background:#eef3f7;font-family:Arial,sans-serif;color:#173451">
        <div style="max-width:720px;margin:auto;overflow:hidden;border:1px solid #c9d6e3;border-radius:14px;background:#ffffff">
          <div style="padding:20px 24px;background:linear-gradient(110deg,#082f5b,#176640);color:#ffffff">
            <div style="font-size:12px;font-weight:700;letter-spacing:.08em">NAFED STATE BRANCH PORTAL</div>
            <h1 style="margin:7px 0 0;font-size:22px">${reportEscapeHtml(heading)}</h1>
          </div>
          <div style="padding:22px 24px">
            <p style="margin:0 0 16px;line-height:1.6">${reportEscapeHtml(intro)}</p>
            <table style="width:100%;border-collapse:collapse;margin-bottom:18px;font-size:13px">
              ${details
                .map(
                  ([label, value]) =>
                    `<tr><td style="width:185px;padding:9px;border:1px solid #d6e0ea;background:#f3f7fa;font-weight:700">${reportEscapeHtml(label)}</td><td style="padding:9px;border:1px solid #d6e0ea">${reportEscapeHtml(value || "-")}</td></tr>`,
                )
                .join("")}
            </table>
            <h2 style="margin:0 0 10px;color:#0b396c;font-size:16px">Next Process</h2>
            <ol style="margin:0 0 20px;padding-left:21px;line-height:1.75">
              ${steps.map((step) => `<li>${reportEscapeHtml(step)}</li>`).join("")}
            </ol>
            <a href="${reportEscapeHtml(portalUrl)}" style="display:inline-block;padding:11px 17px;border-radius:8px;background:#14743f;color:#ffffff;text-decoration:none;font-weight:700">Open NAFED Portal</a>
          </div>
          <div style="padding:12px 24px;background:#f3f7fa;color:#607286;font-size:11px">System-generated allocation communication · ${reportEscapeHtml(branchState)} State Branch</div>
        </div>
      </div>`;

    const pmcSubject = `New PACS Allocation: ${pacsName}`;
    const pacsSubject = `PMC Allocation Confirmed: ${currentPmcName}`;
    const pmcSteps = [
      "Log in to the PMC Dashboard and open the newly allocated PACS file.",
      "Contact the PACS using the registered email/mobile and confirm the nodal person.",
      "Verify PACS registration, land ownership or lease documents, resolutions and financial records.",
      "Guide the PACS to upload missing documents and complete document scrutiny.",
      "After scrutiny, continue the MOU, DPR and bank-linked workflow from the portal.",
    ];
    const pacsSteps = [
      "Log in to the PACS Dashboard and check the allocated PMC details.",
      "Coordinate with the PMC and confirm the authorised PACS contact person.",
      "Keep the registration certificate, land/lease documents, required resolutions, last three years' balance sheets and audit reports ready.",
      "Upload all requested documents in the PACS Dashboard for PMC scrutiny.",
      "After verification, proceed with MOU, DPR and the subsequent project workflow as instructed by the PMC.",
    ];

    try {
      await addDoc(collection(db, "notifications_pmc"), {
        recipientUid: pmc.id,
        recipientRole: "PMC",
        pmcUid: pmc.id,
        pacsUid,
        projectIds: linkedProjects.map((project) => project.id),
        title: "New PACS allocated by State Branch",
        message: `${pacsName} has been allocated to your PMC. Open the file and start document coordination.`,
        nextAction: "OPEN_PACS_DOCUMENT_SCRUTINY",
        state: branchState,
        type: "PACS_ALLOCATED",
        status: "UNREAD",
        isRead: false,
        createdAt: serverTimestamp(),
      });
    } catch (error) {
      console.warn("PMC notification could not be written:", error);
      warnings.push("PMC notification");
    }

    try {
      await addDoc(collection(db, "notifications_pacs"), {
        recipientUid: pacsUid,
        recipientRole: "PACS",
        pacsUid,
        pmcUid: pmc.id,
        projectIds: linkedProjects.map((project) => project.id),
        title: "PMC allocation confirmed",
        message: `${currentPmcName} has been allocated to your PACS. Open the dashboard for PMC contact details and next steps.`,
        nextAction: "PREPARE_AND_UPLOAD_DOCUMENTS",
        state: branchState,
        type: "PMC_ALLOCATED",
        status: "UNREAD",
        isRead: false,
        createdAt: serverTimestamp(),
      });
    } catch (error) {
      console.warn("PACS notification could not be written:", error);
      warnings.push("PACS notification");
    }

    const mailJobs = [];
    if (pmcRecipientEmail) {
      mailJobs.push({
        to: pmcRecipientEmail,
        recipientRole: "PMC",
        subject: pmcSubject,
        text: `${pacsName} has been allocated to ${currentPmcName}. Registration: ${registrationNumber || "-"}. Location: ${location || "-"}. PACS contact: ${pacsEmail || "-"} / ${pacsMobile || "-"}. Next: log in, contact PACS, verify documents and start scrutiny/MOU/DPR workflow.`,
        html: emailLayout({
          heading: "New PACS Allocation",
          intro: `${pacsName} has been officially allocated to ${currentPmcName} by the ${branchState} State Branch.`,
          details: [
            ["PACS / Society", pacsName],
            ["Registration Number", registrationNumber],
            ["Location", location],
            ["PACS Email", pacsEmail],
            ["PACS Mobile", pacsMobile],
          ],
          steps: pmcSteps,
          portalUrl: pmcPortalUrl,
        }),
      });
    } else {
      warnings.push("PMC email missing");
    }

    if (pacsEmail) {
      mailJobs.push({
        to: pacsEmail,
        recipientRole: "PACS",
        subject: pacsSubject,
        text: `${currentPmcName} has been allocated to ${pacsName}. PMC email: ${pmcRecipientEmail || "-"}; PMC mobile: ${pmcMobile(pmc) || "-"}. Next: log in, contact the PMC and upload registration, land/lease, resolutions and financial documents.`,
        html: emailLayout({
          heading: "PMC Allocation Confirmed",
          intro: `${currentPmcName} has been officially allocated to your PACS by the ${branchState} State Branch.`,
          details: [
            ["PACS / Society", pacsName],
            ["Registration Number", registrationNumber],
            ["Allocated PMC", currentPmcName],
            ["PMC Email", pmcRecipientEmail],
            ["PMC Mobile", pmcMobile(pmc)],
          ],
          steps: pacsSteps,
          portalUrl: pacsPortalUrl,
        }),
      });
    } else {
      warnings.push("PACS email missing");
    }

    if (mailJobs.length) {
      try {
        const mailBatch = writeBatch(db);
        mailJobs.forEach((job) => {
          mailBatch.set(doc(collection(db, "mail")), {
            to: [job.to],
            message: {
              subject: job.subject,
              text: job.text,
              html: job.html,
            },
            category: "PACS_PMC_ALLOCATION",
            module: "PACS_PMC_ALLOCATION",
            recipientRole: job.recipientRole,
            pacsUid,
            pmcUid: pmc.id,
            state: branchState,
            deliveryStatus: "QUEUED",
            createdAt: serverTimestamp(),
          });
        });
        await mailBatch.commit();
      } catch (error) {
        console.warn("Allocation email queue could not be written:", error);
        warnings.push("email queue");
      }
    }

    return warnings;
  };

  const saveAllocation = async () => {
    if (!selectedPacs || !selectedPmcId) {
      showMessage("PACS aur approved PMC select karna mandatory hai.", "error");
      return;
    }
    const pmc = approvedPmcs.find((item) => item.id === selectedPmcId);
    if (!pmc || isDeletedProject(pmc) || pmc.isActive === false) {
      showMessage("Selected PMC approved/active nahi hai.", "error");
      return;
    }
    if (
      !window.confirm(
        `${entityName(selectedPacs)} ko ${pmcName(pmc)} allocate karna hai?`,
      )
    )
      return;

    try {
      setBusy(true);
      setMessage("");
      const pacsUid = selectedPacs.uid || selectedPacs.id;
      const registrationNumber =
        selectedPacs.pacsRegistrationNo ||
        selectedPacs.registrationNumber ||
        selectedPacs.registrationNo ||
        "";
      const pacsName = entityName(selectedPacs);
      const common = {
        allocatedPmcUid: pmc.id,
        allocatedPmcId: pmc.id,
        allocatedPmcName: pmcName(pmc),
        allocatedPmcEmail: pmcEmail(pmc),
        allocatedPmcMobile: pmcMobile(pmc),
        allocatedPmcAddress:
          pmc.address || pmc.officeAddress || pmc.registeredAddress || "",
        pmcUid: pmc.id,
        pmcId: pmc.id,
        pmcName: pmcName(pmc),
        pmcEmail: pmcEmail(pmc),
        pmcMobile: pmcMobile(pmc),
        allocationStatus: "ACTIVE",
        allocatedByUid: auth.currentUser?.uid || "",
        allocatedByRole: "STATE_BRANCH",
        allocatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      /*
       * Allocation is the State Branch clearance. Work-start approval still
       * belongs to the allocated PMC, therefore only the PACS profile moves to
       * PENDING_PMC_APPROVAL. Linked project workflow status is never changed.
       */
      const profileWorkflowFields = {
        branchApprovalStatus: "APPROVED",
        roleApprovalStatus: "APPROVED",
        registrationStatus: "PENDING_PMC_APPROVAL",
        status: "PENDING_PMC_APPROVAL",
        approvalStage: "PMC_VERIFICATION",
      };

      // Commit the official allocation first. Optional legacy propagation is
      // performed afterwards so one restricted old collection cannot roll back
      // the valid allocation.
      const linkedProjects = relatedProjects(selectedPacs);
      const allocationBatch = writeBatch(db);
      allocationBatch.set(
        doc(db, "pacs_pmc_allocations", pacsUid),
        {
          pacsUid,
          pacsId: pacsUid,
          pacsUserUid: pacsUid,
          pacsUserId: pacsUid,
          allocatedPacsUid: pacsUid,
          allocatedPacsId: pacsUid,
          societyUid: selectedPacs.societyUid || pacsUid,
          pacsName,
          societyName: selectedPacs.societyName || pacsName,
          organizationName: selectedPacs.organizationName || pacsName,
          pacsRegistrationNo: registrationNumber,
          registrationNumber,
          registrationNo: registrationNumber,
          pacsEmail: firstRecordValue(selectedPacs, [
            "email",
            "pacsEmail",
            "officialEmail",
            "contactEmail",
          ]),
          pacsMobile: firstRecordValue(selectedPacs, [
            "mobile",
            "phone",
            "pacsMobile",
            "contactMobile",
          ]),
          projectIds: linkedProjects.map((project) => project.id),
          state: branchState,
          district: firstRecordValue(selectedPacs, [
            "district",
            "districtName",
          ]),
          taluka: firstRecordValue(selectedPacs, ["taluka", "talukaName"]),
          village: firstRecordValue(selectedPacs, ["village", "villageName"]),
          status: "ACTIVE",
          allocationMode: "PACS_WISE",
          isActive: true,
          isDeleted: false,
          createdAt:
            selectedPacs.activeAllocation?.createdAt || serverTimestamp(),
          ...common,
        },
        { merge: true },
      );
      await allocationBatch.commit();
      const syncWarnings = await propagateAllocationBestEffort(
        selectedPacs,
        linkedProjects,
        common,
        profileWorkflowFields,
      );
      const communicationWarnings = await queueAllocationCommunications(
        selectedPacs,
        pmc,
        linkedProjects,
      );
      const warnings = [...syncWarnings, ...communicationWarnings];
      await saveAudit("STATE_BRANCH_PACS_PMC_ALLOCATED", selectedPacs, pmc);
      showMessage(
        warnings.length
          ? `${entityName(selectedPacs)} allocation saved. Attention required: ${warnings.join(", ")}.`
          : `${entityName(selectedPacs)} successfully ${pmcName(pmc)} ko allocate ho gaya. PMC aur PACS dono ke email queue ho gaye.`,
        warnings.length ? "warning" : "success",
      );
    } catch (error) {
      console.error("PACS–PMC allocation failed:", error);
      showMessage(
        error?.code === "permission-denied"
          ? "Allocation collection write permission missing hai. ZIP me diya Firestore rule add-on apply karein."
          : error?.message ||
              "Allocation failed. Firestore rules aur branch permission check karein.",
        "error",
      );
    } finally {
      setBusy(false);
    }
  };

  const removeAllocation = async () => {
    if (!selectedPacs?.allocatedPmcUid && !selectedPacs?.allocatedPmcName)
      return;
    if (
      !window.confirm(
        `${entityName(selectedPacs)} ki current PMC allocation remove karni hai? Project delete nahi hoga.`,
      )
    )
      return;
    try {
      setBusy(true);
      setMessage("");
      const pacsUid = selectedPacs.uid || selectedPacs.id;
      const registrationNumber =
        selectedPacs.pacsRegistrationNo ||
        selectedPacs.registrationNumber ||
        selectedPacs.registrationNo ||
        "";
      const pacsName = entityName(selectedPacs);
      const cleared = {
        allocatedPmcUid: "",
        allocatedPmcId: "",
        allocatedPmcName: "",
        allocatedPmcEmail: "",
        allocatedPmcMobile: "",
        allocatedPmcAddress: "",
        pmcUid: "",
        pmcId: "",
        pmcName: "",
        pmcEmail: "",
        pmcMobile: "",
        allocationStatus: "UNALLOCATED",
        allocationRemovedByUid: auth.currentUser?.uid || "",
        allocationRemovedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      const allocationBatch = writeBatch(db);
      allocationBatch.set(
        doc(db, "pacs_pmc_allocations", pacsUid),
        {
          ...cleared,
          pacsUid,
          pacsId: pacsUid,
          pacsUserUid: pacsUid,
          pacsUserId: pacsUid,
          allocatedPacsUid: pacsUid,
          allocatedPacsId: pacsUid,
          societyUid: selectedPacs.societyUid || pacsUid,
          pacsName,
          societyName: selectedPacs.societyName || pacsName,
          organizationName: selectedPacs.organizationName || pacsName,
          pacsRegistrationNo: registrationNumber,
          registrationNumber,
          registrationNo: registrationNumber,
          pacsEmail: selectedPacs.email || "",
          pacsMobile: selectedPacs.mobile || selectedPacs.phone || "",
          state: branchState,
          status: "UNALLOCATED",
          isActive: false,
        },
        { merge: true },
      );
      await allocationBatch.commit();
      const syncWarnings = await propagateAllocationBestEffort(
        selectedPacs,
        relatedProjects(selectedPacs),
        cleared,
      );
      await saveAudit("STATE_BRANCH_PACS_PMC_ALLOCATION_REMOVED", selectedPacs);
      setSelectedPmcId("");
      showMessage(
        syncWarnings.length
          ? `Allocation removed. Restricted sync pending: ${syncWarnings.join(", ")}.`
          : "PMC allocation safely removed. PACS/project record retained.",
        syncWarnings.length ? "warning" : "success",
      );
    } catch (error) {
      console.error("Allocation removal failed:", error);
      showMessage(
        error?.code === "permission-denied"
          ? "Remove permission missing hai. Firestore rule add-on apply karein."
          : error?.message || "Allocation removal failed.",
        "error",
      );
    } finally {
      setBusy(false);
    }
  };

  const archiveFromDesk = async (pacs = selectedPacs) => {
    if (!pacs) return;
    if (
      !window.confirm(
        `${entityName(pacs)} ko Allocation Desk se delete/archive karna hai?\n\n` +
          "Yeh recoverable soft-delete hai. PACS profile, applications aur legacy records hard-delete nahi honge.",
      )
    )
      return;

    try {
      setBusy(true);
      setMessage("");
      const pacsUid = pacs.uid || pacs.id;
      const registrationNumber =
        pacs.pacsRegistrationNo ||
        pacs.registrationNumber ||
        pacs.registrationNo ||
        "";
      const pacsName = entityName(pacs);
      const batch = writeBatch(db);
      batch.set(
        doc(db, "pacs_pmc_allocations", pacsUid),
        {
          pacsUid,
          pacsId: pacsUid,
          pacsUserUid: pacsUid,
          societyUid: pacs.societyUid || pacsUid,
          pacsName,
          societyName: pacs.societyName || pacsName,
          organizationName: pacs.organizationName || pacsName,
          pacsRegistrationNo: registrationNumber,
          registrationNumber,
          registrationNo: registrationNumber,
          pacsEmail: pacs.email || "",
          pacsMobile: pacs.mobile || pacs.phone || "",
          state: pacs.state || branchState,
          district: pacsDistrict(pacs) === "District Pending" ? "" : pacsDistrict(pacs),
          deskVisibility: "HIDDEN",
          hiddenFromAllocationDesk: true,
          status: "ARCHIVED_FROM_DESK",
          allocationStatus: "ARCHIVED",
          isActive: false,
          isDeleted: true,
          archivedAt: serverTimestamp(),
          archivedByUid: auth.currentUser?.uid || "",
          archivedByRole: "STATE_BRANCH",
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      await batch.commit();
      await saveAudit("STATE_BRANCH_PACS_ARCHIVED_FROM_ALLOCATION_DESK", pacs);
      setSelectedPacsId("");
      setSelectedPmcId("");
      showMessage(
        `${pacsName} Allocation Desk se remove ho gaya. Original PACS aur project records safe hain.`,
      );
    } catch (error) {
      console.error("PACS archive failed:", error);
      showMessage(
        error?.code === "permission-denied"
          ? "Delete/archive permission missing hai. Firestore rule add-on apply karein."
          : error?.message || "PACS ko desk se remove nahi kiya ja saka.",
        "error",
      );
    } finally {
      setBusy(false);
    }
  };

  const registeredRows = rows.filter((item) => item.sourceUser);
  const allocatedCount = registeredRows.filter(
    (item) => item.allocatedPmcUid || item.allocatedPmcName,
  ).length;

  const downloadAllocationCsv = () => {
    const csvCell = (value) =>
      `"${String(value ?? "").replaceAll('"', '""')}"`;
    const csvRows = [
      [
        "Sr.",
        "PACS / Society",
        "Registration",
        "District",
        "Taluka",
        "Village",
        "Applications",
        "Current PMC",
        "Allocation Status",
      ],
      ...visibleRows.map((pacs, index) => [
        index + 1,
        entityName(pacs),
        pacs.registrationNumber ||
          pacs.registrationNo ||
          pacs.pacsRegistrationNo ||
          pacs.id,
        pacsDistrict(pacs),
        pacsTaluka(pacs),
        pacsVillage(pacs),
        pacs.projects.length,
        pacs.allocatedPmcName || "",
        pacs.allocatedPmcName ? "ALLOCATED" : "UNALLOCATED",
      ]),
    ];
    const blob = new Blob(
      [csvRows.map((row) => row.map(csvCell).join(",")).join("\n")],
      { type: "text/csv;charset=utf-8" },
    );
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `NAFED_${branchState || "State"}_${
      districtFilter === "ALL" ? "All_Districts" : districtFilter
    }_PACS_PMC_Allocation.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const resetAllocationFilters = () => {
    setSearch("");
    setFilter("ALL");
    setDistrictFilter("ALL");
  };

  return (
    <section className="allocation-desk">
      <style>{PACS_ALLOCATION_STYLE}</style>
      <div className="allocation-premium-hero">
        <div>
          <small>NAFED STATE BRANCH · CANONICAL ALLOCATION CONTROL</small>
          <h2>PACS–PMC Allocation Register</h2>
          <p>
            View and manage PACS allocations to approved Project Management
            Consultancies across districts with one live, audit-ready register.
          </p>
        </div>
        <div className="allocation-hero-actions">
          <span className="allocation-live-status">
            <b><i /> Live Data</b>
            <small>Real-time Firestore sync</small>
          </span>
          <button type="button" onClick={downloadAllocationCsv}>
            ⇩&nbsp; Export CSV
          </button>
          <button
            type="button"
            className="primary"
            onClick={() => window.location.reload()}
          >
            ↻&nbsp; Refresh Live Data
          </button>
        </div>
      </div>

      <div className="allocation-kpis">
        <div className="green">
          <i className="allocation-kpi-icon">P</i>
          <p><span>Approved State PACS</span>
          <b>{registeredRows.length}</b>
          <small>Total PACS approved for allocation</small></p>
        </div>
        <div className="blue">
          <i className="allocation-kpi-icon">A</i>
          <p><span>Allocated to PMC</span>
          <b>{allocatedCount}</b>
          <small>PACS successfully allocated</small></p>
        </div>
        <div className="orange">
          <i className="allocation-kpi-icon">◷</i>
          <p><span>Awaiting Allocation</span>
          <b>{Math.max(registeredRows.length - allocatedCount, 0)}</b>
          <small>PACS pending allocation</small></p>
        </div>
        <div className="emerald">
          <i className="allocation-kpi-icon">PMC</i>
          <p><span>Approved Active PMCs</span>
          <b>{approvedPmcs.length}</b>
          <small>PMCs available for allocation</small></p>
        </div>
        <div className="amber">
          <i className="allocation-kpi-icon">⇄</i>
          <p><span>Duplicates Merged</span>
          <b>{duplicateProfilesMerged}</b>
          <small>Duplicate records consolidated</small></p>
        </div>
      </div>

      {message && (
        <div className={`allocation-message ${messageTone}`}>{message}</div>
      )}
      <div className="allocation-management">
        <div className="allocation-management-title">
          <h3>Live Allocation Management</h3>
          <p>Search, filter and manage PACS allocations in real time.</p>
        </div>
        <div className="allocation-toolbar">
          <label className="search-field">
            <span>Search</span>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by PACS name, village or code..."
            />
          </label>
          <label>
            <span>District</span>
            <select
              value={districtFilter}
              onChange={(event) => setDistrictFilter(event.target.value)}
            >
              <option value="ALL">All Districts</option>
              {districts.map((district) => (
                <option key={district} value={district}>{district}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Allocation Status</span>
            <select
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
            >
              <option value="ALL">All Statuses</option>
              <option value="UNALLOCATED">Unallocated PACS</option>
              <option value="ALLOCATED">Allocated PACS</option>
            </select>
          </label>
          <button type="button" onClick={resetAllocationFilters}>Reset</button>
        </div>
      </div>

      <section className="allocation-district-register">
        <header>
          <div>
            <i>●</i>
            <p><b>District-wise PACS Register</b>
            <span>Select a district to view and manage only its PACS allocations.</span></p>
          </div>
          <em>{districtFilter === "ALL" ? "ALL DISTRICTS" : districtFilter}</em>
        </header>
        <div className="allocation-district-cards">
          <button
            type="button"
            className={districtFilter === "ALL" ? "active" : ""}
            onClick={() => setDistrictFilter("ALL")}
          >
            <strong><i>▦</i>All Districts{districtFilter === "ALL" && <em>✓</em>}</strong>
            <span><small>PACS Total</small><b>{rows.length}</b></span>
            <span><small>Allocated</small><b>{rows.filter((pacs) => pacs.allocatedPmcUid || pacs.allocatedPmcName).length}</b></span>
            <span><small>Pending</small><b>{rows.filter((pacs) => !pacs.allocatedPmcUid && !pacs.allocatedPmcName).length}</b></span>
          </button>
          {districtSummary.map((item) => (
            <button
              type="button"
              key={item.district}
              className={normalise(districtFilter) === normalise(item.district) ? "active" : ""}
              onClick={() => setDistrictFilter(item.district)}
            >
              <strong><i>▦</i>{item.district}{normalise(districtFilter) === normalise(item.district) && <em>✓</em>}</strong>
              <span><small>PACS Total</small><b>{item.total}</b></span>
              <span><small>Allocated</small><b>{item.allocated}</b></span>
              <span><small>Pending</small><b>{item.pending}</b></span>
            </button>
          ))}
        </div>
      </section>

      <div className="allocation-result-strip">
        <span>{branchState || "State"} jurisdiction</span>
        <b>Showing {pagedRows.length} of {visibleRows.length} PACS</b>
      </div>

      <div className="allocation-layout">
        <div className="allocation-table">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>PACS / Society</th>
                <th>Location</th>
                <th>Applications</th>
                <th>Current PMC</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((pacs, index) => (
                <tr
                  key={pacs.id}
                  className={selectedPacs?.id === pacs.id ? "selected" : ""}
                >
                  <td>
                    <span className="allocation-serial">
                      {String((page - 1) * PAGE_SIZE + index + 1).padStart(2, "0")}
                    </span>
                  </td>
                  <td>
                    <b>
                      {entityName(pacs)}
                      {(pacs.sourceUserIds?.length || 0) > 1 && (
                        <em className="duplicate-chip">
                          {pacs.sourceUserIds.length} profiles merged
                        </em>
                      )}
                    </b>
                    <small>
                      {pacs.registrationNumber ||
                        pacs.registrationNo ||
                        pacs.pacsRegistrationNo ||
                        pacs.id}
                    </small>
                  </td>
                  <td>
                    <b>{pacsDistrict(pacs)}</b>
                    <small>
                      {[pacsTaluka(pacs), pacsVillage(pacs)]
                        .filter(Boolean)
                        .join(" · ") || branchState}
                    </small>
                  </td>
                  <td>{pacs.projects.length}</td>
                  <td>
                    <span
                      className={`allocation-badge ${
                        pacs.allocatedPmcName ? "allocated" : ""
                      }`}
                    >
                      {pacs.allocatedPmcName || "UNALLOCATED"}
                    </span>
                  </td>
                  <td>
                    <div className="allocation-row-actions">
                      <button
                        type="button"
                        onClick={() => setSelectedPacsId(pacs.id)}
                      >
                        Open
                      </button>
                      <button
                        type="button"
                        className="delete"
                        disabled={busy}
                        onClick={() => archiveFromDesk(pacs)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!visibleRows.length && (
                <tr>
                  <td colSpan="6" className="allocation-empty">
                    No active PACS record matches this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="allocation-pagination">
            <span>
              Page {page} of {totalPages}
            </span>
            <div>
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((value) => Math.max(value - 1, 1))}
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() =>
                  setPage((value) => Math.min(value + 1, totalPages))
                }
              >
                Next
              </button>
            </div>
          </div>
        </div>

        <aside className="allocation-file">
          {!selectedPacs && (
            <div className="allocation-empty">Select a PACS record.</div>
          )}
          {selectedPacs && (
            <>
              <div className="allocation-file-head">
                <small>OFFICIAL PACS–PMC ALLOCATION</small>
                <h3>{entityName(selectedPacs)}</h3>
                <p>
                  {pacsDistrict(selectedPacs) || branchState} ·{" "}
                  {selectedPacs.projects.length} application(s)
                </p>
              </div>
              <div className="allocation-contact">
                <span>
                  PACS Mobile
                  <b>{selectedPacs.mobile || selectedPacs.phone || "-"}</b>
                </span>
                <span>
                  PACS Email<b>{selectedPacs.email || "-"}</b>
                </span>
              </div>
              <label className="allocation-select">
                <span>Select approved PMC</span>
                <select
                  value={selectedPmcId}
                  onChange={(event) => setSelectedPmcId(event.target.value)}
                >
                  <option value="">Choose approved PMC</option>
                  {approvedPmcs.map((pmc) => (
                    <option key={pmc.id} value={pmc.id}>
                      {pmcName(pmc)}
                    </option>
                  ))}
                </select>
              </label>
              {selectedPmcId && (
                <div className="selected-pmc-contact">
                  <b>
                    {pmcName(
                      approvedPmcs.find((item) => item.id === selectedPmcId) ||
                        {},
                    )}
                  </b>
                  <span>
                    {pmcMobile(
                      approvedPmcs.find((item) => item.id === selectedPmcId) ||
                        {},
                    ) || "Mobile pending"}
                  </span>
                  <span>
                    {pmcEmail(
                      approvedPmcs.find((item) => item.id === selectedPmcId) ||
                        {},
                    ) || "Email pending"}
                  </span>
                </div>
              )}
              <div className="allocation-actions">
                <button
                  type="button"
                  disabled={busy || !selectedPmcId}
                  onClick={saveAllocation}
                >
                  {busy
                    ? "Saving..."
                    : selectedPacs.allocatedPmcUid
                      ? "Confirm Re-allocation"
                      : "Allocate PACS to PMC"}
                </button>
                <button
                  type="button"
                  className="remove"
                  disabled={
                    busy ||
                    (!selectedPacs.allocatedPmcUid &&
                      !selectedPacs.allocatedPmcName)
                  }
                  onClick={removeAllocation}
                >
                  Remove Allocation
                </button>
                <button
                  type="button"
                  className="archive"
                  disabled={busy}
                  onClick={() => archiveFromDesk(selectedPacs)}
                >
                  Delete from Desk
                </button>
              </div>
              <p className="allocation-note">
                Official allocation real time save hota hai. Delete from Desk
                recoverable soft-delete hai; PACS profile, applications aur
                legacy records hard-delete nahi hote.
              </p>
            </>
          )}
        </aside>
      </div>
    </section>
  );
}

const PACS_ALLOCATION_STYLE = `
.allocation-desk{--navy:#0b2749;--green:#126a41;--line:#d5e0ea;--muted:#64748b;color:#142b46;font-family:Inter,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}
.allocation-kpis{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:12px;margin-bottom:15px}
.allocation-kpis>div{position:relative;overflow:hidden;min-height:94px;padding:17px 18px;border:1px solid var(--line);border-radius:13px;background:linear-gradient(145deg,#fff,#f8fbfd);box-shadow:0 7px 20px rgba(15,39,70,.055)}
.allocation-kpis>div:before{content:"";position:absolute;inset:0 auto 0 0;width:4px;background:linear-gradient(#126a41,#d49a22)}
.allocation-kpis span,.allocation-kpis b{display:block}
.allocation-kpis span{color:var(--muted);font-size:9px;font-weight:900;letter-spacing:.055em;text-transform:uppercase}
.allocation-kpis b{margin-top:9px;color:var(--navy);font-size:25px;line-height:1}
.allocation-message{display:flex;align-items:center;min-height:44px;margin-bottom:13px;padding:11px 14px;border:1px solid #8bc8a2;border-radius:10px;background:#eef9f2;color:#145f39;font-size:11px;font-weight:800}
.allocation-message.error{border-color:#efaaaa;background:#fff1f1;color:#a12626}
.allocation-message.warning{border-color:#e8c271;background:#fff8e6;color:#7c5200}
.allocation-toolbar{display:grid;grid-template-columns:minmax(260px,1fr) 230px auto;gap:10px;margin-bottom:14px;padding:12px;border:1px solid var(--line);border-radius:12px;background:#f4f7fa}
.allocation-toolbar input,.allocation-toolbar select{height:44px;padding:0 12px;border:1px solid #b8c8d8;border-radius:8px;background:#fff;color:#173451;outline:none}
.allocation-toolbar input:focus,.allocation-toolbar select:focus{border-color:#167344;box-shadow:0 0 0 3px rgba(22,115,68,.12)}
.allocation-toolbar>span{display:flex;align-items:center;gap:4px;padding:0 12px;border-radius:8px;background:#e6edf4;color:#53677b;font-size:10px;white-space:nowrap}
.allocation-layout{display:grid;grid-template-columns:minmax(0,1.35fr) minmax(350px,.65fr);gap:15px;align-items:start}
.allocation-table{overflow:auto;border:1px solid #bfcddd;border-radius:12px;background:#fff;box-shadow:0 9px 24px rgba(15,39,70,.06)}
.allocation-table table{width:100%;min-width:860px;border-collapse:separate;border-spacing:0}
.allocation-table th,.allocation-table td{padding:12px;border-bottom:1px solid #dbe4ed;text-align:left;font-size:10px;vertical-align:middle}
.allocation-table th{position:sticky;top:0;z-index:1;background:var(--navy);color:#fff;font-size:8.5px;font-weight:900;letter-spacing:.06em;text-transform:uppercase}
.allocation-table tbody tr{transition:.18s ease}
.allocation-table tbody tr:hover td{background:#f5faf7}
.allocation-table tr.selected td{background:#eaf7ef}
.allocation-table td b,.allocation-table td small{display:block}
.allocation-table td small{margin-top:5px;color:var(--muted);font-size:8.5px}
.duplicate-chip{display:inline-flex;margin-left:7px;padding:3px 6px;border-radius:999px;background:#e8f0ff;color:#285b9b;font-size:7px;font-style:normal;font-weight:900;white-space:nowrap}
.allocation-row-actions{display:flex;gap:6px}
.allocation-row-actions button,.allocation-pagination button{padding:7px 10px;border:1px solid #155d99;border-radius:7px;background:#fff;color:#155d99;font-size:8px;font-weight:900;cursor:pointer}
.allocation-row-actions button.delete{border-color:#d16464;color:#a72e2e}
.allocation-row-actions button:disabled,.allocation-pagination button:disabled{cursor:not-allowed;opacity:.42}
.allocation-badge{display:inline-block;max-width:220px;padding:6px 9px;border-radius:999px;background:#fff0dc;color:#945800;font-size:8px;font-weight:900;line-height:1.35}
.allocation-badge.allocated{background:#e3f6ea;color:#12683b}
.allocation-pagination{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:11px 12px;background:#f7fafc;color:#607286;font-size:9px}
.allocation-pagination>div{display:flex;gap:6px}
.allocation-file{position:sticky;top:14px;overflow:hidden;min-height:390px;border:1px solid #cbd8e6;border-radius:13px;background:#fff;box-shadow:0 12px 30px rgba(15,23,42,.09)}
.allocation-file-head{padding:19px;background:linear-gradient(115deg,#0b315e,#176b43);color:#fff}
.allocation-file-head small{font-size:8px;font-weight:900;letter-spacing:.075em}
.allocation-file-head h3{margin:8px 0 5px;font-size:19px;line-height:1.25}
.allocation-file-head p{margin:0;color:#d5f5e2;font-size:10px}
.allocation-contact{display:grid;grid-template-columns:1fr 1fr;padding:12px}
.allocation-contact span{min-width:0;padding:11px;border:1px solid #dbe4ed;background:#f8fafc;color:#64748b;font-size:9px}
.allocation-contact b{display:block;margin-top:6px;color:#173451;overflow-wrap:anywhere}
.allocation-select{display:block;padding:0 12px 12px}
.allocation-select>span{display:block;margin-bottom:7px;color:#37516b;font-size:10px;font-weight:900}
.allocation-select select{width:100%;height:45px;padding:0 10px;border:1px solid #aebfd0;border-radius:8px;background:#fff;color:#173451}
.selected-pmc-contact{margin:0 12px 12px;padding:12px;border-left:4px solid #16834f;background:#edf8f1}
.selected-pmc-contact b,.selected-pmc-contact span{display:block}
.selected-pmc-contact span{margin-top:4px;color:#496657;font-size:9px}
.allocation-actions{display:grid;grid-template-columns:1fr 1fr;gap:8px;padding:0 12px}
.allocation-actions button{min-height:42px;padding:8px;border:0;border-radius:8px;background:#137344;color:#fff;font-size:8.5px;font-weight:900;cursor:pointer}
.allocation-actions button.remove{background:#b16b14}
.allocation-actions button.archive{grid-column:1/-1;border:1px solid #c64848;background:#fff;color:#ad3030}
.allocation-actions button:disabled{cursor:not-allowed;opacity:.48}
.allocation-note{margin:11px 12px 15px;padding:10px;border-radius:8px;background:#f7fafc;color:#64748b;font-size:9px;line-height:1.55}
.allocation-empty{padding:38px!important;text-align:center!important;color:#64748b}
@media(max-width:1200px){.allocation-kpis{grid-template-columns:repeat(3,1fr)}.allocation-layout{grid-template-columns:1fr}.allocation-file{position:static}}
@media(max-width:760px){.allocation-kpis{grid-template-columns:repeat(2,1fr)}.allocation-toolbar{grid-template-columns:1fr}.allocation-contact,.allocation-actions{grid-template-columns:1fr}.allocation-actions button.archive{grid-column:auto}}
@media(max-width:520px){.allocation-kpis{grid-template-columns:1fr}.allocation-pagination{align-items:flex-start;flex-direction:column}}

/* Premium State Branch allocation register — additive visual upgrade. */
.allocation-desk{--navy:#102f5e;--navy2:#19477e;--green:#167044;--green2:#238653;--orange:#d99024;--soft:#f3f7fb;--line:#cedbe8;padding:0 0 18px;background:transparent}
.allocation-premium-hero{position:relative;display:flex;align-items:center;justify-content:space-between;gap:28px;overflow:hidden;margin-bottom:18px;padding:28px 30px;border:1px solid #c9d8e6;border-top:5px solid var(--green);border-radius:14px;background:linear-gradient(115deg,#fff 0%,#f8fbfd 62%,#edf7f1 100%);box-shadow:0 12px 30px rgba(16,47,94,.08)}
.allocation-premium-hero:after{content:"";position:absolute;right:-54px;bottom:-92px;width:235px;height:235px;border:36px solid rgba(22,112,68,.055);border-radius:50%;pointer-events:none}
.allocation-premium-hero>div:first-child{position:relative;z-index:1;max-width:720px}
.allocation-premium-hero>div:first-child>small{display:block;color:var(--green);font-size:10px;font-weight:950;letter-spacing:.13em;text-transform:uppercase}
.allocation-premium-hero h2{margin:8px 0 7px;color:var(--navy);font-size:30px;font-weight:850;line-height:1.1;letter-spacing:-.025em}
.allocation-premium-hero p{margin:0;color:#5e7186;font-size:12px;line-height:1.65}
.allocation-hero-actions{position:relative;z-index:1;display:flex;align-items:center;justify-content:flex-end;gap:9px;flex-wrap:wrap}
.allocation-hero-actions>button,.allocation-management button{min-height:42px;padding:0 15px;border:1px solid #aebfd1;border-radius:8px;background:#fff;color:var(--navy);font-size:10px;font-weight:900;cursor:pointer;white-space:nowrap;transition:.18s ease}
.allocation-hero-actions>button:hover,.allocation-management button:hover{border-color:var(--green);color:var(--green);transform:translateY(-1px)}
.allocation-hero-actions>button.primary{border-color:var(--green);background:var(--green);color:#fff;box-shadow:0 7px 16px rgba(22,112,68,.18)}
.allocation-live-status{min-width:112px;padding:8px 12px;border:1px solid #acd8bc;border-radius:9px;background:#effaf3;color:var(--green)}
.allocation-live-status b,.allocation-live-status small{display:block}
.allocation-live-status b{font-size:9px;text-transform:uppercase;letter-spacing:.045em}
.allocation-live-status b i{display:inline-block;width:7px;height:7px;margin-right:5px;border-radius:50%;background:#22a55b;box-shadow:0 0 0 4px rgba(34,165,91,.12)}
.allocation-live-status small{margin-top:3px;color:#668274;font-size:7.5px}
.allocation-kpis{gap:13px;margin-bottom:18px}
.allocation-kpis>div{display:flex;align-items:center;gap:13px;min-height:116px;padding:17px;border-radius:12px;background:#fff;box-shadow:0 8px 24px rgba(16,47,94,.06)}
.allocation-kpis>div:before{width:3px;background:var(--green)}
.allocation-kpis>div.blue:before{background:#2f73ba}.allocation-kpis>div.orange:before{background:#dd8b1f}.allocation-kpis>div.emerald:before{background:#15936b}.allocation-kpis>div.amber:before{background:#b98a2e}
.allocation-kpi-icon{display:grid;place-items:center;flex:0 0 43px;width:43px;height:43px;border-radius:11px;background:#e9f6ee;color:var(--green);font-size:11px;font-style:normal;font-weight:950}
.allocation-kpis .blue .allocation-kpi-icon{background:#e9f2fc;color:#2769ad}.allocation-kpis .orange .allocation-kpi-icon{background:#fff3e3;color:#bd7415}.allocation-kpis .emerald .allocation-kpi-icon{background:#e7f8f2;color:#127857}.allocation-kpis .amber .allocation-kpi-icon{background:#fff7df;color:#9e751e}
.allocation-kpis p{min-width:0;margin:0}.allocation-kpis span{font-size:8.5px}.allocation-kpis b{margin-top:6px;font-size:26px}.allocation-kpis p small{display:block;margin-top:6px;color:#7b8999;font-size:8px;line-height:1.35}
.allocation-management{display:grid;grid-template-columns:minmax(220px,.5fr) minmax(0,1.5fr);align-items:end;gap:24px;margin-bottom:15px;padding:18px 20px;border:1px solid var(--line);border-radius:13px;background:#fff;box-shadow:0 8px 23px rgba(16,47,94,.05)}
.allocation-management-title h3{margin:0;color:var(--navy);font-size:17px}.allocation-management-title p{margin:5px 0 0;color:#718195;font-size:9px;line-height:1.5}
.allocation-management .allocation-toolbar{display:grid;grid-template-columns:minmax(220px,1.35fr) minmax(145px,.65fr) minmax(155px,.7fr) auto;align-items:end;gap:9px;margin:0;padding:0;border:0;background:transparent}
.allocation-toolbar label{display:block;min-width:0}.allocation-toolbar label>span{display:block;margin:0 0 6px;color:#556a80;font-size:8px;font-weight:950;letter-spacing:.055em;text-transform:uppercase}
.allocation-toolbar input,.allocation-toolbar select{width:100%;height:42px;border-color:#c4d2df;font-size:10px}
.allocation-management .allocation-toolbar>button{height:42px;min-height:42px;padding:0 13px}
.allocation-district-register{margin-bottom:15px;padding:17px 18px;border:1px solid #b8d9c5;border-radius:13px;background:linear-gradient(135deg,#f7fcf9,#eff8f3);box-shadow:0 7px 20px rgba(18,106,65,.045)}
.allocation-district-register>header{display:flex;align-items:center;justify-content:space-between;gap:18px;margin-bottom:13px}
.allocation-district-register>header>div{display:flex;align-items:center;gap:10px}.allocation-district-register>header>div>i{display:grid;place-items:center;width:31px;height:31px;border-radius:9px;background:#dff2e7;color:var(--green);font-size:11px;font-style:normal}
.allocation-district-register header p{margin:0}.allocation-district-register header p b,.allocation-district-register header p span{display:block}.allocation-district-register header p b{color:#164b35;font-size:13px}.allocation-district-register header p span{margin-top:3px;color:#6a8275;font-size:8.5px}
.allocation-district-register>header>em{padding:7px 10px;border:1px solid #9bcaae;border-radius:999px;background:#fff;color:var(--green);font-size:7.5px;font-style:normal;font-weight:950;letter-spacing:.06em}
.allocation-district-cards{display:grid;grid-auto-flow:column;grid-auto-columns:minmax(205px,1fr);gap:9px;overflow-x:auto;padding:1px 1px 7px;scrollbar-width:thin}
.allocation-district-cards>button{min-height:105px;padding:12px;border:1px solid #c8dbcf;border-radius:10px;background:#fff;color:#26455a;text-align:left;cursor:pointer;box-shadow:0 3px 10px rgba(16,47,94,.035)}
.allocation-district-cards>button.active{border:2px solid var(--green);background:#f4fbf7;box-shadow:0 6px 16px rgba(22,112,68,.11)}
.allocation-district-cards strong{display:flex;align-items:center;gap:6px;min-height:29px;padding-bottom:9px;border-bottom:1px solid #e4ebe6;font-size:10px;line-height:1.3}.allocation-district-cards strong>i{color:var(--green);font-style:normal}.allocation-district-cards strong>em{display:grid;place-items:center;width:17px;height:17px;margin-left:auto;border-radius:50%;background:var(--green);color:#fff;font-size:8px;font-style:normal}
.allocation-district-cards>button>span{display:inline-flex;flex-direction:column;width:33.33%;padding-top:9px}.allocation-district-cards span small{color:#819087;font-size:6.5px;text-transform:uppercase}.allocation-district-cards span b{margin-top:3px;color:var(--navy);font-size:13px}
.allocation-result-strip{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:9px;padding:9px 12px;border:1px solid #d4e0eb;border-radius:9px;background:#eef4f9;color:#65798e;font-size:8.5px}.allocation-result-strip span{text-transform:uppercase;letter-spacing:.055em}.allocation-result-strip b{color:var(--navy);font-size:9px}
.allocation-layout{grid-template-columns:minmax(0,1.65fr) minmax(330px,.62fr);gap:14px}
.allocation-table{border-color:#c3d2e1;border-radius:11px;box-shadow:0 9px 27px rgba(16,47,94,.07)}
.allocation-table table{min-width:940px}.allocation-table th,.allocation-table td{padding:12px 11px}.allocation-table th{background:#e8f0f7;color:#334e68;border-bottom:1px solid #bccddd;font-size:8px}.allocation-table th:first-child{width:46px}.allocation-table tbody tr:nth-child(even) td{background:#fbfcfd}.allocation-table tbody tr:hover td{background:#eef8f2}.allocation-table tr.selected td{background:#e8f6ed!important}
.allocation-serial{display:grid!important;place-items:center;width:28px;height:28px;border-radius:50%;background:var(--navy);color:#fff!important;font-size:8px!important;font-weight:950}
.allocation-row-actions button:first-child{border-color:var(--navy);background:var(--navy);color:#fff}.allocation-row-actions button:first-child:hover{background:var(--navy2)}
.allocation-file{top:12px;border-color:#bfcfe0;border-radius:11px}.allocation-file-head{background:linear-gradient(115deg,#102f5e,#176b43)}
@media(max-width:1250px){.allocation-management{grid-template-columns:1fr}.allocation-management .allocation-toolbar{grid-template-columns:1.4fr 1fr 1fr auto}.allocation-layout{grid-template-columns:1fr}.allocation-file{position:static}.allocation-kpis{grid-template-columns:repeat(3,1fr)}}
@media(max-width:820px){.allocation-premium-hero{align-items:flex-start;flex-direction:column}.allocation-hero-actions{justify-content:flex-start}.allocation-management .allocation-toolbar{grid-template-columns:1fr 1fr}.allocation-kpis{grid-template-columns:repeat(2,1fr)}}
@media(max-width:560px){.allocation-premium-hero{padding:22px 18px}.allocation-premium-hero h2{font-size:24px}.allocation-hero-actions{width:100%;align-items:stretch;flex-direction:column}.allocation-hero-actions>button{width:100%}.allocation-management .allocation-toolbar,.allocation-kpis{grid-template-columns:1fr}.allocation-district-register>header{align-items:flex-start;flex-direction:column}.allocation-result-strip{align-items:flex-start;flex-direction:column}}
`;

const STATE_PACS_STYLE = `
.state-pacs{color:#142b46}.state-pacs-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:14px}.state-pacs-kpis>div{padding:14px;border:1px solid #d5e0eb;border-radius:11px;background:#fff}.state-pacs-kpis span,.state-pacs-kpis b{display:block}.state-pacs-kpis span{color:#64748b;font-size:10px}.state-pacs-kpis b{margin-top:5px;font-size:21px}.pmc-selector-head{padding:14px 16px 8px;border:1px solid #d5e0eb;border-bottom:0;border-radius:12px 12px 0 0;background:#f7fafc}.pmc-selector-head b,.pmc-selector-head span{display:block}.pmc-selector-head span{margin-top:4px;color:#64748b;font-size:10px}.pmc-selector{display:grid;grid-template-columns:repeat(3,1fr);gap:9px;margin-bottom:14px;padding:10px 14px 14px;border:1px solid #d5e0eb;border-top:0;border-radius:0 0 12px 12px;background:#f7fafc}.pmc-selector button{padding:13px;border:1px solid #ccd8e5;border-radius:10px;background:#fff;color:#203b57;text-align:left}.pmc-selector button.active{border:2px solid #087746;background:#edf9f2}.pmc-selector span,.pmc-selector b,.pmc-selector small{display:block}.pmc-selector span{min-height:30px;font-weight:900}.pmc-selector b{margin-top:7px;color:#075b3d;font-size:11px}.pmc-selector small{margin-top:4px;color:#718096}.no-pmc{grid-column:1/-1;padding:22px;text-align:center;color:#718096}.select-pmc-message{padding:34px;border:1px dashed #aebdcc;border-radius:12px;background:#fff;text-align:center}.select-pmc-message b,.select-pmc-message span{display:block}.select-pmc-message b{font-size:18px}.select-pmc-message span{margin-top:6px;color:#64748b}.selected-pmc-head{display:flex;align-items:center;justify-content:space-between;gap:16px;margin-bottom:10px;padding:15px;border:1px solid #b8d8c5;border-radius:12px;background:#eff9f3}.selected-pmc-head small{font-size:8px;font-weight:900}.selected-pmc-head h3{margin:4px 0;font-size:18px}.selected-pmc-head p{margin:0;color:#527064;font-size:10px}.selected-pmc-head>div:last-child{display:flex;gap:7px}.selected-pmc-head button{padding:9px 11px;border:1px solid #075b3d;border-radius:8px;background:#075b3d;color:#fff;font-size:10px;font-weight:900}.state-pacs-filters{display:grid;grid-template-columns:1.5fr 1fr 1fr auto;gap:9px;margin-bottom:13px;padding:12px;border:1px solid #d5e0eb;border-radius:11px;background:#fff}.state-pacs-filters input,.state-pacs-filters select{width:100%;height:42px;padding:0 10px;border:1px solid #cbd7e5;border-radius:8px;background:#fff;color:#183650}.filter-result{display:flex;align-items:center;padding:0 10px;border-radius:8px;background:#edf3f8;color:#526579;font-size:10px}.state-pacs-table{overflow:auto;border:1px solid #cbd7e5;border-radius:11px;background:#fff}.state-pacs-table table{width:100%;min-width:900px;border-collapse:collapse}.state-pacs-table th,.state-pacs-table td{padding:11px;border-bottom:1px solid #dce5ee;text-align:left;font-size:11px}.state-pacs-table th{background:#edf3f8;color:#526579;font-size:9px;text-transform:uppercase}.state-pacs-table td small{display:block;margin-top:4px;color:#718096;font-size:9px}.state-stage{display:inline-block;padding:6px 8px;border-radius:20px;background:#e9f2ff;color:#165da4;font-size:8px;font-weight:900}.no-record{padding:30px!important;text-align:center!important;color:#718096}@media(max-width:900px){.state-pacs-kpis{grid-template-columns:repeat(2,1fr)}.pmc-selector{grid-template-columns:repeat(2,1fr)}.state-pacs-filters{grid-template-columns:1fr 1fr}.selected-pmc-head{align-items:flex-start;flex-direction:column}}@media(max-width:600px){.state-pacs-kpis,.state-pacs-filters,.pmc-selector{grid-template-columns:1fr}.selected-pmc-head>div:last-child{width:100%;flex-direction:column}.selected-pmc-head button{width:100%}}
`;

const STATE_REGISTRAR_APPROVAL_STYLE = `
.sr-approval{color:#132a44}.sr-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:16px}.sr-kpis>div{padding:17px 18px;border:1px solid #cbd8e6;border-top:4px solid #1b7a4a;border-radius:12px;background:#fff;box-shadow:0 8px 22px rgba(15,23,42,.06)}.sr-kpis span,.sr-kpis b{display:block}.sr-kpis span{color:#64748b;font-size:10px;font-weight:850;text-transform:uppercase;letter-spacing:.06em}.sr-kpis b{margin-top:7px;color:#0b2b50;font-size:24px;font-weight:950}.sr-message{margin-bottom:14px;padding:12px 14px;border:1px solid #9bd5b4;border-radius:9px;background:#ecf9f1;color:#12613b;font-size:12px;font-weight:850}.sr-filters{display:grid;grid-template-columns:1fr 260px;gap:10px;margin-bottom:14px;padding:12px;border:1px solid #cbd8e6;border-radius:12px;background:#f5f8fb}.sr-filters input,.sr-filters select{height:44px;width:100%;padding:0 12px;border:1px solid #b9c9d9;border-radius:8px;background:#fff;color:#183650;font:inherit;outline:none}.sr-filters input:focus,.sr-filters select:focus{border-color:#176b43;box-shadow:0 0 0 3px rgba(23,107,67,.12)}.sr-layout{display:grid;grid-template-columns:minmax(0,1.25fr) minmax(340px,.75fr);gap:14px;align-items:start}.sr-table-wrap{overflow:auto;border:1.5px solid #1f2937;border-radius:11px;background:#fff}.sr-table-wrap table{width:100%;min-width:720px;border-collapse:collapse}.sr-table-wrap th,.sr-table-wrap td{padding:12px;border:1px solid #1f2937;text-align:left;font-size:11px;vertical-align:middle}.sr-table-wrap th{background:#dfe8f1;color:#102b4d;font-size:9px;font-weight:950;text-transform:uppercase;letter-spacing:.06em}.sr-table-wrap tr.selected td{background:#edf7f1}.sr-table-wrap td b,.sr-table-wrap td small{display:block}.sr-table-wrap td small{margin-top:4px;color:#64748b;font-size:9px}.sr-status{display:inline-block;padding:6px 8px;border:1px solid #e5aa32;border-radius:999px;background:#fff5d9;color:#875500;font-size:8px;font-weight:950;white-space:nowrap}.sr-status.approved{border-color:#58a978;background:#e9f7ef;color:#17663b}.sr-status.rejected{border-color:#dc7777;background:#fff0f0;color:#a72828}.sr-open{padding:8px 10px;border:1px solid #164f91;border-radius:7px;background:#fff;color:#164f91;font-size:9px;font-weight:950;cursor:pointer}.sr-file{min-height:320px;border:1px solid #cbd8e6;border-radius:12px;background:#fff;box-shadow:0 9px 24px rgba(15,23,42,.07);overflow:hidden}.sr-file-head{padding:16px 17px;background:linear-gradient(110deg,#0b2f5b,#176b43);color:#fff}.sr-file-head small{font-size:8px;font-weight:950;letter-spacing:.08em}.sr-file-head h3{margin:7px 0 5px;font-size:18px}.sr-file-head span{color:#d8f3e4;font-size:9px;font-weight:800}.sr-details{display:grid;grid-template-columns:1fr 1fr;gap:0;padding:10px}.sr-details span{min-height:65px;padding:10px;border:1px solid #dbe4ed;background:#f8fafc;color:#64748b;font-size:9px}.sr-details b{display:block;margin-top:7px;color:#172f4b;font-size:11px;overflow-wrap:anywhere}.sr-remarks{margin:0 10px 10px;padding:10px;border-left:4px solid #e5a21b;background:#fff8e8;font-size:10px}.sr-remarks p{margin:5px 0 0;color:#6b4b0b}.sr-confirm{display:flex;gap:8px;align-items:flex-start;margin:10px;padding:11px;border:1px solid #cbd8e6;border-radius:8px;background:#f5f8fb;color:#334155;font-size:9px;font-weight:750;line-height:1.5}.sr-confirm input{margin-top:2px}.sr-actions{display:grid;grid-template-columns:1.35fr .75fr .65fr;gap:7px;padding:0 10px 12px}.sr-actions button{min-height:39px;border:0;border-radius:8px;background:#167344;color:#fff;font-size:9px;font-weight:950;cursor:pointer}.sr-actions button.query{background:#ba7300}.sr-actions button.reject{background:#b62f35}.sr-actions button:disabled{cursor:not-allowed;opacity:.52}.sr-empty{padding:35px!important;text-align:center!important;color:#718096;font-size:11px}@media(max-width:1050px){.sr-kpis{grid-template-columns:repeat(2,1fr)}.sr-layout{grid-template-columns:1fr}}@media(max-width:650px){.sr-kpis,.sr-filters,.sr-details,.sr-actions{grid-template-columns:1fr}}
`;

function StatCard({ label, value, sub, color, index }) {
  return (
    <div className="stat">
      <div className="stat-icon" style={{ background: color }}>
        {String(index + 1).padStart(2, "0")}
      </div>
      <div>
        <label>{label}</label>
        <b>{value}</b>
        <small>{sub}</small>
      </div>
    </div>
  );
}

function PortalIcon({ name, size = 20 }) {
  const paths = {
    home: (
      <>
        <path d="M3 11.5 12 4l9 7.5" />
        <path d="M5.5 10.5V21h13V10.5M9.5 21v-6h5v6" />
      </>
    ),
    projects: (
      <>
        <path d="M7 3h8l4 4v14H7z" />
        <path d="M15 3v5h5M10 12h6M10 16h6" />
      </>
    ),
    pacs: (
      <>
        <path d="M4 21V9l8-5 8 5v12" />
        <path d="M8 21v-5h8v5M8 10h.01M12 10h.01M16 10h.01M8 13h.01M12 13h.01M16 13h.01" />
      </>
    ),
    pmcs: (
      <>
        <circle cx="9" cy="8" r="3" />
        <path d="M3.5 20c.5-4 2.3-6 5.5-6s5 2 5.5 6M16 8h5M18.5 5.5v5" />
      </>
    ),
    approvals: (
      <>
        <path d="M12 3 20 6v5c0 5-3.2 8.3-8 10-4.8-1.7-8-5-8-10V6z" />
        <path d="m8.5 12 2.3 2.3 4.8-5" />
      </>
    ),
    monitoring: (
      <>
        <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
      </>
    ),
    reports: (
      <>
        <path d="M5 3h14v18H5zM9 8h6M9 12h6M9 16h4" />
      </>
    ),
    bell: (
      <>
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
        <path d="M10 21h4" />
      </>
    ),
    allocation: (
      <>
        <path d="M4 7h14M15 4l3 3-3 3M20 17H6M9 14l-3 3 3 3" />
      </>
    ),
    dpr: (
      <>
        <path d="M6 3h9l4 4v14H6zM15 3v5h5M9 12h7M9 16h5" />
      </>
    ),
    tender: (
      <>
        <path d="m14 5 5 5M12 7l5 5M4 20l9-9M3 21h8" />
      </>
    ),
    construction: (
      <>
        <path d="M4 20h16M6 20v-7h12v7M8 13l2-6h4l2 6M5 13h14" />
      </>
    ),
    bills: (
      <>
        <path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" />
        <path d="M9 8h6M9 12h6M9 16h4" />
      </>
    ),
    expand: (
      <>
        <path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" />
      </>
    ),
    logout: (
      <>
        <path d="M10 4H5v16h5M14 8l4 4-4 4M18 12H9" />
      </>
    ),
  };
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name] || paths.projects}
    </svg>
  );
}

function StorageHeroGraphic() {
  return (
    <svg
      viewBox="0 0 760 205"
      role="img"
      aria-label="Grain storage infrastructure"
    >
      <defs>
        <linearGradient id="sky" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#c9deec" stopOpacity=".9" />
          <stop offset="1" stopColor="#e9f2f5" stopOpacity=".35" />
        </linearGradient>
        <linearGradient id="steel" x1="0" x2="1">
          <stop offset="0" stopColor="#8aa0ad" />
          <stop offset=".5" stopColor="#e6eef1" />
          <stop offset="1" stopColor="#6f8796" />
        </linearGradient>
      </defs>
      <path
        d="M0 130 90 105l70 10 90-25 85 17 90-36 93 17 85-25 157 41v101H0z"
        fill="url(#sky)"
        opacity=".45"
      />
      <path d="M0 167h760v38H0z" fill="#55755e" opacity=".58" />
      <g opacity=".94">
        <path d="M214 110h155v67H214z" fill="#d9e1df" />
        <path d="m195 112 96-45 98 45z" fill="#2e6858" />
        <path d="M258 130h60v47h-60z" fill="#8da69c" />
        {[420, 482, 544, 606].map((x) => (
          <g key={x}>
            <ellipse cx={x} cy="66" rx="27" ry="10" fill="#d8e2e7" />
            <path d={`M${x - 27} 66h54v100h-54z`} fill="url(#steel)" />
            <ellipse cx={x} cy="166" rx="27" ry="9" fill="#637987" />
            <path d={`m${x - 29} 66 29-29 29 29`} fill="#8fa4af" />
            <path
              d={`M${x - 14} 72v86M${x} 72v86M${x + 14} 72v86`}
              stroke="#718795"
              strokeWidth="1"
              opacity=".65"
            />
          </g>
        ))}
        <path
          d="M386 46h260M405 46V26h221v20M420 26V12h185v14"
          fill="none"
          stroke="#677f8d"
          strokeWidth="5"
        />
        <path d="M685 74h48v103h-48z" fill="#cad4d8" />
        <path d="m680 74 29-27 29 27z" fill="#315d67" />
      </g>
    </svg>
  );
}

const GUJARAT_DISTRICT_POINTS = [
  ["Kutch", 112, 83, ["kachchh", "kutch"]],
  ["Vav-Tharad", 306, 38, ["vavtharad", "vav tharadh", "tharad"]],
  ["Banaskantha", 342, 54, ["banas kantha", "banaskantha"]],
  ["Patan", 307, 79, []],
  ["Mehsana", 344, 96, ["mahesana", "mehsana"]],
  ["Sabarkantha", 397, 91, ["sabar kantha", "sabarkantha"]],
  ["Aravalli", 420, 114, ["aravali", "aravalli"]],
  ["Gandhinagar", 365, 121, []],
  ["Ahmedabad", 339, 150, ["ahmadabad", "ahmedabad"]],
  ["Surendranagar", 288, 155, ["surendra nagar", "surendranagar"]],
  ["Morbi", 239, 119, ["morvi", "morbi"]],
  ["Jamnagar", 188, 154, []],
  ["Devbhumi Dwarka", 142, 167, ["dev bhoomi dwarka", "dwarka"]],
  ["Rajkot", 229, 180, []],
  ["Porbandar", 169, 204, []],
  ["Junagadh", 215, 226, ["junagarh", "junagadh"]],
  ["Gir Somnath", 230, 260, ["girsomnath", "somnath"]],
  ["Amreli", 278, 228, []],
  ["Bhavnagar", 321, 215, []],
  ["Botad", 304, 190, []],
  ["Kheda", 380, 151, []],
  ["Anand", 392, 172, []],
  ["Mahisagar", 432, 143, ["mahi sagar", "mahisagar"]],
  ["Panchmahal", 445, 170, ["panch mahals", "panchmahal"]],
  ["Dahod", 476, 166, []],
  ["Vadodara", 423, 198, ["baroda", "vadodara"]],
  ["Chhota Udepur", 464, 202, ["chhotaudepur", "chhota udaipur"]],
  ["Bharuch", 407, 226, []],
  ["Narmada", 450, 226, []],
  ["Surat", 408, 257, []],
  ["Tapi", 447, 259, []],
  ["Navsari", 417, 284, []],
  ["Dang", 452, 290, ["the dangs", "dangs"]],
  ["Valsad", 432, 315, []],
];

const mapDistrictKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/district/g, "")
    .replace(/[^a-z0-9]+/g, "")
    .trim();

const districtProjectCountTone = (count) => {
  if (count > 5) return { fill: "#245fca", soft: "#e9f1ff", label: ">5" };
  if (count > 2) return { fill: "#149ca8", soft: "#e3f7f5", label: "3–5" };
  if (count > 0) return { fill: "#4fb989", soft: "#e7f7ef", label: "1–2" };
  return { fill: "#cbd8e2", soft: "#f1f5f8", label: "0" };
};

function GujaratOperationsMap({ projectRows = [] }) {
  const [selectedName, setSelectedName] = useState("");

  const districtData = useMemo(() => {
    const aliases = new Map();
    GUJARAT_DISTRICT_POINTS.forEach(([name, , , extraAliases]) => {
      [name, ...extraAliases].forEach((alias) =>
        aliases.set(mapDistrictKey(alias), name),
      );
    });

    const counts = new Map();
    projectRows.forEach((project) => {
      const rawDistrict =
        project.district ||
        project.districtName ||
        project.projectDistrict ||
        project.siteDistrict ||
        project.pacsDistrict ||
        project.location?.district ||
        "";
      const officialName = aliases.get(mapDistrictKey(rawDistrict));
      if (officialName) {
        counts.set(officialName, (counts.get(officialName) || 0) + 1);
      }
    });

    return GUJARAT_DISTRICT_POINTS.map(([name, x, y]) => ({
      name,
      x,
      y,
      count: counts.get(name) || 0,
    })).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [projectRows]);

  const activeDistricts = districtData.filter((district) => district.count > 0);
  const selectedDistrict =
    districtData.find((district) => district.name === selectedName) ||
    activeDistricts[0] ||
    districtData[0];
  const mappedProjects = districtData.reduce(
    (total, district) => total + district.count,
    0,
  );
  const unmappedProjects = Math.max(0, projectRows.length - mappedProjects);

  return (
    <div className="command-map-graphic">
      <div className="map-live-strip">
        <div>
          <span>Active districts</span>
          <b>{activeDistricts.length}</b>
        </div>
        <div>
          <span>Mapped projects</span>
          <b>{mappedProjects}</b>
        </div>
        <div>
          <span>Highest activity</span>
          <b>{activeDistricts[0]?.name || "No live projects"}</b>
        </div>
      </div>

      <div className="map-workspace">
        <div className="gujarat-map-canvas">
          <svg
            viewBox="0 0 535 350"
            role="img"
            aria-label={`${mappedProjects} live projects across ${activeDistricts.length} Gujarat districts`}
          >
            <defs>
              <linearGradient id="gujaratLandV8" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0" stopColor="#f4faf8" />
                <stop offset=".58" stopColor="#dcefe9" />
                <stop offset="1" stopColor="#c9e4dd" />
              </linearGradient>
              <pattern
                id="mapGridV8"
                width="18"
                height="18"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M18 0H0V18"
                  fill="none"
                  stroke="#a9c8d2"
                  strokeWidth=".55"
                  opacity=".28"
                />
              </pattern>
              <filter
                id="gujaratShadowV8"
                x="-20%"
                y="-20%"
                width="140%"
                height="150%"
              >
                <feDropShadow
                  dx="0"
                  dy="8"
                  stdDeviation="8"
                  floodColor="#123d62"
                  floodOpacity=".18"
                />
              </filter>
              <filter
                id="districtGlowV8"
                x="-80%"
                y="-80%"
                width="260%"
                height="260%"
              >
                <feDropShadow
                  dx="0"
                  dy="2"
                  stdDeviation="3"
                  floodColor="#143f69"
                  floodOpacity=".28"
                />
              </filter>
            </defs>

            <rect
              x="1"
              y="1"
              width="533"
              height="348"
              rx="16"
              fill="url(#mapGridV8)"
            />
            <g filter="url(#gujaratShadowV8)">
              <path
                className="gujarat-land"
                d="M42 68 83 49 128 33 172 40 205 37 227 55 222 77 245 94 282 101 301 83 306 57 327 36 364 34 393 48 415 69 425 94 454 109 474 133 503 155 488 179 467 194 458 218 468 239 451 258 461 279 450 302 437 328 421 337 409 316 406 291 394 267 399 245 382 224 361 228 344 216 326 224 303 245 278 260 250 270 219 263 190 247 162 226 140 205 110 203 78 190 55 171 66 151 103 143 136 150 159 139 187 131 213 112 198 102 166 107 140 119 104 124 78 115 55 115 61 95Z"
                fill="url(#gujaratLandV8)"
                stroke="#8fb9c7"
                strokeWidth="2.2"
              />
              <path
                d="M93 126c28-2 60-13 101-26 16-5 30-4 46 1-26 19-57 31-83 39l-20 10-25-7Z"
                fill="#f8fbfd"
                stroke="#a9cad4"
                strokeWidth="1.4"
              />
              <path
                d="M326 187c18 0 38 9 55 34l-18 7-19-11-17-11Z"
                fill="#f8fbfd"
                stroke="#a9cad4"
                strokeWidth="1.35"
              />
            </g>

            <g className="map-boundaries" aria-hidden="true">
              <path d="M224 77 205 112 239 119 281 102 289 153 339 150 365 121 344 96 307 79M289 153l-60 27 76 10 34-40M229 180l-14 46 63 2 27-38M278 228l25 17M339 150l43 74M365 121l60-27M382 224l26 33 43 1M408 257l9 27 34 18" />
              <path d="M140 150 188 154l41 26M110 203l59 1 46 22M169 204l21 43M425 94l-5 49 45 51M420 143l-28 29 31 26M423 198l41 4" />
            </g>

            <g className="map-north" aria-hidden="true">
              <path d="M504 65v28" />
              <path d="m504 58-7 13h14Z" fill="#0b4c78" stroke="none" />
              <text x="504" y="108" textAnchor="middle">
                N
              </text>
            </g>

            {[...districtData]
              .sort((a, b) => a.count - b.count)
              .map((district) => {
                const tone = districtProjectCountTone(district.count);
                const isSelected = selectedDistrict?.name === district.name;
                const radius = district.count
                  ? 7 + Math.min(district.count, 8) * 0.75
                  : 2.8;
                return (
                  <g
                    className={`district-point ${district.count ? "has-projects" : ""} ${isSelected ? "selected" : ""}`}
                    key={district.name}
                    role="button"
                    tabIndex="0"
                    aria-label={`${district.name}: ${district.count} projects`}
                    onClick={() => setSelectedName(district.name)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedName(district.name);
                      }
                    }}
                  >
                    <title>{`${district.name}: ${district.count} project${district.count === 1 ? "" : "s"}`}</title>
                    {isSelected && district.count > 0 && (
                      <circle
                        cx={district.x}
                        cy={district.y}
                        r={radius + 7}
                        fill="none"
                        stroke={tone.fill}
                        strokeWidth="1.5"
                        opacity=".28"
                      />
                    )}
                    <circle
                      cx={district.x}
                      cy={district.y}
                      r={radius}
                      fill={tone.fill}
                      stroke="#fff"
                      strokeWidth={district.count ? 2.2 : 1}
                      filter={
                        district.count ? "url(#districtGlowV8)" : undefined
                      }
                    />
                    {district.count > 0 && (
                      <text
                        className="district-count"
                        x={district.x}
                        y={district.y + 3.2}
                        textAnchor="middle"
                      >
                        {district.count}
                      </text>
                    )}
                  </g>
                );
              })}

            {selectedDistrict?.count > 0 &&
              (() => {
                const calloutX =
                  selectedDistrict.x > 370
                    ? selectedDistrict.x - 122
                    : selectedDistrict.x + 16;
                const calloutY = Math.max(
                  18,
                  Math.min(304, selectedDistrict.y - 18),
                );
                const lineEndX =
                  selectedDistrict.x > 370 ? calloutX + 112 : calloutX;
                return (
                  <g className="selected-map-callout" aria-hidden="true">
                    <path
                      d={`M${selectedDistrict.x} ${selectedDistrict.y} L${lineEndX} ${calloutY + 15}`}
                    />
                    <rect
                      x={calloutX}
                      y={calloutY}
                      width="112"
                      height="30"
                      rx="7"
                    />
                    <text x={calloutX + 9} y={calloutY + 12}>
                      {selectedDistrict.name}
                    </text>
                    <text className="value" x={calloutX + 9} y={calloutY + 23}>
                      {selectedDistrict.count} LIVE PROJECT
                      {selectedDistrict.count === 1 ? "" : "S"}
                    </text>
                  </g>
                );
              })()}

            <text className="map-water-label" x="116" y="178">
              Arabian Sea
            </text>
          </svg>

          <div className="map-legend" aria-label="Project count legend">
            <span>
              <i className="none" />
              No projects
            </span>
            <span>
              <i className="low" />
              1–2 projects
            </span>
            <span>
              <i className="medium" />
              3–5 projects
            </span>
            <span>
              <i className="high" />
              More than 5
            </span>
          </div>
        </div>

        <aside className="map-district-panel">
          <div className="selected-district-card">
            <span>Selected district</span>
            <strong>{selectedDistrict?.name || "Gujarat"}</strong>
            <div>
              <b>{selectedDistrict?.count || 0}</b>
              <small>
                Live project{selectedDistrict?.count === 1 ? "" : "s"}
              </small>
            </div>
          </div>

          <div className="map-ranking-head">
            <b>District activity</b>
            <span>Live</span>
          </div>
          <div className="map-ranking-list">
            {(activeDistricts.length
              ? activeDistricts
              : districtData.slice(0, 4)
            )
              .slice(0, 5)
              .map((district, index) => {
                const maxCount = Math.max(activeDistricts[0]?.count || 1, 1);
                return (
                  <button
                    type="button"
                    className={
                      selectedDistrict?.name === district.name ? "active" : ""
                    }
                    key={district.name}
                    onClick={() => setSelectedName(district.name)}
                  >
                    <em>{String(index + 1).padStart(2, "0")}</em>
                    <span>
                      <b>{district.name}</b>
                      <i>
                        <u
                          style={{
                            width: `${(district.count / maxCount) * 100}%`,
                          }}
                        />
                      </i>
                    </span>
                    <strong>{district.count}</strong>
                  </button>
                );
              })}
          </div>

          {unmappedProjects > 0 && (
            <p className="map-data-note">
              {unmappedProjects} project{unmappedProjects === 1 ? "" : "s"} need
              a valid district name.
            </p>
          )}
        </aside>
      </div>
    </div>
  );
}

const REGISTRATION_REPORT_STYLE = `
.registration-mis .reg-table table{min-width:1180px}
.registration-mis .reg-toolbar.with-location-filters{grid-template-columns:auto minmax(230px,1fr) repeat(3,minmax(150px,185px))}
.registration-mis .reg-badge.allocated{background:#e6efff;color:#174f9d}
.registration-mis .reg-message{margin:0 0 12px;padding:11px 13px;border:1px solid #8bc8a2;border-radius:9px;background:#eef9f2;color:#145f39;font-size:10px;font-weight:850}
.registration-mis .reg-message.error{border-color:#efaaaa;background:#fff1f1;color:#a12626}
.registration-mis .duplicate-action{display:grid;gap:5px;min-width:125px}
.registration-mis .duplicate-action button{min-height:32px;padding:6px 9px;border:1px solid #bb3535;border-radius:7px;background:#fff4f4;color:#9a2525;font-size:8px;font-weight:950;cursor:pointer}
.registration-mis .duplicate-action button:disabled{cursor:not-allowed;opacity:.5}
.registration-mis .duplicate-action small{margin:0;color:#6b7f93;font-size:7.5px}
.registration-mis .canonical-record{display:inline-flex;padding:5px 7px;border-radius:999px;background:#edf5ef;color:#286542;font-size:7.5px;font-weight:900}
.reg-export-actions{grid-column:1/-1;display:flex;align-items:center;gap:10px;padding-top:11px;border-top:1px solid #cbd8e6}
.reg-export-actions>div{min-width:0;margin-right:auto}.reg-export-actions b,.reg-export-actions span{display:block}.reg-export-actions b{color:#173451;font-size:10px}.reg-export-actions span{margin-top:3px;color:#64748b;font-size:8px;font-weight:800}
.reg-export-actions button{min-height:38px;padding:9px 14px;border:0;border-radius:8px;color:#fff;font-size:9px;font-weight:950;cursor:pointer;box-shadow:0 5px 14px rgba(15,45,76,.12);transition:.18s ease}
.reg-export-actions button.excel{background:#14743f}.reg-export-actions button.pdf{background:#b42318}.reg-export-actions button:hover{filter:brightness(.96);transform:translateY(-1px)}
.registration-mis .reg-table tfoot td{background:#e1f3e8;color:#0b5733;font-weight:950}.registration-mis .reg-table tfoot td:nth-child(2){font-size:12px;white-space:nowrap}
@media(max-width:1100px){.registration-mis .reg-toolbar.with-location-filters{grid-template-columns:auto 1fr 180px}.registration-mis .reg-toolbar.with-location-filters>select:nth-of-type(2),.registration-mis .reg-toolbar.with-location-filters>select:nth-of-type(3){grid-column:span 1}}
@media(max-width:760px){.registration-mis .reg-toolbar.with-location-filters{grid-template-columns:1fr}.reg-export-actions{align-items:stretch;flex-wrap:wrap}.reg-export-actions>div{width:100%;margin-right:0}.reg-export-actions button{flex:1}}
`;

const reportToDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value?.toDate === "function") {
    const date = value.toDate();
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
  }
  if (Number.isFinite(value?.seconds)) {
    const date = new Date(value.seconds * 1000);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const reportDateText = (value) => {
  const date = reportToDate(value);
  return date ? date.toLocaleDateString("en-IN") : "";
};

const reportEscapeHtml = (value) =>
  String(value ?? "").replace(
    /[&<>"']/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;",
      })[character],
  );

const reportFileSlug = (value) =>
  String(value || "PACS")
    .trim()
    .replace(/[^a-zA-Z0-9\u0A80-\u0AFF]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 70) || "PACS";

const reportLocationKey = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLocaleLowerCase("en-IN");

const reportLocationLabel = (value, fallback = "-") => {
  const cleaned = String(value || "")
    .trim()
    .replace(/\s+/g, " ");
  if (!cleaned || cleaned === "-") return fallback;
  return cleaned
    .toLocaleLowerCase("en-IN")
    .replace(/(^|[\s\-(/])([a-z])/g, (_, boundary, letter) =>
      `${boundary}${letter.toLocaleUpperCase("en-IN")}`,
    );
};

const reportExportValue = (
  item = {},
  project = {},
  allocation = {},
  fields,
) => {
  const sources = [
    project,
    project.projectDetails,
    project.applicationData,
    project.formData,
    project.godownDetails,
    project.technicalDetails,
    project.landDetails,
    project.projectDetails?.landDetails,
    project.applicationData?.landDetails,
    project.formData?.landDetails,
    item,
    item.profile,
    item.pacsDetails,
    item.societyDetails,
    item.landDetails,
    allocation,
  ].filter(Boolean);
  for (const source of sources) {
    const value = firstRecordValue(source, fields);
    if (value) return value;
  }
  return "";
};

const reportFindLinkedProject = (item = {}, projects = []) => {
  const itemKeys = new Set(
    [item.id, item.uid, ...allocationPacsKeys(item), ...pacsDedupeKeys(item)]
      .map(normalise)
      .filter(Boolean),
  );
  const itemRegistration = normalise(
    firstRecordValue(item, [
      "pacsRegistrationNo",
      "pacsRegistrationNumber",
      "registrationNumber",
      "registrationNo",
    ]),
  );
  const itemEmail = normalise(firstRecordValue(item, ["pacsEmail", "email"]));
  const itemName = normalise(entityName(item));
  const itemDistrict = normalise(
    firstRecordValue(item, ["district", "districtName"]),
  );

  return (
    projects
      .filter((project) => !isDeletedProject(project))
      .filter((project) => {
        const projectKeys = [
          project.id,
          project.uid,
          ...allocationPacsKeys(project),
          ...pacsDedupeKeys(project),
        ]
          .map(normalise)
          .filter(Boolean);
        const keyMatch = projectKeys.some((key) => itemKeys.has(key));
        const registrationMatch =
          itemRegistration &&
          itemRegistration ===
            normalise(
              firstRecordValue(project, [
                "pacsRegistrationNo",
                "pacsRegistrationNumber",
                "registrationNumber",
                "registrationNo",
              ]),
            );
        const emailMatch =
          itemEmail &&
          itemEmail ===
            normalise(firstRecordValue(project, ["pacsEmail", "email"]));
        const projectDistrict = normalise(
          firstRecordValue(project, ["district", "districtName"]),
        );
        const nameLocationMatch =
          itemName &&
          itemName === normalise(projectName(project)) &&
          (!itemDistrict ||
            !projectDistrict ||
            itemDistrict === projectDistrict);
        return keyMatch || registrationMatch || emailMatch || nameLocationMatch;
      })
      .sort(
        (a, b) =>
          toMillis(b.updatedAt || b.submittedAt || b.createdAt) -
          toMillis(a.updatedAt || a.submittedAt || a.createdAt),
      )[0] || null
  );
};

const reportFindLinkedAllocation = (item = {}, allocations = []) => {
  const itemKeys = new Set(
    [item.id, item.uid, ...allocationPacsKeys(item), ...pacsDedupeKeys(item)]
      .map(normalise)
      .filter(Boolean),
  );
  const matches = allocations
    .filter((allocation) => !isDeletedProject(allocation))
    .filter((allocation) =>
      [
        allocation.id,
        allocation.uid,
        ...allocationPacsKeys(allocation),
        ...pacsDedupeKeys(allocation),
      ]
        .map(normalise)
        .filter(Boolean)
        .some((key) => itemKeys.has(key)),
    )
    .sort((a, b) => {
      const activeDifference =
        Number(isActivePmcAllocation(b)) - Number(isActivePmcAllocation(a));
      if (activeDifference) return activeDifference;
      return (
        toMillis(b.updatedAt || b.allocatedAt || b.createdAt) -
        toMillis(a.updatedAt || a.allocatedAt || a.createdAt)
      );
    });
  return matches[0] || null;
};

const DEFAULT_HIRING_ASSURANCE_BODY =
  "With reference to the proposed scientific storage godown of the above PACS, the State Branch records its hiring assurance for the eligible completed capacity. The facility shall be considered for hiring after completion in accordance with the approved DPR, applicable technical specifications, statutory permissions, quality inspection and execution of the prescribed hiring agreement.";

const DEFAULT_HIRING_ASSURANCE_TERMS = [
  "The godown shall be completed strictly as per the approved DPR, drawings, specifications and applicable quality standards.",
  "Final hiring shall commence only after completion, joint inspection, statutory compliance, required accreditation/registration and execution of the prescribed agreement.",
  "Rent, handling conditions, hiring period, payment milestones and operational obligations shall be governed by the scheme guidelines and the final executed agreement.",
  "This assurance is linked to the PACS and proposed location stated in this letter and is not transferable without prior written approval.",
];

const HIRING_ASSURANCE_STYLE = `
.ha-desk{display:grid;gap:14px;color:#16324f;font-family:Inter,"Segoe UI",Arial,sans-serif}.ha-message{padding:11px 13px;border:1px solid #8bc8a2;border-radius:9px;background:#eef9f2;color:#145f39;font-size:10px;font-weight:850}.ha-message.error{border-color:#efaaaa;background:#fff1f1;color:#a12626}.ha-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:11px}.ha-kpis>div{padding:14px 15px;border:1px solid #cfdae5;border-top:4px solid #167344;border-radius:11px;background:#fff;box-shadow:0 6px 18px rgba(13,48,82,.05)}.ha-kpis span,.ha-kpis b{display:block}.ha-kpis span{color:#63778b;font-size:8px;font-weight:900;text-transform:uppercase}.ha-kpis b{margin-top:7px;color:#0b315b;font-size:22px}.ha-layout{display:grid;grid-template-columns:minmax(360px,.72fr) minmax(0,1.28fr);gap:14px;align-items:start}.ha-card{overflow:hidden;border:1px solid #c9d6e3;border-radius:12px;background:#fff;box-shadow:0 9px 25px rgba(13,48,82,.06)}.ha-card-head{padding:15px 17px;border-bottom:1px solid #d8e2eb;background:linear-gradient(110deg,#0a3968,#176640);color:#fff}.ha-card-head h3{margin:0;font-size:15px}.ha-card-head p{margin:5px 0 0;color:#d7eee2;font-size:9px}.ha-form{display:grid;gap:10px;padding:14px}.ha-form label{display:grid;gap:5px;color:#526b82;font-size:8px;font-weight:900;text-transform:uppercase}.ha-form input,.ha-form select,.ha-form textarea{width:100%;padding:10px;border:1px solid #afbdca;border-radius:8px;background:#fff;color:#173451;font:inherit;font-size:10px;outline:none}.ha-form input,.ha-form select{height:41px}.ha-form textarea{min-height:128px;resize:vertical;line-height:1.5}.ha-form input:focus,.ha-form select:focus,.ha-form textarea:focus{border-color:#167344;box-shadow:0 0 0 3px rgba(22,115,68,.11)}.ha-signature-preview{min-height:92px;display:grid;place-items:center;padding:10px;border:1px dashed #9babb9;border-radius:9px;background:#f8fafc}.ha-signature-preview img{max-width:240px;max-height:80px;object-fit:contain}.ha-signature-preview span{color:#75879a;font-size:9px}.ha-actions{display:grid;grid-template-columns:1fr 1fr;gap:8px}.ha-actions button,.ha-letter-actions button{min-height:39px;padding:8px 11px;border:1px solid #167344;border-radius:8px;background:#167344;color:#fff;font-size:8.5px;font-weight:950;cursor:pointer}.ha-actions button.secondary,.ha-letter-actions button.secondary{background:#fff;color:#125d3a}.ha-actions button:disabled,.ha-letter-actions button:disabled{cursor:not-allowed;opacity:.48}.ha-letter-grid{display:grid;gap:10px;padding:14px}.ha-letter{padding:13px;border:1px solid #d3dee8;border-left:4px solid #167344;border-radius:9px;background:#fbfdfc}.ha-letter-top{display:flex;justify-content:space-between;gap:12px}.ha-letter h4{margin:0;color:#0b315b;font-size:12px}.ha-letter p{margin:5px 0 0;color:#607286;font-size:9px}.ha-status{display:inline-flex;height:24px;align-items:center;padding:0 8px;border-radius:999px;background:#e4f5ea;color:#17633c;font-size:7.5px;font-weight:950}.ha-letter-meta{display:grid;grid-template-columns:repeat(3,1fr);gap:6px;margin-top:10px}.ha-letter-meta span{padding:7px;border-radius:7px;background:#eef3f7;color:#657789;font-size:7.5px}.ha-letter-meta b{display:block;margin-top:3px;color:#173451;font-size:8.5px}.ha-letter-actions{display:flex;gap:7px;margin-top:10px}.ha-empty{padding:34px;text-align:center;color:#6f8192;font-size:10px}@media(max-width:1050px){.ha-layout{grid-template-columns:1fr}.ha-kpis{grid-template-columns:1fr 1fr}}@media(max-width:650px){.ha-kpis,.ha-actions{grid-template-columns:1fr}.ha-letter-meta{grid-template-columns:1fr}}
`;

function HiringAssuranceDesk({
  pacs = [],
  allocations = [],
  projects = [],
  approvedPmcs = [],
  profile = {},
  stateName = "",
}) {
  const settingsId =
    auth.currentUser?.uid || profile.uid || profile.id || "branch_settings_unavailable";
  const [assurances, setAssurances] = useState([]);
  const [selectedPacsId, setSelectedPacsId] = useState("");
  const [signatureDataUrl, setSignatureDataUrl] = useState("");
  const [signerName, setSignerName] = useState("");
  const [signerDesignation, setSignerDesignation] = useState(
    "State Branch Manager",
  );
  const [branchOfficeAddress, setBranchOfficeAddress] = useState("");
  const [hiringPeriod, setHiringPeriod] = useState(
    "As per applicable scheme / final agreement",
  );
  const [bodyText, setBodyText] = useState(DEFAULT_HIRING_ASSURANCE_BODY);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [messageTone, setMessageTone] = useState("success");

  useEffect(() => {
    const stopSettings = onSnapshot(
      doc(db, "nafed_branch_settings", settingsId),
      (snapshot) => {
        const value = snapshot.exists()
          ? { id: snapshot.id, ...snapshot.data() }
          : null;
        setSignatureDataUrl(value?.hiringAssuranceSignatureDataUrl || "");
        setSignerName(
          value?.hiringAssuranceSignerName ||
            profile.name ||
            profile.officerName ||
            "",
        );
        setSignerDesignation(
          value?.hiringAssuranceSignerDesignation ||
            profile.designation ||
            "State Branch Manager",
        );
        setBranchOfficeAddress(
          value?.branchOfficeAddress ||
            profile.officeAddress ||
            profile.address ||
            "",
        );
      },
      (error) => console.warn("Branch assurance settings load skipped:", error),
    );
    const stopAssurances = onSnapshot(
      query(
        collection(db, "nafed_hiring_assurances"),
        where("state", "==", stateName),
      ),
      (snapshot) => {
        setAssurances(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      },
      (error) => console.warn("Hiring assurance register load skipped:", error),
    );
    return () => {
      stopSettings();
      stopAssurances();
    };
  }, [settingsId, stateName, profile.id]);

  const allocatedPacs = pacs.filter((item) => {
    const allocation = reportFindLinkedAllocation(item, allocations);
    return allocation && isActivePmcAllocation(allocation);
  });

  useEffect(() => {
    if (!allocatedPacs.length) {
      setSelectedPacsId("");
      return;
    }
    if (!allocatedPacs.some((item) => item.id === selectedPacsId)) {
      setSelectedPacsId(allocatedPacs[0].id);
    }
  }, [allocatedPacs.length, selectedPacsId]);

  const selectedPacs =
    allocatedPacs.find((item) => item.id === selectedPacsId) || null;
  const selectedAllocation = selectedPacs
    ? reportFindLinkedAllocation(selectedPacs, allocations)
    : null;
  const selectedProject = selectedPacs
    ? reportFindLinkedProject(selectedPacs, projects)
    : null;

  const pacsDocumentUid = (item, allocation = {}) => {
    const sourceIds = Array.from(
      new Set(
        [...(item?.sourcePacsIds || []), item?.id || item?.uid].filter(Boolean),
      ),
    );
    return (
      [
        allocation.pacsUid,
        allocation.pacsUserUid,
        allocation.pacsUserId,
        allocation.pacsId,
        item?.uid,
        item?.id,
      ].find((value) => sourceIds.includes(value)) || sourceIds[0] || ""
    );
  };

  const assuranceFor = (item) => {
    const allocation = reportFindLinkedAllocation(item, allocations);
    const uid = pacsDocumentUid(item, allocation || {});
    return assurances.find(
      (assurance) => assurance.id === uid || assurance.pacsUid === uid,
    );
  };

  const selectedExisting = selectedPacs ? assuranceFor(selectedPacs) : null;
  const issuedAssuranceCount = allocatedPacs.filter((item) =>
    Boolean(assuranceFor(item)),
  ).length;
  const financialYear = (() => {
    const today = new Date();
    const start = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
    return `${start}-${String(start + 1).slice(-2)}`;
  })();

  const buildLetter = () => {
    if (!selectedPacs || !selectedAllocation) return null;
    const pacsUid = pacsDocumentUid(selectedPacs, selectedAllocation);
    const currentPmc = resolveCurrentPmc(selectedAllocation, approvedPmcs);
    const registrationNumber = firstRecordValue(selectedPacs, [
      "pacsRegistrationNo",
      "pacsRegistrationNumber",
      "registrationNumber",
      "registrationNo",
    ]);
    const letterNumber =
      selectedExisting?.letterNumber ||
      `NAFED/${reportFileSlug(stateName).toUpperCase()}/HA/${financialYear}/${reportFileSlug(registrationNumber || pacsUid).toUpperCase()}`;
    return {
      ...(selectedExisting || {}),
      documentId: pacsUid,
      pacsUid,
      sourcePacsIds: selectedPacs.sourcePacsIds || [pacsUid],
      pacsName: entityName(selectedPacs),
      registrationNumber,
      state: stateName,
      district: reportExportValue(
        selectedPacs,
        selectedProject || {},
        selectedAllocation,
        ["district", "districtName", "projectDistrict"],
      ),
      taluka: reportExportValue(
        selectedPacs,
        selectedProject || {},
        selectedAllocation,
        ["taluka", "talukaName", "block"],
      ),
      village: reportExportValue(
        selectedPacs,
        selectedProject || {},
        selectedAllocation,
        ["village", "villageName"],
      ),
      capacityMt: Number(projectCapacity(selectedProject || {}) || 0),
      allocatedPmcUid:
        currentPmc?.id ||
        selectedAllocation.allocatedPmcUid ||
        selectedAllocation.pmcUid ||
        "",
      allocatedPmcName:
        (currentPmc && entityName(currentPmc)) ||
        selectedAllocation.allocatedPmcName ||
        selectedAllocation.pmcName ||
        "",
      letterNumber,
      financialYear,
      subject:
        "Hiring Assurance for the proposed scientific storage godown under the applicable NAFED programme",
      bodyText,
      terms: DEFAULT_HIRING_ASSURANCE_TERMS,
      hiringPeriod,
      signatureDataUrl,
      signerName,
      signerDesignation,
      branchOfficeAddress,
      issueDate: new Date().toISOString(),
      issuedByUid: auth.currentUser?.uid || "",
      issuedByRole: "STATE_BRANCH",
      status: "ISSUED",
      documentType: "HIRING_ASSURANCE",
    };
  };

  const handleSignatureFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setBusy(true);
      setMessage("");
      setSignatureDataUrl(await prepareSignatureImage(file));
      setMessageTone("success");
      setMessage("Signature prepared. Save Signature or Generate & Publish the letter.");
    } catch (error) {
      setMessageTone("error");
      setMessage(error.message || "Signature processing failed.");
    } finally {
      setBusy(false);
      event.target.value = "";
    }
  };

  const saveSignature = async () => {
    if (!signatureDataUrl || !signerName.trim() || !signerDesignation.trim()) {
      setMessageTone("error");
      setMessage("Signature, authorised signer name and designation are mandatory.");
      return;
    }
    try {
      setBusy(true);
      const batch = writeBatch(db);
      batch.set(
        doc(db, "nafed_branch_settings", settingsId),
        {
          state: stateName,
          branchUid: auth.currentUser?.uid || "",
          hiringAssuranceSignatureDataUrl: signatureDataUrl,
          hiringAssuranceSignerName: signerName.trim(),
          hiringAssuranceSignerDesignation: signerDesignation.trim(),
          branchOfficeAddress: branchOfficeAddress.trim(),
          signatureUpdatedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      await batch.commit();
      setMessageTone("success");
      setMessage("Authorised State Branch signature saved successfully.");
    } catch (error) {
      setMessageTone("error");
      setMessage(
        error?.code === "permission-denied"
          ? "Signature save permission denied. Supplied final Firestore Rules publish karein."
          : error.message || "Signature save failed.",
      );
    } finally {
      setBusy(false);
    }
  };

  const publishLetter = async () => {
    const letter = buildLetter();
    if (!letter?.pacsUid) {
      setMessageTone("error");
      setMessage("An allocated PACS must be selected.");
      return;
    }
    if (!signatureDataUrl || !signerName.trim() || !signerDesignation.trim()) {
      setMessageTone("error");
      setMessage("Upload the authorised signature and complete signer details first.");
      return;
    }
    if (
      !window.confirm(
        `${letter.pacsName} ke liye Hiring Assurance generate karke PACS dashboard par publish karna hai?`,
      )
    )
      return;

    try {
      setBusy(true);
      setMessage("");
      const now = serverTimestamp();
      const batch = writeBatch(db);
      batch.set(
        doc(db, "nafed_branch_settings", settingsId),
        {
          state: stateName,
          branchUid: auth.currentUser?.uid || "",
          hiringAssuranceSignatureDataUrl: signatureDataUrl,
          hiringAssuranceSignerName: signerName.trim(),
          hiringAssuranceSignerDesignation: signerDesignation.trim(),
          branchOfficeAddress: branchOfficeAddress.trim(),
          signatureUpdatedAt: now,
          updatedAt: now,
        },
        { merge: true },
      );
      batch.set(
        doc(db, "nafed_hiring_assurances", letter.pacsUid),
        {
          ...letter,
          bodyText: bodyText.trim(),
          signerName: signerName.trim(),
          signerDesignation: signerDesignation.trim(),
          branchOfficeAddress: branchOfficeAddress.trim(),
          signatureDataUrl,
          documentVersion: Number(selectedExisting?.documentVersion || 0) + 1,
          createdAt: selectedExisting?.createdAt || now,
          issuedAt: now,
          updatedAt: now,
        },
        { merge: true },
      );
      batch.set(
        doc(db, "nafed_users", letter.pacsUid),
        {
          hiringAssuranceStatus: "ISSUED",
          hiringAssuranceId: letter.pacsUid,
          hiringAssuranceLetterNumber: letter.letterNumber,
          hiringAssuranceIssuedAt: now,
          updatedAt: now,
        },
        { merge: true },
      );
      await batch.commit();

      try {
        await addDoc(collection(db, "notifications_pacs"), {
          recipientUid: letter.pacsUid,
          recipientRole: "PACS",
          pacsUid: letter.pacsUid,
          title: "Hiring Assurance issued",
          message: `${letter.letterNumber} is available for direct download in your PACS dashboard.`,
          type: "HIRING_ASSURANCE_ISSUED",
          nextAction: "DOWNLOAD_HIRING_ASSURANCE",
          state: stateName,
          status: "UNREAD",
          isRead: false,
          createdAt: serverTimestamp(),
        });
      } catch (notificationError) {
        console.warn("Hiring assurance notification skipped:", notificationError);
      }
      try {
        await addDoc(collection(db, "nafed_activity"), {
          action: "STATE_BRANCH_HIRING_ASSURANCE_ISSUED",
          targetUid: letter.pacsUid,
          targetRole: "PACS",
          targetName: letter.pacsName,
          letterNumber: letter.letterNumber,
          state: stateName,
          performedByUid: auth.currentUser?.uid || "",
          performedByRole: "STATE_BRANCH",
          createdAt: serverTimestamp(),
        });
      } catch (auditError) {
        console.warn("Hiring assurance audit skipped:", auditError);
      }

      setMessageTone("success");
      setMessage(
        `${letter.letterNumber} generated and published directly to ${letter.pacsName}.`,
      );
    } catch (error) {
      console.error("Hiring assurance publish failed:", error);
      setMessageTone("error");
      setMessage(
        error?.code === "permission-denied"
          ? "Hiring Assurance publish permission denied. Supplied final Firestore Rules publish karein."
          : error.message || "Hiring Assurance generation failed.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="ha-desk">
      <style>{HIRING_ASSURANCE_STYLE}</style>
      <div className="ha-kpis">
        <div><span>Allocated PACS</span><b>{allocatedPacs.length}</b></div>
        <div><span>Assurance Issued</span><b>{issuedAssuranceCount}</b></div>
        <div><span>Awaiting Letter</span><b>{Math.max(allocatedPacs.length - issuedAssuranceCount, 0)}</b></div>
        <div><span>Signature Status</span><b>{signatureDataUrl ? "Ready" : "Missing"}</b></div>
      </div>
      {message && (
        <div className={`ha-message ${messageTone === "error" ? "error" : ""}`}>
          {message}
        </div>
      )}
      <div className="ha-layout">
        <section className="ha-card">
          <div className="ha-card-head"><h3>Authorised Signature & Letter Setup</h3><p>One saved signature can be applied to PACS-specific assurance letters.</p></div>
          <div className="ha-form">
            <label>Select Allocated PACS<select value={selectedPacsId} onChange={(event) => setSelectedPacsId(event.target.value)}><option value="">Select PACS</option>{allocatedPacs.map((item) => <option key={item.id} value={item.id}>{entityName(item)}</option>)}</select></label>
            <label>Authorised Signer Name<input value={signerName} onChange={(event) => setSignerName(event.target.value)} placeholder="State Branch authorised officer" /></label>
            <label>Designation<input value={signerDesignation} onChange={(event) => setSignerDesignation(event.target.value)} /></label>
            <label>Branch Office Address<input value={branchOfficeAddress} onChange={(event) => setBranchOfficeAddress(event.target.value)} /></label>
            <label>Hiring Period / Reference<input value={hiringPeriod} onChange={(event) => setHiringPeriod(event.target.value)} /></label>
            <label>Authorised Signature Image<input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleSignatureFile} /></label>
            <div className="ha-signature-preview">{signatureDataUrl ? <img src={signatureDataUrl} alt="State Branch signature preview" /> : <span>Upload cropped PNG/JPG signature</span>}</div>
            <label>Official Assurance Text<textarea value={bodyText} onChange={(event) => setBodyText(event.target.value)} /></label>
            <div className="ha-actions">
              <button type="button" className="secondary" disabled={busy} onClick={saveSignature}>Save Signature</button>
              <button type="button" disabled={busy || !selectedPacs} onClick={publishLetter}>{busy ? "Processing..." : selectedExisting ? "Regenerate & Publish" : "Generate & Publish"}</button>
              <button type="button" className="secondary" disabled={!selectedPacs || !signatureDataUrl} onClick={() => { const letter = buildLetter(); if (letter) printHiringAssurance(letter); }}>Preview / Print PDF</button>
            </div>
          </div>
        </section>
        <section className="ha-card">
          <div className="ha-card-head"><h3>Published Hiring Assurance Register</h3><p>PACS can open the same issued copy from its own dashboard.</p></div>
          <div className="ha-letter-grid">
            {allocatedPacs.map((item) => {
              const assurance = assuranceFor(item);
              const allocation = reportFindLinkedAllocation(item, allocations);
              const currentPmc = resolveCurrentPmc(allocation || {}, approvedPmcs);
              return (
                <article className="ha-letter" key={item.id}>
                  <div className="ha-letter-top"><div><h4>{entityName(item)}</h4><p>{assurance?.letterNumber || "Hiring Assurance not issued"}</p></div><span className="ha-status">{assurance ? "ISSUED" : "AWAITING"}</span></div>
                  <div className="ha-letter-meta"><span>Allocated PMC<b>{(currentPmc && entityName(currentPmc)) || allocation?.allocatedPmcName || allocation?.pmcName || "-"}</b></span><span>Capacity<b>{formatCapacity(projectCapacity(reportFindLinkedProject(item, projects) || {}))}</b></span><span>Version<b>{assurance?.documentVersion || "-"}</b></span></div>
                  <div className="ha-letter-actions"><button type="button" disabled={!assurance} onClick={() => assurance && printHiringAssurance(assurance)}>Download / Save PDF</button><button type="button" className="secondary" onClick={() => setSelectedPacsId(item.id)}>{assurance ? "Review / Regenerate" : "Prepare Letter"}</button></div>
                </article>
              );
            })}
            {!allocatedPacs.length && <div className="ha-empty">No actively allocated PACS is available. Complete PACS–PMC allocation first.</div>}
          </div>
        </section>
      </div>
    </section>
  );
}

function BranchRegistrationMis({
  pacs = [],
  pmcs = [],
  vendors = [],
  stateName = "",
  projects = [],
  allocations = [],
}) {
  const [type, setType] = useState("PACS");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [districtFilter, setDistrictFilter] = useState("ALL");
  const [talukaFilter, setTalukaFilter] = useState("ALL");
  const [cleanupBusyId, setCleanupBusyId] = useState("");
  const [cleanupMessage, setCleanupMessage] = useState("");
  const [cleanupTone, setCleanupTone] = useState("success");

  const source = type === "PACS" ? pacs : type === "PMC" ? pmcs : vendors;
  const linkedWorkflow = (item) => {
    if (type !== "PACS") return { project: null, allocation: null };
    return {
      project: reportFindLinkedProject(item, projects),
      allocation: reportFindLinkedAllocation(item, allocations),
    };
  };
  const statusOf = (item) => {
    if (type !== "PACS") {
      return upper(
        item.registrationStatus ||
          item.branchApprovalStatus ||
          item.roleApprovalStatus ||
          item.status ||
          "PENDING",
      );
    }
    const { project, allocation } = linkedWorkflow(item);
    const workStartStatuses = [
      item.workStartStatus,
      item.pmcApprovalStatus,
      item.approvalStage,
      project?.workStartStatus,
      project?.pmcApprovalStatus,
      project?.approvalStage,
      project?.workflowStage,
      project?.currentStage,
    ].map(upper);
    const workStartApproved =
      item.workStartApproved === true ||
      project?.workStartApproved === true ||
      workStartStatuses.some(
        (value) =>
          value === "WORK_START_APPROVED" ||
          value === "APPROVED_TO_START_WORK" ||
          value === "WORK_COMMENCEMENT_APPROVED",
      );
    if (workStartApproved) return "WORK_START_APPROVED";
    if (allocation && isActivePmcAllocation(allocation)) {
      return "ALLOCATED_PMC_VERIFICATION_PENDING";
    }
    if (
      [item.branchApprovalStatus, item.roleApprovalStatus].some(
        (value) => upper(value) === "APPROVED",
      )
    ) {
      return "BRANCH_APPROVED_ALLOCATION_PENDING";
    }
    return upper(
      item.registrationStatus || item.status || "PENDING_STATE_BRANCH_APPROVAL",
    );
  };
  const searchedSource = source.filter((item) => {
    const text = [
      item.organizationName,
      item.societyName,
      item.pacsName,
      item.agencyName,
      item.companyName,
      item.name,
      item.email,
      item.mobile,
      item.registrationNumber,
      item.district,
      item.village,
    ]
      .join(" ")
      .toLowerCase();
    return (
      text.includes(search.trim().toLowerCase()) &&
      (statusFilter === "ALL" || statusOf(item).includes(statusFilter))
    );
  });

  const pacsLocationValue = (item, fields) => {
    const linkedProject = reportFindLinkedProject(item, projects);
    return reportExportValue(item, linkedProject || {}, {}, fields);
  };

  const districtOptions =
    type === "PACS"
      ? Array.from(
          new Set(
            searchedSource
              .map((item) =>
                reportLocationLabel(
                  pacsLocationValue(item, [
                    "district",
                    "districtName",
                    "projectDistrict",
                  ]),
                  "",
                ),
              )
              .filter(Boolean),
          ),
        ).sort((a, b) => a.localeCompare(b))
      : [];

  const talukaOptions =
    type === "PACS"
      ? Array.from(
          new Set(
            searchedSource
              .filter((item) => {
                if (districtFilter === "ALL") return true;
                return (
                  normalise(
                    pacsLocationValue(item, [
                      "district",
                      "districtName",
                      "projectDistrict",
                    ]),
                  ) === normalise(districtFilter)
                );
              })
              .map((item) =>
                reportLocationLabel(
                  pacsLocationValue(item, ["taluka", "talukaName", "block"]),
                  "",
                ),
              )
              .filter(Boolean),
          ),
        ).sort((a, b) => a.localeCompare(b))
      : [];

  const visible = searchedSource
    .filter((item) => {
      if (type !== "PACS") return true;
      const itemDistrict = pacsLocationValue(item, [
        "district",
        "districtName",
        "projectDistrict",
      ]);
      const itemTaluka = pacsLocationValue(item, [
        "taluka",
        "talukaName",
        "block",
      ]);
      return (
        (districtFilter === "ALL" ||
          normalise(itemDistrict) === normalise(districtFilter)) &&
        (talukaFilter === "ALL" ||
          normalise(itemTaluka) === normalise(talukaFilter))
      );
    })
    .sort((a, b) => {
      if (type !== "PACS") return entityName(a).localeCompare(entityName(b));
      const districtA = pacsLocationValue(a, [
        "district",
        "districtName",
        "projectDistrict",
      ]);
      const districtB = pacsLocationValue(b, [
        "district",
        "districtName",
        "projectDistrict",
      ]);
      const talukaA = pacsLocationValue(a, ["taluka", "talukaName", "block"]);
      const talukaB = pacsLocationValue(b, ["taluka", "talukaName", "block"]);
      return (
        districtA.localeCompare(districtB) ||
        talukaA.localeCompare(talukaB) ||
        entityName(a).localeCompare(entityName(b))
      );
    });

  useEffect(() => {
    setDistrictFilter("ALL");
    setTalukaFilter("ALL");
    setCleanupMessage("");
  }, [type]);
  const branchCleared = source.filter((item) => {
    const statusValue = statusOf(item);
    return (
      statusValue.includes("APPROVED") || statusValue.includes("ALLOCATED")
    );
  }).length;
  const pending = source.filter(
    (item) => {
      const statusValue = statusOf(item);
      return (
        !statusValue.includes("ALLOCATED") &&
        (statusValue.includes("PENDING") || statusValue.includes("AWAITING"))
      );
    },
  ).length;
  const duplicateSourceIds = (item = {}) =>
    Array.from(
      new Set(
        [...(item.sourcePacsIds || []), item.id || item.uid].filter(Boolean),
      ),
    );
  const canonicalProfileId = (item = {}) => {
    const ids = duplicateSourceIds(item);
    const { project, allocation } = linkedWorkflow(item);
    const references = [
      allocation?.pacsUid,
      allocation?.pacsUserUid,
      allocation?.pacsUserId,
      allocation?.pacsId,
      project?.pacsUid,
      project?.pacsUserUid,
      project?.pacsUserId,
      project?.pacsId,
      item.uid,
      item.id,
    ].filter(Boolean);
    return references.find((id) => ids.includes(id)) || ids[0] || "";
  };
  const duplicateIdsFor = (item = {}) => {
    const canonicalId = canonicalProfileId(item);
    return duplicateSourceIds(item).filter((id) => id !== canonicalId);
  };
  const duplicateProfileCount =
    type === "PACS"
      ? source.reduce((total, item) => total + duplicateIdsFor(item).length, 0)
      : 0;

  const archiveDuplicateProfiles = async (item) => {
    const duplicateIds = duplicateIdsFor(item);
    const canonicalId = canonicalProfileId(item);
    if (!duplicateIds.length || !canonicalId) return;
    const confirmation = window.confirm(
      `${entityName(item)} ke ${duplicateIds.length} duplicate profile archive karne hain?\n\nCanonical PACS login/profile (${canonicalId}) aur linked application/allocation safe rahenge.`,
    );
    if (!confirmation) return;

    try {
      setCleanupBusyId(item.id);
      setCleanupMessage("");
      const batch = writeBatch(db);
      duplicateIds.forEach((profileId) => {
        batch.set(
          doc(db, "nafed_users", profileId),
          {
            isDeleted: true,
            isArchived: true,
            status: "ARCHIVED",
            registrationStatus: "ARCHIVED",
            archivedAsDuplicate: true,
            duplicateOfPacsUid: canonicalId,
            archivedByUid: auth.currentUser?.uid || "",
            archivedByRole: "STATE_BRANCH",
            archivedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
      });
      await batch.commit();
      try {
        await addDoc(collection(db, "nafed_activity"), {
          action: "STATE_BRANCH_DUPLICATE_PACS_ARCHIVED",
          targetUid: canonicalId,
          targetRole: "PACS",
          targetName: entityName(item),
          duplicateProfileIds: duplicateIds,
          state: stateName,
          performedByUid: auth.currentUser?.uid || "",
          performedByRole: "STATE_BRANCH",
          createdAt: serverTimestamp(),
        });
      } catch (auditError) {
        console.warn("Duplicate cleanup audit write skipped:", auditError);
      }
      setCleanupTone("success");
      setCleanupMessage(
        `${duplicateIds.length} duplicate profile safely archived. Canonical PACS record retained.`,
      );
    } catch (error) {
      console.error("Duplicate PACS cleanup failed:", error);
      setCleanupTone("error");
      setCleanupMessage(
        error?.code === "permission-denied"
          ? "Duplicate archive permission denied. Supplied final Firestore Rules publish karein."
          : error?.message || "Duplicate profile archive failed.",
      );
    } finally {
      setCleanupBusyId("");
    }
  };

  const pacsExportRows =
    type === "PACS"
      ? visible
          .map((item) => {
            const linkedProject = reportFindLinkedProject(item, projects);
            const linkedAllocation = reportFindLinkedAllocation(
              item,
              allocations,
            );
            const linkedPmc = resolveCurrentPmc(
              linkedAllocation || linkedProject || {},
              pmcs,
            );
            const exportValue = (fields, fallback = "-") =>
              reportExportValue(
                item,
                linkedProject || {},
                linkedAllocation || {},
                fields,
              ) || fallback;
            const rawApplicationStatus = linkedProject
              ? firstRecordValue(linkedProject, [
                  "currentStage",
                  "workflowStage",
                  "projectStatus",
                  "applicationStatus",
                  "rawStatus",
                  "status",
                ]) || canonicalStage(linkedProject)
              : "NOT APPLIED";
            return {
              pacsName: entityName(item),
              registrationNo: exportValue([
                "pacsRegistrationNo",
                "pacsRegistrationNumber",
                "registrationNumber",
                "registrationNo",
              ]),
              applicationId: linkedProject
                ? linkedProject.applicationId || linkedProject.id || "-"
                : "-",
              capacityMt: linkedProject
                ? Number(projectCapacity(linkedProject) || 0)
                : 0,
              village: reportLocationLabel(
                exportValue(["village", "villageName"]),
              ),
              taluka: reportLocationLabel(
                exportValue(["taluka", "talukaName", "block"]),
              ),
              district: reportLocationLabel(
                exportValue([
                  "district",
                  "districtName",
                  "projectDistrict",
                ]),
              ),
              contact: exportValue([
                "mobile",
                "phone",
                "mobileNumber",
                "pacsMobile",
                "contactMobile",
                "contactNumber",
              ]),
              email: exportValue([
                "email",
                "officialEmail",
                "pacsEmail",
                "contactEmail",
              ]),
              applicationStatus: rawApplicationStatus.replaceAll("_", " "),
              allocatedPmc:
                (linkedPmc && entityName(linkedPmc)) ||
                firstRecordValue(linkedAllocation || {}, [
                  "allocatedPmcName",
                  "pmcName",
                  "pmcAgencyName",
                  "pmcOrganizationName",
                ]) ||
                firstRecordValue(linkedProject || {}, [
                  "allocatedPmcName",
                  "pmcName",
                  "pmcAgencyName",
                  "pmcOrganizationName",
                ]) ||
                "Not Allocated",
              registrationStatus: statusOf(item).replaceAll("_", " "),
              registeredOn: reportDateText(item.createdAt) || "-",
              appliedOn: linkedProject
                ? reportDateText(
                    linkedProject.submittedAt ||
                      linkedProject.applicationDate ||
                      linkedProject.createdAt,
                  ) || "-"
                : "-",
            };
          })
          .sort(
            (a, b) =>
              a.district.localeCompare(b.district) ||
              a.taluka.localeCompare(b.taluka) ||
              a.pacsName.localeCompare(b.pacsName),
          )
          .map((row, index) => ({ ...row, srNo: index + 1 }))
      : [];

  const pacsExportColumns = [
    ["srNo", "Sr. No."],
    ["pacsName", "PACS Name"],
    ["registrationNo", "Registration Number"],
    ["applicationId", "Application ID"],
    ["capacityMt", "Applied Capacity (MT)"],
    ["village", "Village"],
    ["taluka", "Taluka"],
    ["district", "District"],
    ["contact", "Contact Number"],
    ["email", "Email"],
    ["applicationStatus", "Application Status"],
    ["allocatedPmc", "Allocated PMC"],
    ["registrationStatus", "Registration Status"],
    ["registeredOn", "Registered On"],
    ["appliedOn", "Applied On"],
  ];

  const totalAppliedCapacity = pacsExportRows.reduce(
    (sum, row) => sum + Number(row.capacityMt || 0),
    0,
  );
  const appliedPacsCount = pacsExportRows.filter(
    (row) => row.applicationId !== "-",
  ).length;

  const pacsDistrictSummaryRows = Array.from(
    pacsExportRows
      .reduce((summary, row) => {
        const key = reportLocationKey(row.district) || "-";
        const current = summary.get(key) || {
          district: reportLocationLabel(row.district),
          pacsCount: 0,
          godownCount: 0,
          capacityMt: 0,
        };
        current.pacsCount += 1;
        current.godownCount += row.applicationId !== "-" ? 1 : 0;
        current.capacityMt += Number(row.capacityMt || 0);
        summary.set(key, current);
        return summary;
      }, new Map())
      .values(),
  )
    .sort((a, b) => a.district.localeCompare(b.district))
    .map((row, index) => ({ ...row, srNo: index + 1 }));

  const pacsTalukaSummaryRows = Array.from(
    pacsExportRows
      .reduce((summary, row) => {
        const key = `${reportLocationKey(row.district) || "-"}|||${
          reportLocationKey(row.taluka) || "-"
        }`;
        const current = summary.get(key) || {
          district: reportLocationLabel(row.district),
          taluka: reportLocationLabel(row.taluka),
          pacsCount: 0,
          godownCount: 0,
          capacityMt: 0,
        };
        current.pacsCount += 1;
        current.godownCount += row.applicationId !== "-" ? 1 : 0;
        current.capacityMt += Number(row.capacityMt || 0);
        summary.set(key, current);
        return summary;
      }, new Map())
      .values(),
  )
    .sort(
      (a, b) =>
        a.district.localeCompare(b.district) ||
        a.taluka.localeCompare(b.taluka),
    )
    .map((row, index) => ({ ...row, srNo: index + 1 }));

  const pacsExecutiveSummaryHtml = () =>
    `<table class="executive-summary"><thead><tr><th>State</th><th>Total PACS</th><th>Total Godowns</th><th>Total Capacity</th></tr></thead><tbody><tr><td>${reportEscapeHtml(
      reportLocationLabel(stateName || "All India"),
    )}</td><td>${pacsExportRows.length}</td><td>${appliedPacsCount}</td><td>${totalAppliedCapacity.toLocaleString(
      "en-IN",
    )} MT</td></tr></tbody></table>`;

  const pacsDistrictSummaryHtml = () => {
    const body = pacsDistrictSummaryRows.length
      ? pacsDistrictSummaryRows
          .map(
            (row) =>
              `<tr><td>${row.srNo}</td><td>${reportEscapeHtml(row.district)}</td><td>${row.pacsCount}</td><td>${row.godownCount}</td><td>${row.capacityMt.toLocaleString("en-IN")} MT</td></tr>`,
          )
          .join("")
      : '<tr><td colspan="5">No district data matches the selected filters.</td></tr>';
    return `<h3 class="area-title">District-wise Consolidated Summary</h3><table class="area-summary district-summary"><thead><tr><th>Sr.</th><th>District</th><th>Total PACS</th><th>Total Godowns</th><th>Total Capacity</th></tr></thead><tbody>${body}</tbody><tfoot><tr><td colspan="2">DISTRICT GRAND TOTAL</td><td>${pacsExportRows.length}</td><td>${appliedPacsCount}</td><td>${totalAppliedCapacity.toLocaleString("en-IN")} MT</td></tr></tfoot></table>`;
  };

  const pacsTalukaSummaryHtml = () => {
    const body = pacsTalukaSummaryRows.length
      ? pacsTalukaSummaryRows
          .map(
            (row) =>
              `<tr><td>${row.srNo}</td><td>${reportEscapeHtml(row.district)}</td><td>${reportEscapeHtml(row.taluka)}</td><td>${row.pacsCount}</td><td>${row.godownCount}</td><td>${row.capacityMt.toLocaleString("en-IN")} MT</td></tr>`,
          )
          .join("")
      : '<tr><td colspan="6">No taluka data matches the selected filters.</td></tr>';
    return `<h3 class="area-title">Taluka-wise Detailed Summary</h3><table class="area-summary taluka-summary"><thead><tr><th>Sr.</th><th>District</th><th>Taluka</th><th>Total PACS</th><th>Total Godowns</th><th>Total Capacity</th></tr></thead><tbody>${body}</tbody><tfoot><tr><td colspan="3">TALUKA GRAND TOTAL</td><td>${pacsExportRows.length}</td><td>${appliedPacsCount}</td><td>${totalAppliedCapacity.toLocaleString("en-IN")} MT</td></tr></tfoot></table>`;
  };

  const pacsReportTableHtml = () => {
    const head = pacsExportColumns
      .map(([, label]) => `<th>${reportEscapeHtml(label)}</th>`)
      .join("");
    const body = pacsExportRows.length
      ? pacsExportRows
          .map(
            (row) =>
              `<tr>${pacsExportColumns
                .map(([key]) => {
                  const textCell = [
                    "registrationNo",
                    "applicationId",
                    "contact",
                  ].includes(key);
                  return `<td${textCell ? ` style="mso-number-format:'\\@'"` : ""}>${reportEscapeHtml(
                    key === "capacityMt"
                      ? Number(row[key] || 0).toLocaleString("en-IN")
                      : row[key],
                  )}</td>`;
                })
                .join("")}</tr>`,
          )
          .join("")
      : `<tr><td colspan="${pacsExportColumns.length}">No PACS record matches the selected filters.</td></tr>`;
    const grandTotalFooter = `<tfoot><tr><td colspan="4">GRAND TOTAL · ${reportEscapeHtml(reportScopeLabel)}</td><td>${totalAppliedCapacity.toLocaleString("en-IN")} MT</td><td colspan="${pacsExportColumns.length - 5}">${pacsExportRows.length} PACS RECORDS · ${appliedPacsCount} GODOWNS</td></tr></tfoot>`;
    return `<h3 class="area-title detail-title">Complete PACS / Godown Detail Register</h3><table class="detail-register"><thead><tr>${head}</tr></thead><tbody>${body}</tbody>${grandTotalFooter}</table>`;
  };
  const reportScopeLabel = [
    reportLocationLabel(stateName || "All India"),
    districtFilter === "ALL"
      ? "All Districts"
      : `${reportLocationLabel(districtFilter)} District`,
    talukaFilter === "ALL"
      ? "All Talukas"
      : `${reportLocationLabel(talukaFilter)} Taluka`,
  ]
    .filter(Boolean)
    .join(" · ");
  const reportFileScope = [
    stateName,
    districtFilter === "ALL" ? "All_Districts" : districtFilter,
    talukaFilter === "ALL" ? "All_Talukas" : talukaFilter,
  ]
    .filter(Boolean)
    .map(reportFileSlug)
    .join("_");

  const downloadPacsExcel = () => {
    const generatedOn = new Date().toLocaleString("en-IN");
    const html = `<!doctype html><html><head><meta charset="utf-8"><style>body{font-family:Arial;color:#102d4f}h1{font-size:22px;margin:0;color:#082f5b}h2{font-size:15px;margin:5px 0;color:#176640}.scope{margin-top:5px;color:#4d647a;font-size:11px}.meta{margin:10px 0 12px;padding:8px;border:1px solid #7690aa;background:#eef4f8;font-size:10px}.area-title{margin:16px 0 6px;color:#0b396c;font-size:13px}.executive-summary{margin:12px 0 16px;font-size:12px}.executive-summary th{background:#176640}.executive-summary td{background:#edf8f1!important;color:#0b396c;font-size:15px;font-weight:bold}.area-summary{margin-bottom:15px}.district-summary{max-width:920px}.detail-title{margin-top:20px}table{width:100%;border-collapse:collapse;font-size:10px}th,td{border:1px solid #243b53;padding:7px;vertical-align:top}th{background:#0b396c;color:#fff;font-weight:bold}tbody tr:nth-child(even) td{background:#f2f6f9}tfoot td{background:#dff2e7!important;color:#0c5632;font-size:11px;font-weight:bold}</style></head><body><h1>NAFED State Branch Portal</h1><h2>Professional PACS &amp; Godown Register</h2><div class="scope">${reportEscapeHtml(reportScopeLabel)}</div><div class="meta"><b>Report Type:</b> State / District / Taluka Consolidated MIS &nbsp; | &nbsp; <b>Generated:</b> ${reportEscapeHtml(generatedOn)} &nbsp; | &nbsp; Location names are consolidated without case differences.</div>${pacsExecutiveSummaryHtml()}${pacsDistrictSummaryHtml()}${pacsTalukaSummaryHtml()}${pacsReportTableHtml()}</body></html>`;
    const blob = new Blob(["\uFEFF", html], {
      type: "application/vnd.ms-excel;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `NAFED_${reportFileScope}_PACS_Application_Register.xls`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const printPacsPdf = () => {
    const reportWindow = window.open(
      "",
      "_blank",
      "width=1400,height=900,scrollbars=yes,resizable=yes",
    );
    if (!reportWindow) {
      window.alert(
        "PDF report window was blocked. Please allow pop-ups and try again.",
      );
      return;
    }
    const generatedOn = new Date().toLocaleString("en-IN");
    const logoUrl = new URL(LOGO_PATH, window.location.origin).href;
    reportWindow.document.open();
    reportWindow.document.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>${reportEscapeHtml(reportScopeLabel)} PACS &amp; Godown Register</title><style>@page{size:A4 landscape;margin:9mm}*{box-sizing:border-box}body{margin:0;color:#102d4f;font-family:Arial,sans-serif;-webkit-print-color-adjust:exact;print-color-adjust:exact}.letterhead{display:flex;align-items:center;gap:13px;padding-bottom:9px;border-bottom:3px solid #176640}.letterhead img{width:72px;height:58px;object-fit:contain}.letterhead h1{margin:0;color:#082f5b;font-size:17px}.letterhead h2{margin:4px 0 0;color:#176640;font-size:12px}.letterhead p{margin:4px 0 0;color:#53677d;font-size:8px}.report-meta{display:flex;justify-content:space-between;margin:7px 0;color:#53677d;font-size:7px}.area-title{margin:8px 0 4px;color:#0b396c;font-size:8px}.detail-title{break-before:page;page-break-before:always;padding-top:2px}.executive-summary{margin:9px 0;font-size:8px}.executive-summary th{background:#176640}.executive-summary td{background:#edf8f1!important;color:#0b396c;font-size:11px;font-weight:bold}.district-summary{font-size:7px}table{width:100%;border-collapse:collapse;table-layout:auto;font-size:5.8px}.area-summary{margin-bottom:8px;font-size:6.2px}thead{display:table-header-group}tfoot{display:table-row-group}tr{break-inside:avoid;page-break-inside:avoid}th,td{border:1px solid #34495e;padding:4px 3px;text-align:left;vertical-align:top;overflow-wrap:anywhere}th{background:#0b396c;color:#fff;font-size:5.6px;text-transform:uppercase}tbody tr:nth-child(even) td{background:#f2f6f9}tfoot td{background:#dff2e7!important;color:#0c5632;font-size:6.4px;font-weight:bold}footer{margin-top:8px;padding-top:6px;border-top:1px solid #9aaaba;color:#64748b;font-size:6.5px;text-align:center}@media print{body{print-color-adjust:exact}}</style></head><body><div class="letterhead"><img src="${reportEscapeHtml(logoUrl)}" alt="NAFED"><div><h1>NAFED State Branch Portal</h1><h2>Professional PACS &amp; Godown Register</h2><p>${reportEscapeHtml(reportScopeLabel)}</p></div></div><div class="report-meta"><span>State / District / Taluka Consolidated MIS</span><span>Generated: ${reportEscapeHtml(generatedOn)}</span></div>${pacsExecutiveSummaryHtml()}${pacsDistrictSummaryHtml()}${pacsTalukaSummaryHtml()}${pacsReportTableHtml()}<footer>System-generated official MIS report · NAFED State Branch Portal · Location names consolidated case-insensitively · Printed ${reportEscapeHtml(generatedOn)}</footer><script>window.onload=function(){setTimeout(function(){window.print();},350)};<\/script></body></html>`,
    );
    reportWindow.document.close();
  };

  const tableColumnCount = type === "PACS" ? 9 : 7;

  return (
    <section className="registration-mis">
      <style>{REGISTRATION_REPORT_STYLE}</style>
      <div className="reg-kpis">
        <div>
          <span>Total Registered</span>
          <b>{source.length}</b>
        </div>
        <div>
          <span>{type === "PACS" ? "Branch Cleared / Allocated" : "Approved"}</span>
          <b>{branchCleared}</b>
        </div>
        <div>
          <span>{type === "PACS" ? "Awaiting Branch Clearance" : "Pending Approval"}</span>
          <b>{pending}</b>
        </div>
        <div>
          <span>Search Result</span>
          <b>{visible.length}</b>
        </div>
      </div>
      {type === "PACS" && duplicateProfileCount > 0 && !cleanupMessage && (
        <div className="reg-message">
          {duplicateProfileCount} duplicate PACS profile detected. Canonical
          allocation/login record is protected; duplicate-only archive controls
          are available in the Duplicate Control column.
        </div>
      )}
      {cleanupMessage && (
        <div className={`reg-message ${cleanupTone === "error" ? "error" : ""}`}>
          {cleanupMessage}
        </div>
      )}
      <div
        className={`reg-toolbar ${
          type === "PACS" ? "with-location-filters" : ""
        }`}
      >
        <div className="reg-tabs">
          {[
            ["PACS", "PACS Register"],
            ["PMC", "PMC Register"],
            ["VENDOR", "Vendor Register"],
          ].map(([value, label]) => (
            <button
              key={value}
              className={type === value ? "active" : ""}
              onClick={() => setType(value)}
            >
              {label}
            </button>
          ))}
        </div>
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={`Search ${type.toLowerCase()} name, registration, district...`}
        />
        <select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
        >
          <option value="ALL">All Status</option>
          <option value="APPROVED">Approved</option>
          <option value="ALLOCATED">Allocated to PMC</option>
          <option value="PENDING">Pending</option>
          <option value="QUERY">Query</option>
          <option value="REJECT">Rejected</option>
        </select>
        {type === "PACS" && (
          <>
            <select
              value={districtFilter}
              onChange={(event) => {
                setDistrictFilter(event.target.value);
                setTalukaFilter("ALL");
              }}
              aria-label="Filter PACS report by district"
            >
              <option value="ALL">All Districts</option>
              {districtOptions.map((district) => (
                <option key={district} value={district}>
                  {district}
                </option>
              ))}
            </select>
            <select
              value={talukaFilter}
              onChange={(event) => setTalukaFilter(event.target.value)}
              aria-label="Filter PACS report by taluka"
            >
              <option value="ALL">All Talukas</option>
              {talukaOptions.map((taluka) => (
                <option key={taluka} value={taluka}>
                  {taluka}
                </option>
              ))}
            </select>
          </>
        )}
        {type === "PACS" && (
          <div className="reg-export-actions">
            <div>
              <b>PACS Application Register</b>
              <span>
                {appliedPacsCount} applied PACS ·{" "}
                {formatCapacity(totalAppliedCapacity)} · {reportScopeLabel}
              </span>
            </div>
            <button type="button" className="excel" onClick={downloadPacsExcel}>
              Download Excel
            </button>
            <button type="button" className="pdf" onClick={printPacsPdf}>
              Print / Save PDF
            </button>
          </div>
        )}
      </div>
      <div className="reg-table">
        <table>
          <thead>
            <tr>
              <th>Sr.</th>
              <th>{type} Name</th>
              <th>Registration</th>
              {type === "PACS" && <th>Application / Capacity</th>}
              <th>Location / Scope</th>
              <th>Contact</th>
              <th>Status</th>
              <th>Registered</th>
              {type === "PACS" && <th>Duplicate Control</th>}
            </tr>
          </thead>
          <tbody>
            {!visible.length && (
              <tr>
                <td className="reg-empty" colSpan={tableColumnCount}>
                  No active {type.toLowerCase()} registration found for{" "}
                  {stateName}.
                </td>
              </tr>
            )}
            {visible.map((item, index) => {
              const linkedProject =
                type === "PACS"
                  ? reportFindLinkedProject(item, projects)
                  : null;
              return (
                <tr key={item.id}>
                  <td>{index + 1}</td>
                  <td>
                    <b>{entityName(item)}</b>
                    <small>{upper(item.role).replaceAll("_", " ")}</small>
                  </td>
                  <td>
                    <b>
                      {firstRecordValue(item, [
                        "pacsRegistrationNo",
                        "pacsRegistrationNumber",
                        "registrationNumber",
                        "registrationNo",
                      ]) || item.id}
                    </b>
                  </td>
                  {type === "PACS" && (
                    <td>
                      <b>
                        {linkedProject?.applicationId ||
                          linkedProject?.id ||
                          "Not Applied"}
                      </b>
                      <small>
                        {linkedProject
                          ? `${formatCapacity(
                              projectCapacity(linkedProject),
                            )} · ${(
                              canonicalStage(linkedProject) || "APPLIED"
                            ).replaceAll("_", " ")}`
                          : "No linked application"}
                      </small>
                    </td>
                  )}
                  <td>
                    <b>
                      {firstRecordValue(linkedProject || item, [
                        "state",
                        "stateName",
                        "projectState",
                      ]) ||
                        stateName ||
                        "All India"}
                    </b>
                    <small>
                      {[
                        reportExportValue(item, linkedProject || {}, {}, [
                          "district",
                          "districtName",
                          "projectDistrict",
                        ]),
                        reportExportValue(item, linkedProject || {}, {}, [
                          "taluka",
                          "talukaName",
                          "block",
                        ]),
                        reportExportValue(item, linkedProject || {}, {}, [
                          "village",
                          "villageName",
                        ]),
                      ]
                        .filter(Boolean)
                        .join(" · ") ||
                        (item.serviceStates || []).join(", ") ||
                        "-"}
                    </small>
                  </td>
                  <td>
                    <b>
                      {firstRecordValue(item, [
                        "mobile",
                        "phone",
                        "mobileNumber",
                        "pacsMobile",
                        "contactMobile",
                        "contactNumber",
                      ]) || "-"}
                    </b>
                    <small>
                      {firstRecordValue(item, [
                        "email",
                        "officialEmail",
                        "pacsEmail",
                        "contactEmail",
                      ]) || "-"}
                    </small>
                  </td>
                  <td>
                    <span
                      className={`reg-badge ${
                        statusOf(item).includes("APPROVED")
                          ? "approved"
                          : statusOf(item).includes("ALLOCATED")
                            ? "allocated"
                            : ""
                      }`}
                    >
                      {statusOf(item).replaceAll("_", " ")}
                    </span>
                  </td>
                  <td>
                    {reportDateText(
                      item.createdAt || item.registeredAt || item.updatedAt,
                    ) || "-"}
                  </td>
                  {type === "PACS" && (
                    <td>
                      {duplicateIdsFor(item).length ? (
                        <div className="duplicate-action">
                          <button
                            type="button"
                            disabled={cleanupBusyId === item.id}
                            onClick={() => archiveDuplicateProfiles(item)}
                          >
                            {cleanupBusyId === item.id
                              ? "Archiving..."
                              : `Archive ${duplicateIdsFor(item).length} Duplicate`}
                          </button>
                          <small>
                            Canonical: {canonicalProfileId(item).slice(0, 12)}…
                          </small>
                        </div>
                      ) : (
                        <span className="canonical-record">Canonical Record</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          {type === "PACS" && (
            <tfoot>
              <tr>
                <td colSpan="3">GRAND TOTAL · {reportScopeLabel}</td>
                <td>{formatCapacity(totalAppliedCapacity)}</td>
                <td colSpan="5">{visible.length} PACS RECORDS</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </section>
  );
}

export default function StateBranchDashboard() {
  const [tab, setTab] = useState("home");
  const [branchState, setBranchState] = useState("Gujarat");
  const [profile, setProfile] = useState(null);
  const [data, setData] = useState(null);
  const [approvedBranchPmcs, setApprovedBranchPmcs] = useState([]);
  const [stateRegistrars, setStateRegistrars] = useState([]);
  const [statePacsProjects, setStatePacsProjects] = useState([]);
  const [mouProjects, setMouProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registrarBusy, setRegistrarBusy] = useState("");
  const [registrarMessage, setRegistrarMessage] = useState("");
  const [registrarSearch, setRegistrarSearch] = useState("");
  const [registrarStatusFilter, setRegistrarStatusFilter] = useState("ALL");
  const [selectedRegistrarId, setSelectedRegistrarId] = useState("");

  const [liveProjects, setLiveProjects] = useState([]);
  const [liveUsers, setLiveUsers] = useState([]);
  const [liveAllocations, setLiveAllocations] = useState([]);
  const [lastLiveUpdate, setLastLiveUpdate] = useState(null);
  const [liveError, setLiveError] = useState("");
  const legacyPmcNameSyncRef = useRef(false);

  // Real-time listeners. No manual refresh is needed and both listeners are
  // always cleaned up when the logged-in branch/state changes.
  useEffect(() => {
    if (!branchState) return undefined;
    setLiveError("");
    const handleError = (error) => {
      console.error("State Branch live listener failed:", error);
      setLiveError(error?.message || "Live update connection failed.");
      setLoading(false);
    };
    const stopProjects = onSnapshot(
      collection(db, "godown_projects"),
      (snapshot) => {
        setLiveProjects(
          snapshot.docs.map((item) => ({ id: item.id, ...item.data() })),
        );
        setLastLiveUpdate(new Date());
        setLoading(false);
      },
      handleError,
    );
    const stopUsers = onSnapshot(
      collection(db, "nafed_users"),
      (snapshot) => {
        setLiveUsers(
          snapshot.docs.map((item) => ({ id: item.id, ...item.data() })),
        );
        setLastLiveUpdate(new Date());
        setLoading(false);
      },
      handleError,
    );
    const stopAllocations = onSnapshot(
      collection(db, "pacs_pmc_allocations"),
      (snapshot) => {
        setLiveAllocations(
          snapshot.docs.map((item) => ({ id: item.id, ...item.data() })),
        );
        setLastLiveUpdate(new Date());
      },
      handleError,
    );
    return () => {
      stopProjects();
      stopUsers();
      stopAllocations();
    };
  }, [branchState]);

  // A deleted PACS can be removed only from nafed_users while its old project
  // document remains. This linked-key set guarantees that such projects never
  // reappear in any State Branch module.
  const deletedPacsKeySet = useMemo(() => {
    const keys = new Set();
    liveUsers
      .filter(
        (user) => upper(user.role).includes("PACS") && isDeletedProject(user),
      )
      .forEach((user) =>
        pacsKeys(user)
          .concat(normalise(user.id))
          .forEach((key) => keys.add(key)),
      );
    return keys;
  }, [liveUsers]);

  useEffect(() => {
    const stateKey = normalise(branchState);
    const visibleUsers = liveUsers.filter((user) => !isDeletedProject(user));
    const registrarRecords = visibleUsers
      .filter(
        (user) =>
          ["STATE_REGISTRAR", "STATE_REG"].includes(upper(user.role)) &&
          normalise(user.state || user.stateName) === stateKey,
      )
      .sort(
        (a, b) =>
          Number(b.updatedAt?.seconds || b.createdAt?.seconds || 0) -
          Number(a.updatedAt?.seconds || a.createdAt?.seconds || 0),
      );
    const statePacsKeySet = new Set();
    visibleUsers
      .filter(
        (user) =>
          upper(user.role).includes("PACS") &&
          normalise(user.state || user.stateName) === stateKey,
      )
      .forEach((user) => {
        [user.id, user.uid, ...pacsKeys(user)]
          .filter(Boolean)
          .forEach((key) => statePacsKeySet.add(normalise(key)));
      });

    const approvedPmcs = dedupePmcRecords(
      visibleUsers.filter(
        (pmc) =>
          ["PMC", "PMC_AGENCY"].includes(upper(pmc.role)) &&
          (normalise(pmc.state || pmc.stateName) === stateKey ||
            (Array.isArray(pmc.serviceStates) &&
              pmc.serviceStates.some(
                (state) => normalise(state) === stateKey,
              )) ||
            (Array.isArray(pmc.states) &&
              pmc.states.some((state) => normalise(state) === stateKey))) &&
          [
            pmc.status,
            pmc.registrationStatus,
            pmc.approvalStatus,
            pmc.shineAgriApprovalStatus,
          ].some((value) => upper(value) === "APPROVED") &&
          (!pmc.branchApprovalStatus ||
            upper(pmc.branchApprovalStatus) === "APPROVED") &&
          (!pmc.roleApprovalStatus ||
            upper(pmc.roleApprovalStatus) === "APPROVED") &&
          pmc.isActive !== false,
      ),
    ).sort((a, b) =>
      String(a.organizationName || a.agencyName || a.name || "").localeCompare(
        String(b.organizationName || b.agencyName || b.name || ""),
      ),
    );

    const records = liveProjects
      .filter((project) => {
        const projectPacsKeys = pacsKeys(project);
        const linkedPacsWasDeleted = projectPacsKeys.some((key) =>
          deletedPacsKeySet.has(key),
        );
        const linkedToStatePacs = projectPacsKeys.some((key) =>
          statePacsKeySet.has(key),
        );
        return (
          !isDeletedProject(project) &&
          !linkedPacsWasDeleted &&
          isNafedProject(project) &&
          (normalise(projectState(project)) === stateKey || linkedToStatePacs)
        );
      })
      .sort(
        (a, b) =>
          toMillis(b.updatedAt || b.createdAt || b.submittedAt) -
          toMillis(a.updatedAt || a.createdAt || a.submittedAt),
      );

    setStateRegistrars(registrarRecords);
    setApprovedBranchPmcs(approvedPmcs);
    setStatePacsProjects(records);
    setMouProjects(
      records.filter((project) => {
        const stage = upper(project.mouStage || project.mouStatus);
        return (
          [
            "PENDING_STATE_BRANCH_SIGNATURE",
            "PENDING_PACS_SIGNATURE",
            "FULLY_EXECUTED",
          ].includes(stage) ||
          (stage === "PENDING_PMC_SIGNATURE" &&
            (upper(project.mouReviewStatus) === "RETURNED_BY_STATE_BRANCH" ||
              upper(project.mouStateBranchDecision) === "REJECTED" ||
              Boolean(project.mouStateBranchRejectionReason)))
        );
      }),
    );
  }, [branchState, liveProjects, liveUsers, deletedPacsKeySet]);

  const loadDashboard = async (stateName = branchState) => {
    try {
      setLoading(true);
      const res = await getNafedDashboardData({
        onlyNafed: true,
        state: stateName,
      });
      setData(res);
    } catch (err) {
      console.error("State Branch Dashboard loading failed:", err);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      try {
        let stateName = "Gujarat";

        if (user?.uid) {
          const snap = await getDoc(doc(db, "nafed_users", user.uid));
          if (snap.exists()) {
            const p = { id: snap.id, ...snap.data() };
            setProfile(p);
            if (p.state) stateName = p.state;
            setBranchState(stateName);
          }
        }

        await loadDashboard(stateName);
      } catch (e) {
        console.error("Branch profile loading failed:", e);
        await loadDashboard("Gujarat");
      }
    });

    return () => unsub();
  }, []);

  // Dashboard-engine results are sanitized again here. This prevents deleted
  // records from leaking into old counters, news, charts or recent tables.
  const enginePmcs = (data?.pmcs || []).filter(
    (item) => !isDeletedProject(item),
  );
  const engineUsers = (data?.users || []).filter(
    (item) => !isDeletedProject(item),
  );
  const pmcs = enginePmcs;
  const users = engineUsers;
  const recentProjects = statePacsProjects.slice(0, 12);
  const notifications = (data?.notifications || []).filter(
    (item) => !isDeletedProject(item),
  );
  const status = useMemo(() => {
    const result = {};
    statePacsProjects.forEach((project) => {
      const key = canonicalStage(project);
      result[key] = (result[key] || 0) + 1;
    });
    return result;
  }, [statePacsProjects]);

  const k = useMemo(() => {
    const pacs = new Set();
    let totalCapacity = 0;
    let totalInvestment = 0;
    statePacsProjects.forEach((project) => {
      const key = pacsKeys(project)[0] || normalise(projectName(project));
      if (key) pacs.add(key);
      totalCapacity += Number(projectCapacity(project) || 0);
      totalInvestment += Number(
        project.estimatedProjectCost ||
          project.projectCost ||
          project.tenderValue ||
          0,
      );
    });
    return {
      totalProjects: statePacsProjects.length,
      pacsInvolved: pacs.size,
      totalCapacity,
      totalInvestment,
    };
  }, [statePacsProjects]);

  const statePmcs = useMemo(
    () =>
      dedupePmcRecords(
        pmcs.filter(
          (p) =>
            !isDeletedProject(p) &&
            String(p.state || p.stateName || "").toLowerCase() ===
              String(branchState).toLowerCase(),
        ),
      ),
    [pmcs, branchState],
  );

  const branchUsers = useMemo(() => {
    return users.filter(
      (u) =>
        !isDeletedProject(u) &&
        String(u.state || "").toLowerCase() ===
          String(branchState).toLowerCase(),
    );
  }, [users, branchState]);

  const stateRegisteredPacs = useMemo(
    () =>
      dedupePacsRecords(
        liveUsers.filter(
          (user) =>
            !isDeletedProject(user) &&
            upper(user.role).includes("PACS") &&
            normalise(user.state || user.stateName) === normalise(branchState),
        ),
      ),
    [liveUsers, branchState],
  );
  const stateRegisteredPmcs = useMemo(
    () =>
      dedupePmcRecords(
        liveUsers.filter(
          (user) =>
            !isDeletedProject(user) &&
            ["PMC", "PMC_AGENCY"].includes(upper(user.role)) &&
            (normalise(user.state || user.stateName) ===
              normalise(branchState) ||
              (Array.isArray(user.serviceStates) &&
                user.serviceStates.some(
                  (state) => normalise(state) === normalise(branchState),
                )) ||
              (Array.isArray(user.states) &&
                user.states.some(
                  (state) => normalise(state) === normalise(branchState),
                ))),
        ),
      ),
    [liveUsers, branchState],
  );
  const stateApprovedPacs = useMemo(
    () =>
      stateRegisteredPacs.filter(
        (user) =>
          [
            user.status,
            user.roleApprovalStatus,
            user.registrationStatus,
            user.branchApprovalStatus,
          ].some((value) => upper(value) === "APPROVED") &&
          user.isActive !== false,
      ),
    [stateRegisteredPacs],
  );
  const stateAllocations = useMemo(() => {
    const statePacsIds = new Set(
      stateRegisteredPacs.flatMap((pacs) => [
        ...allocationPacsKeys(pacs),
        ...pacsDedupeKeys(pacs),
      ]),
    );
    return liveAllocations.filter(
      (allocation) =>
        normalise(allocation.state || allocation.stateName) ===
          normalise(branchState) ||
        [...allocationPacsKeys(allocation), ...pacsDedupeKeys(allocation)].some(
          (key) => statePacsIds.has(key),
        ),
    );
  }, [liveAllocations, stateRegisteredPacs, branchState]);

  // Keep every active allocation linked to the latest approved PMC profile.
  // If a PMC changes its organisation name/contact, legacy allocation,
  // PACS-profile and project fields are refreshed without deleting records.
  useEffect(() => {
    const seenAllocationPacsKeys = new Set();
    const latestActiveAllocations = stateAllocations
      .filter(isActivePmcAllocation)
      .sort(
        (a, b) =>
          toMillis(b.updatedAt || b.allocatedAt || b.createdAt) -
          toMillis(a.updatedAt || a.allocatedAt || a.createdAt),
      )
      .filter((allocation) => {
        const keys = [
          ...allocationPacsKeys(allocation),
          ...pacsDedupeKeys(allocation),
        ];
        if (keys.some((key) => seenAllocationPacsKeys.has(key))) return false;
        keys.forEach((key) => seenAllocationPacsKeys.add(key));
        return true;
      });
    const staleAllocations = latestActiveAllocations
      .map((allocation) => ({
        allocation,
        currentPmc: resolveCurrentPmc(allocation, approvedBranchPmcs),
      }))
      .filter(({ allocation, currentPmc }) => {
        if (!currentPmc) return false;
        return (
          normalise(
            allocation.allocatedPmcUid ||
              allocation.allocatedPmcId ||
              allocation.pmcUid ||
              allocation.pmcId,
          ) !== normalise(currentPmc.id) ||
          normalise(allocation.allocatedPmcName || allocation.pmcName) !==
            normalise(entityName(currentPmc)) ||
          normalise(allocation.allocatedPmcEmail || allocation.pmcEmail) !==
            normalise(
              firstRecordValue(currentPmc, [
                "email",
                "officialEmail",
                "contactEmail",
              ]),
            ) ||
          normalise(allocation.allocatedPmcMobile || allocation.pmcMobile) !==
            normalise(
              firstRecordValue(currentPmc, [
                "mobile",
                "phone",
                "contactMobile",
                "mobileNumber",
              ]),
            )
        );
      });

    if (!staleAllocations.length) return;
    let cancelled = false;
    const syncCurrentPmcProfiles = async () => {
      const syncRows = staleAllocations.map(({ allocation, currentPmc }) => {
        const currentName = entityName(currentPmc);
        const currentEmail = firstRecordValue(currentPmc, [
          "email",
          "officialEmail",
          "contactEmail",
        ]);
        const currentMobile = firstRecordValue(currentPmc, [
          "mobile",
          "phone",
          "contactMobile",
          "mobileNumber",
        ]);
        return {
          allocation,
          allocationKeys: new Set([
            ...allocationPacsKeys(allocation),
            ...pacsDedupeKeys(allocation),
          ]),
          fields: {
            allocatedPmcUid: currentPmc.id,
            allocatedPmcId: currentPmc.id,
            allocatedPmcName: currentName,
            allocatedPmcEmail: currentEmail,
            allocatedPmcMobile: currentMobile,
            pmcUid: currentPmc.id,
            pmcId: currentPmc.id,
            pmcName: currentName,
            pmcEmail: currentEmail,
            pmcMobile: currentMobile,
            pmcProfileSyncedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
        };
      });

      try {
        const allocationBatch = writeBatch(db);
        syncRows.forEach(({ allocation, fields }) => {
          allocationBatch.set(
            doc(db, "pacs_pmc_allocations", allocation.id),
            fields,
            { merge: true },
          );
        });
        await allocationBatch.commit();
      } catch (error) {
        if (!cancelled) {
          console.warn("Official allocation PMC-name sync skipped:", error);
        }
        return;
      }

      if (cancelled) return;
      try {
        const profileBatch = writeBatch(db);
        const queuedProfiles = new Set();
        syncRows.forEach(({ allocationKeys, fields }) => {
          stateRegisteredPacs
            .filter((pacs) =>
              [...allocationPacsKeys(pacs), ...pacsDedupeKeys(pacs)].some(
                (key) => allocationKeys.has(key),
              ),
            )
            .forEach((pacs) => {
              if (!pacs.id || queuedProfiles.has(pacs.id)) return;
              queuedProfiles.add(pacs.id);
              profileBatch.set(doc(db, "nafed_users", pacs.id), fields, {
                merge: true,
              });
            });
        });
        if (queuedProfiles.size) await profileBatch.commit();
      } catch (error) {
        if (!cancelled) {
          console.warn("PACS profile PMC-name sync skipped:", error);
        }
      }

      if (cancelled) return;
      try {
        const projectBatch = writeBatch(db);
        const queuedProjects = new Set();
        syncRows.forEach(({ allocationKeys, fields }) => {
          statePacsProjects
            .filter((project) =>
              [...allocationPacsKeys(project), ...pacsDedupeKeys(project)].some(
                (key) => allocationKeys.has(key),
              ),
            )
            .forEach((project) => {
              if (!project.id || queuedProjects.has(project.id)) return;
              queuedProjects.add(project.id);
              projectBatch.set(doc(db, "godown_projects", project.id), fields, {
                merge: true,
              });
            });
        });
        if (queuedProjects.size) await projectBatch.commit();
      } catch (error) {
        if (!cancelled) {
          console.warn("Linked application PMC-name sync skipped:", error);
        }
      }
    };
    syncCurrentPmcProfiles();
    return () => {
      cancelled = true;
    };
  }, [
    approvedBranchPmcs,
    stateAllocations,
    stateRegisteredPacs,
    statePacsProjects,
  ]);

  /*
   * One-time compatibility backfill for the renamed PMC. The UI already uses
   * the live PMC master name through resolveCurrentPmc(); these writes also
   * remove the historical "Sun Shine Overseas" label from active allocation,
   * PACS-profile and linked-project reference fields in Firestore. No record
   * or workflow status is deleted/changed.
   */
  useEffect(() => {
    if (legacyPmcNameSyncRef.current) return;
    const currentShineAgriPmc = approvedBranchPmcs.find(
      (pmc) =>
        canonicalPmcNameKey(entityName(pmc)) ===
        "shine-agri-allied-current-profile",
    );
    if (!currentShineAgriPmc) return;

    const currentUid = currentShineAgriPmc.id;
    const referencesRenamedPmc = (record = {}) => {
      const referencedUid = firstRecordValue(record, [
        "allocatedPmcUid",
        "allocatedPmcId",
        "assignedPmcUid",
        "assignedPmcId",
        "pmcUid",
        "pmcId",
      ]);
      return (
        hasLegacyShineAgriReference(record) ||
        (normalise(referencedUid) === normalise(currentUid) &&
          collectRecordValues(record, [
            "allocatedPmcName",
            "assignedPmcName",
            "pmcName",
            "pmcAgencyName",
          ]).some(isLegacyShineAgriPmcName))
      );
    };

    const staleAllocations = stateAllocations.filter(referencesRenamedPmc);
    const staleProfiles = liveUsers.filter(
      (user) =>
        !isDeletedProject(user) &&
        upper(user.role).includes("PACS") &&
        normalise(user.state || user.stateName) === normalise(branchState) &&
        referencesRenamedPmc(user),
    );
    const staleProjects = statePacsProjects.filter(referencesRenamedPmc);
    const stalePmcMasterFields = Object.fromEntries(
      ["pmcName", "agencyName", "companyName"].flatMap((field) =>
        isLegacyShineAgriPmcName(currentShineAgriPmc[field])
          ? [[field, entityName(currentShineAgriPmc)]]
          : [],
      ),
    );
    if (
      !staleAllocations.length &&
      !staleProfiles.length &&
      !staleProjects.length &&
      !Object.keys(stalePmcMasterFields).length
    ) {
      return;
    }

    const currentName = entityName(currentShineAgriPmc);
    const currentEmail = firstRecordValue(currentShineAgriPmc, [
      "email",
      "officialEmail",
      "contactEmail",
    ]);
    const currentMobile = firstRecordValue(currentShineAgriPmc, [
      "mobile",
      "phone",
      "contactMobile",
      "mobileNumber",
    ]);
    const currentFields = {
      allocatedPmcUid: currentUid,
      allocatedPmcId: currentUid,
      allocatedPmcName: currentName,
      allocatedPmcEmail: currentEmail,
      allocatedPmcMobile: currentMobile,
      assignedPmcUid: currentUid,
      assignedPmcId: currentUid,
      assignedPmcName: currentName,
      pmcUid: currentUid,
      pmcId: currentUid,
      pmcName: currentName,
      pmcAgencyName: currentName,
      pmcOrganizationName: currentName,
      pmcEmail: currentEmail,
      pmcMobile: currentMobile,
      pmcMasterNameSyncedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    legacyPmcNameSyncRef.current = true;
    const syncRecords = async (collectionName, records) => {
      if (!records.length) return 0;
      try {
        const batch = writeBatch(db);
        records.forEach((record) => {
          if (!record.id) return;
          batch.set(doc(db, collectionName, record.id), currentFields, {
            merge: true,
          });
        });
        await batch.commit();
        return records.filter((record) => record.id).length;
      } catch (error) {
        console.warn(`${collectionName} PMC-name sync skipped:`, error);
        return 0;
      }
    };

    const runLegacyNameSync = async () => {
      try {
        const allocationRecordsUpdated = await syncRecords(
          "pacs_pmc_allocations",
          staleAllocations,
        );
        const pacsProfilesUpdated = await syncRecords(
          "nafed_users",
          staleProfiles,
        );
        const projectRecordsUpdated = await syncRecords(
          "godown_projects",
          staleProjects,
        );
        let pmcMasterFieldsUpdated = 0;
        if (Object.keys(stalePmcMasterFields).length) {
          try {
            const masterBatch = writeBatch(db);
            masterBatch.set(
              doc(db, "nafed_users", currentUid),
              {
                ...stalePmcMasterFields,
                pmcMasterNameSyncedAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              },
              { merge: true },
            );
            await masterBatch.commit();
            pmcMasterFieldsUpdated = Object.keys(stalePmcMasterFields).length;
          } catch (masterError) {
            console.warn("PMC master alias cleanup skipped:", masterError);
          }
        }
        try {
          await addDoc(collection(db, "nafed_activity"), {
            action: "STATE_BRANCH_PMC_LEGACY_NAME_SYNCED",
            targetUid: currentUid,
            targetRole: "PMC",
            targetName: currentName,
            previousName: "Sun Shine Overseas",
            allocationRecordsUpdated,
            pacsProfilesUpdated,
            projectRecordsUpdated,
            pmcMasterFieldsUpdated,
            state: branchState,
            performedByUid: auth.currentUser?.uid || "",
            performedByRole: "STATE_BRANCH",
            createdAt: serverTimestamp(),
          });
        } catch (auditError) {
          console.warn("PMC legacy-name sync audit skipped:", auditError);
        }
      } catch (error) {
        console.warn("PMC legacy-name backfill skipped:", error);
      } finally {
        legacyPmcNameSyncRef.current = false;
      }
    };
    runLegacyNameSync();
  }, [
    approvedBranchPmcs,
    branchState,
    liveUsers,
    stateAllocations,
    statePacsProjects,
  ]);

  const allocatedPacsKeySet = useMemo(() => {
    const keys = new Set();
    stateAllocations.filter(isActivePmcAllocation).forEach((allocation) => {
      [
        ...allocationPacsKeys(allocation),
        ...pacsDedupeKeys(allocation),
      ].forEach((key) => keys.add(key));
    });
    stateRegisteredPacs.forEach((pacs) => {
      if (hasPmcReference(pacs)) {
        [...allocationPacsKeys(pacs), ...pacsDedupeKeys(pacs)].forEach((key) =>
          keys.add(key),
        );
      }
    });
    // Older portal builds sometimes saved the PMC only on the application.
    // Include those links in the branch KPI without mutating the project.
    statePacsProjects.forEach((project) => {
      if (hasPmcReference(project)) {
        [...allocationPacsKeys(project), ...pacsDedupeKeys(project)].forEach(
          (key) => keys.add(key),
        );
      }
    });
    return keys;
  }, [stateAllocations, stateRegisteredPacs, statePacsProjects]);
  const unallocatedRegisteredPacs = useMemo(
    () =>
      stateRegisteredPacs.filter(
        (pacs) =>
          ![...allocationPacsKeys(pacs), ...pacsDedupeKeys(pacs)].some((key) =>
            allocatedPacsKeySet.has(key),
          ),
      ),
    [stateRegisteredPacs, allocatedPacsKeySet],
  );
  const allocatedPacsCount = useMemo(
    () =>
      stateRegisteredPacs.filter((pacs) =>
        [...allocationPacsKeys(pacs), ...pacsDedupeKeys(pacs)].some((key) =>
          allocatedPacsKeySet.has(key),
        ),
      ).length,
    [stateRegisteredPacs, allocatedPacsKeySet],
  );
  const stateRegisteredVendors = useMemo(
    () =>
      liveUsers.filter((user) => {
        if (
          isDeletedProject(user) ||
          !["VENDOR", "TECHNICAL_VENDOR", "WORKFLOW_VENDOR"].includes(
            upper(user.role),
          )
        )
          return false;
        const states = Array.isArray(user.serviceStates)
          ? user.serviceStates
          : Array.isArray(user.states)
            ? user.states
            : [];
        return (
          (!user.state && !states.length) ||
          normalise(user.state || user.stateName) === normalise(branchState) ||
          states.some((state) => normalise(state) === normalise(branchState))
        );
      }),
    [liveUsers, branchState],
  );

  const pendingPmcs = statePmcs.filter(
    (p) =>
      (p.branchApprovalStatus || p.status || "PENDING").toUpperCase() ===
      "PENDING",
  ).length;

  const approvedPmcs = statePmcs.filter(
    (p) =>
      (p.branchApprovalStatus || p.status || "").toUpperCase() === "APPROVED",
  ).length;

  const rejectedPmcs = statePmcs.filter(
    (p) =>
      (p.branchApprovalStatus || p.status || "").toUpperCase() === "REJECTED",
  ).length;

  const currentPmcNameFor = (reference = {}) => {
    const currentPmc = resolveCurrentPmc(reference, approvedBranchPmcs);
    return currentPmc
      ? entityName(currentPmc)
      : reference.allocatedPmcName || reference.pmcName || "Not allocated";
  };

  const pendingUsers = branchUsers.filter(
    (u) =>
      u.status === "PENDING_SHINE_AGRI" ||
      u.status === "PENDING_ROLE_APPROVAL" ||
      u.shineAgriVerificationStatus === "PENDING",
  ).length;

  const pendingStateRegistrars = stateRegistrars.filter((item) =>
    String(item.status || item.approvalStatus || "PENDING")
      .toUpperCase()
      .includes("PENDING"),
  ).length;
  const approvedStateRegistrars = stateRegistrars.filter(
    (item) =>
      String(item.status || item.approvalStatus || "").toUpperCase() ===
      "APPROVED",
  ).length;
  const queryStateRegistrars = stateRegistrars.filter((item) =>
    String(item.status || item.approvalStatus || "")
      .toUpperCase()
      .includes("QUERY"),
  ).length;
  const rejectedStateRegistrars = stateRegistrars.filter((item) =>
    String(item.status || item.approvalStatus || "")
      .toUpperCase()
      .includes("REJECT"),
  ).length;

  const visibleStateRegistrars = stateRegistrars.filter((item) => {
    const statusValue = String(
      item.status || item.approvalStatus || "PENDING",
    ).toUpperCase();
    const haystack = [
      item.name,
      item.officerName,
      item.organizationName,
      item.email,
      item.mobile,
      item.registrationNumber,
      item.registrationNo,
    ]
      .join(" ")
      .toLowerCase();
    return (
      haystack.includes(registrarSearch.trim().toLowerCase()) &&
      (registrarStatusFilter === "ALL" ||
        statusValue.includes(registrarStatusFilter))
    );
  });
  const selectedRegistrar =
    stateRegistrars.find((item) => item.id === selectedRegistrarId) ||
    visibleStateRegistrars[0] ||
    null;

  const saveRegistrarAudit = async (action, registrar, remarks = "") => {
    await addDoc(collection(db, "nafed_activity"), {
      action,
      targetUid: registrar.id,
      targetRole: "STATE_REGISTRAR",
      targetName:
        registrar.name ||
        registrar.officerName ||
        registrar.organizationName ||
        registrar.email ||
        "State Registrar",
      state: registrar.state || branchState,
      performedByUid: auth.currentUser?.uid || "",
      performedByRole: "STATE_BRANCH",
      performedByName:
        profile?.name ||
        profile?.officerName ||
        profile?.organizationName ||
        profile?.email ||
        "NAFED State Branch",
      remarks,
      createdAt: serverTimestamp(),
    });
  };

  const handleStateRegistrarAction = async (action, registrar) => {
    if (!registrar) return;
    let remarks = "";
    if (action === "QUERY" || action === "REJECT") {
      remarks =
        window.prompt(
          action === "QUERY"
            ? "Enter required document/detail query:"
            : "Enter complete rejection reason:",
          "",
        ) || "";
      if (!remarks.trim()) return;
    }
    const confirmText =
      action === "APPROVE"
        ? "Approve this State Registrar and enable login?"
        : action === "QUERY"
          ? "Send this query to the State Registrar?"
          : "Reject this State Registrar registration?";
    if (!window.confirm(confirmText)) return;

    const nextStatus =
      action === "APPROVE"
        ? "APPROVED"
        : action === "QUERY"
          ? "QUERY_RAISED_BY_STATE_BRANCH"
          : "REJECTED_BY_STATE_BRANCH";

    try {
      setRegistrarBusy(registrar.id);
      setRegistrarMessage("");
      const changes = {
        status: nextStatus,
        approvalStatus: nextStatus,
        roleApprovalStatus:
          action === "APPROVE"
            ? "APPROVED"
            : action === "QUERY"
              ? "QUERY_RAISED"
              : "REJECTED",
        branchApprovalStatus:
          action === "APPROVE"
            ? "APPROVED"
            : action === "QUERY"
              ? "QUERY_RAISED"
              : "REJECTED",
        stateBranchQuery: action === "QUERY" ? remarks : "",
        stateBranchRejectionReason: action === "REJECT" ? remarks : "",
        approvedByUid:
          action === "APPROVE"
            ? auth.currentUser?.uid || ""
            : registrar.approvedByUid || "",
        approvedByRole:
          action === "APPROVE"
            ? "STATE_BRANCH"
            : registrar.approvedByRole || "",
        approvedAt:
          action === "APPROVE"
            ? serverTimestamp()
            : registrar.approvedAt || null,
        stateBranchActionByUid: auth.currentUser?.uid || "",
        stateBranchActionAt: serverTimestamp(),
        isActive: action === "APPROVE",
        updatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, "nafed_users", registrar.id), changes);
      await saveRegistrarAudit(
        `STATE_BRANCH_STATE_REGISTRAR_${action}`,
        registrar,
        remarks,
      );
      setStateRegistrars((current) =>
        current.map((item) =>
          item.id === registrar.id ? { ...item, ...changes } : item,
        ),
      );
      setRegistrarMessage(
        action === "APPROVE"
          ? "State Registrar approved successfully. Login access is enabled."
          : action === "QUERY"
            ? "Query sent to State Registrar."
            : "State Registrar registration rejected.",
      );
    } catch (error) {
      console.error("State Registrar approval failed:", error);
      setRegistrarMessage(
        error?.message || "Action failed. Check Firestore permissions.",
      );
    } finally {
      setRegistrarBusy("");
    }
  };

  const totalInvestment = k.totalInvestment || 0;

  const overviewKpis = [
    [
      "Total Projects",
      formatMetricNumber(k.totalProjects || 0),
      `${branchState} Projects`,
      "#18b76b",
    ],
    [
      "Total Capacity",
      formatCapacity(k.totalCapacity || 0),
      "Storage Capacity",
      "#1685d8",
    ],
    [
      "Registered PACS",
      formatMetricNumber(stateRegisteredPacs.length),
      `${k.pacsInvolved || 0} applications`,
      "#8b5cf6",
    ],
    [
      "Approved PMCs",
      formatMetricNumber(approvedBranchPmcs.length),
      "By State Branch",
      "#10b981",
    ],
    [
      "Technical Vendors",
      formatMetricNumber(stateRegisteredVendors.length),
      "Shine Agri register",
      "#ef476f",
    ],
    [
      "Investment",
      formatCurrency(totalInvestment),
      "Estimated / Tender",
      "#6366f1",
    ],
  ];

  const openModule = (item) => {
    if (!item) return;
    if (item.path) {
      window.location.href = item.path;
      return;
    }
    setTab(item.key);
  };

  const logout = async () => {
    await signOut(auth);
    window.location.href = "/nafed";
  };

  const commandKpis = [
    {
      label: "Total Projects",
      value: formatMetricNumber(k.totalProjects || 0),
      sub: `${branchState} Projects`,
      icon: "projects",
      tone: "green",
    },
    {
      label: "Total Capacity",
      value: formatCapacity(k.totalCapacity || 0),
      sub: "Storage Capacity",
      icon: "construction",
      tone: "blue",
    },
    {
      label: "Registered PACS",
      value: formatMetricNumber(stateRegisteredPacs.length),
      sub: "With Applications",
      icon: "pacs",
      tone: "purple",
    },
    {
      label: "Allocated PACS",
      value: formatMetricNumber(allocatedPacsCount),
      sub: "To Approved PMCs",
      icon: "allocation",
      tone: "orange",
    },
    {
      label: "Approved PMCs",
      value: formatMetricNumber(approvedBranchPmcs.length),
      sub: "PMC Agencies",
      icon: "approvals",
      tone: "green",
    },
    {
      label: "Awaiting Allocation",
      value: formatMetricNumber(unallocatedRegisteredPacs.length),
      sub: "PACS Pending",
      icon: "allocation",
      tone: unallocatedRegisteredPacs.length ? "red" : "green",
    },
  ];

  const rawWorkflow = [
    ["Submission", stateRegisteredPacs.length],
    ["PACS Verification", stateApprovedPacs.length],
    ["PMC Approval", approvedBranchPmcs.length],
    ["PACS–PMC Allocation", allocatedPacsCount],
    ["DPR Preparation", status.DPR || 0],
    ["Tender Process", status.TENDER || 0],
    ["Construction Progress", status.CONSTRUCTION || 0],
    [
      "RA Bills & Payments",
      statePacsProjects.filter((project) =>
        upper(
          project.currentStage ||
            project.stage ||
            project.workflowStage ||
            project.status,
        ).includes("RA_BILL"),
      ).length,
    ],
    ["Project Completion", status.COMPLETED || 0],
  ];
  let activeWorkflowIndex = 0;
  rawWorkflow.forEach(([, value], index) => {
    if (Number(value) > 0) activeWorkflowIndex = index;
  });
  if (unallocatedRegisteredPacs.length > 0 && activeWorkflowIndex < 3) {
    activeWorkflowIndex = 3;
  }
  const workflowStages = rawWorkflow.map(([label, value], index) => ({
    label,
    value,
    state:
      index < activeWorkflowIndex
        ? "complete"
        : index === activeWorkflowIndex
          ? "active"
          : "pending",
  }));

  const activityRows = recentProjects.slice(0, 5).map((project) => {
    const stage = canonicalStage(project);
    const stageLabels = {
      APPLIED: "Application submitted",
      FIELD_VISIT: "Field visit updated",
      DPR: "DPR submitted",
      TENDER: "Tender process updated",
      LOA: "Work order updated",
      CONSTRUCTION: "Construction progress updated",
      COMPLETED: "Project completed",
    };
    const changedAt = toMillis(
      project.updatedAt || project.createdAt || project.submittedAt,
    );
    return {
      id: project.id,
      time: changedAt
        ? new Date(changedAt).toLocaleTimeString("en-IN", {
            hour: "2-digit",
            minute: "2-digit",
          })
        : "Live",
      activity: stageLabels[stage] || "Project updated",
      entity: projectName(project),
      location: project.district || project.districtName || branchState,
      complete: stage === "COMPLETED",
    };
  });

  const priorityActions = [
    {
      label: "MOU awaiting PACS final signature",
      value: mouProjects.filter(
        (project) => [
          "PENDING_PACS_SIGNATURE",
          "PENDING_STATE_BRANCH_SIGNATURE",
        ].includes(upper(project.mouStage || project.mouStatus)),
      ).length,
      icon: "documents",
      tone: "blue",
      module: MODULES.find((item) => item.key === "mouExecution"),
    },
    {
      label: "PACS–PMC allocation pending",
      value: unallocatedRegisteredPacs.length,
      icon: "allocation",
      tone: "orange",
      module: MODULES.find((item) => item.key === "pacsAllocation"),
    },
    {
      label: "DPRs pending review",
      value: status.DPR || 0,
      icon: "dpr",
      tone: "blue",
      module: MODULES.find((item) => item.key === "dpr"),
    },
    {
      label: "Tenders pending approval",
      value: status.TENDER || 0,
      icon: "tender",
      tone: "purple",
      module: MODULES.find((item) => item.key === "tender"),
    },
    {
      label: "RA bills pending payment",
      value: rawWorkflow[7][1],
      icon: "bills",
      tone: "green",
      module: MODULES.find((item) => item.key === "raBills"),
    },
    {
      label: "State Registrar approvals",
      value: pendingStateRegistrars,
      icon: "approvals",
      tone: "red",
      module: MODULES.find((item) => item.key === "stateRegistrarApproval"),
    },
  ];

  const quickAccess = [
    ["pacsDocuments", "PACS Documents", "documents", "blue"],
    ["mouExecution", "MOU Status & Signature", "documents", "red"],
    ["pmcApproval", "PMC Approval", "approvals", "green"],
    ["pacsAllocation", "PACS–PMC Allocation", "allocation", "orange"],
    ["hiringAssurance", "Hiring Assurance", "documents", "green"],
    ["dpr", "DPR", "dpr", "purple"],
    ["tender", "Tender", "tender", "blue"],
    ["construction", "Construction", "construction", "green"],
    ["raBills", "RA Bills", "bills", "red"],
    ["mis", "MIS Reports", "monitoring", "blue"],
  ].map(([key, label, icon, tone]) => ({
    key,
    label,
    icon,
    tone,
    module: MODULES.find((item) => item.key === key),
  }));

  const renderOverview = () => (
    <div className="overview-space">
      <section className="overview-kpis">
        {overviewKpis.map(([label, value, sub, color], index) => (
          <StatCard
            key={label}
            label={label}
            value={value}
            sub={sub}
            color={color}
            index={index}
          />
        ))}
      </section>

      <section className="analytics-grid">
        <div className="panel">
          <div className="panel-head">
            <h3>Project Status Overview</h3>
            <span>Live State Data</span>
          </div>

          <div className="donut-wrap">
            <div className="donut">
              <div className="donut-inner">
                <b>{formatMetricNumber(k.totalProjects || 0)}</b>
                <small>Projects</small>
              </div>
            </div>

            <div className="legend">
              {[
                ["Applied", status.APPLIED || 0, "#1189d8"],
                ["Field Visit", status.FIELD_VISIT || 0, "#f59e0b"],
                ["DPR", status.DPR || 0, "#31c77b"],
                ["Tender", status.TENDER || 0, "#ffb21b"],
                ["LOA", status.LOA || 0, "#8956d8"],
                ["Construction", status.CONSTRUCTION || 0, "#50c5b7"],
                ["Completed", status.COMPLETED || 0, "#6bd24a"],
              ].map(([title, value, color]) => (
                <div className="legend-row" key={title}>
                  <span>
                    <i style={{ background: color }} />
                    {title}
                  </span>
                  <b>{formatMetricNumber(value)}</b>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="panel">
          <div className="panel-head">
            <h3>PMC Approval Overview</h3>
            <span>State Branch Control</span>
          </div>

          {[
            [
              "Approved PMCs",
              approvedPmcs,
              "#22c55e",
              `${statePmcs.length ? (approvedPmcs / statePmcs.length) * 100 : 0}%`,
            ],
            [
              "Pending PMCs",
              pendingPmcs,
              "#f59e0b",
              `${statePmcs.length ? (pendingPmcs / statePmcs.length) * 100 : 0}%`,
            ],
            [
              "Rejected PMCs",
              rejectedPmcs,
              "#ef4444",
              `${statePmcs.length ? (rejectedPmcs / statePmcs.length) * 100 : 0}%`,
            ],
            [
              "Pending Users",
              pendingUsers,
              "#2196f3",
              `${branchUsers.length ? (pendingUsers / branchUsers.length) * 100 : 0}%`,
            ],
          ].map(([name, value, color, width]) => (
            <div className="bar-row" key={name}>
              <div>
                <span>{name}</span>
                <b>{formatMetricNumber(value)}</b>
              </div>
              <div className="bar-bg">
                <div className="bar" style={{ width, background: color }} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bottom-grid">
        <div className="panel wide">
          <div className="panel-head">
            <h3>Recent State Projects</h3>
            <span>{branchState}</span>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Application / Project</th>
                  <th>PACS / Institution</th>
                  <th>District</th>
                  <th>PMC</th>
                  <th>Capacity</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {recentProjects.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      style={{ textAlign: "center", padding: 30 }}
                    >
                      No NAFED projects found for {branchState}.
                    </td>
                  </tr>
                ) : (
                  recentProjects.map((p, i) => (
                    <tr key={p.id}>
                      <td>
                        <b>{p.projectName || p.applicationId || p.id}</b>
                      </td>
                      <td>{p.pacs || p.pacsName || p.societyName || "-"}</td>
                      <td>{p.district || "-"}</td>
                      <td>{currentPmcNameFor(p)}</td>
                      <td>{formatCapacity(projectCapacity(p))}</td>
                      <td>
                        <span className={`status-badge s${i % 5}`}>
                          {p.rawStatus || p.status || "APPLIED"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="panel">
          <div className="panel-head">
            <h3>Alerts & Automation</h3>
            <span>Live</span>
          </div>

          {notifications.length > 0 &&
            notifications.slice(0, 4).map((n, i) => (
              <div className="alert" key={`${n.type || "note"}-${i}`}>
                <div className="alert-icon blue">{n.icon || "!"}</div>
                <div>
                  <b>{n.title || "Notification"}</b>
                  <small>{n.subtitle || "-"}</small>
                </div>
                <em>Live</em>
              </div>
            ))}

          <div className="alert">
            <div className="alert-icon amber">↔</div>
            <div>
              <b>PACS–PMC allocation pending</b>
              <small>
                {unallocatedRegisteredPacs.length} registered PACS need an
                authorised PMC allocation.
              </small>
            </div>
            <em>Allocate</em>
          </div>

          <div className="alert">
            <div className="alert-icon amber">!</div>
            <div>
              <b>PMC approval pending</b>
              <small>{pendingPmcs} PMCs need branch approval.</small>
            </div>
            <em>Action</em>
          </div>

          <div className="alert">
            <div className="alert-icon blue">!</div>
            <div>
              <b>User approval pending</b>
              <small>
                {pendingUsers} users require verification / approval.
              </small>
            </div>
            <em>Review</em>
          </div>

          <div className="alert">
            <div className="alert-icon amber">SR</div>
            <div>
              <b>State Registrar approval pending</b>
              <small>
                {pendingStateRegistrars} State Registrar registrations need
                Branch action.
              </small>
            </div>
            <em>Approve</em>
          </div>
        </div>
      </section>

      <section className="panel">
        <div className="panel-head">
          <h3>State PMC Agencies</h3>
          <span>{statePmcs.length} records</span>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>PMC / Agency</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>State</th>
                <th>Approval Status</th>
              </tr>
            </thead>

            <tbody>
              {statePmcs.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center", padding: 30 }}>
                    No PMC agencies registered for {branchState}.
                  </td>
                </tr>
              ) : (
                statePmcs.slice(0, 10).map((p, i) => (
                  <tr key={p.id || i}>
                    <td>
                      <b>
                        {p.agencyName || p.organizationName || p.name || "-"}
                      </b>
                    </td>
                    <td>{p.email || "-"}</td>
                    <td>{p.mobile || p.phone || "-"}</td>
                    <td>{p.state || p.stateName || "-"}</td>
                    <td>
                      <span className={`status-badge s${i % 5}`}>
                        {p.branchApprovalStatus || p.status || "PENDING"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );

  const renderModule = () => {
    if (tab === "stateRegistrarApproval") {
      return (
        <section className="module-page">
          <div className="module-head">
            <div>
              <h2>State Registrar Approval</h2>
              <p>
                Approve authorised State Registrar registrations for{" "}
                {branchState} and enable their portal login.
              </p>
            </div>
            <button className="back-btn" onClick={() => setTab("home")}>
              ← Back to Branch Home
            </button>
          </div>
          <div className="module-content">
            <style>{STATE_REGISTRAR_APPROVAL_STYLE}</style>
            <section className="sr-approval">
              <div className="sr-kpis">
                <div>
                  <span>Total Registrations</span>
                  <b>{stateRegistrars.length}</b>
                </div>
                <div>
                  <span>Pending Approval</span>
                  <b>{pendingStateRegistrars}</b>
                </div>
                <div>
                  <span>Approved & Active</span>
                  <b>{approvedStateRegistrars}</b>
                </div>
                <div>
                  <span>Query / Rejected</span>
                  <b>{queryStateRegistrars + rejectedStateRegistrars}</b>
                </div>
              </div>

              {registrarMessage && (
                <div className="sr-message">{registrarMessage}</div>
              )}

              <div className="sr-filters">
                <input
                  value={registrarSearch}
                  onChange={(event) => setRegistrarSearch(event.target.value)}
                  placeholder="Search officer, office, email or registration..."
                />
                <select
                  value={registrarStatusFilter}
                  onChange={(event) =>
                    setRegistrarStatusFilter(event.target.value)
                  }
                >
                  <option value="ALL">All Status</option>
                  <option value="PENDING">Pending Approval</option>
                  <option value="APPROVED">Approved</option>
                  <option value="QUERY">Query Raised</option>
                  <option value="REJECT">Rejected</option>
                </select>
              </div>

              <div className="sr-layout">
                <div className="sr-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>State Registrar / Office</th>
                        <th>Contact</th>
                        <th>Status</th>
                        <th>Review</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleStateRegistrars.map((registrar) => (
                        <tr
                          key={registrar.id}
                          className={
                            selectedRegistrar?.id === registrar.id
                              ? "selected"
                              : ""
                          }
                        >
                          <td>
                            <b>
                              {registrar.name ||
                                registrar.officerName ||
                                registrar.organizationName ||
                                "State Registrar"}
                            </b>
                            <small>
                              {registrar.registrationNumber ||
                                registrar.registrationNo ||
                                registrar.id}
                            </small>
                          </td>
                          <td>
                            {registrar.mobile || registrar.phone || "-"}
                            <small>{registrar.email || "-"}</small>
                          </td>
                          <td>
                            <span
                              className={`sr-status ${
                                String(registrar.status || "").toUpperCase() ===
                                "APPROVED"
                                  ? "approved"
                                  : String(registrar.status || "")
                                        .toUpperCase()
                                        .includes("REJECT")
                                    ? "rejected"
                                    : "pending"
                              }`}
                            >
                              {String(registrar.status || "PENDING").replaceAll(
                                "_",
                                " ",
                              )}
                            </span>
                          </td>
                          <td>
                            <button
                              className="sr-open"
                              onClick={() =>
                                setSelectedRegistrarId(registrar.id)
                              }
                            >
                              Open File
                            </button>
                          </td>
                        </tr>
                      ))}
                      {!visibleStateRegistrars.length && (
                        <tr>
                          <td colSpan="4" className="sr-empty">
                            No State Registrar registration found for{" "}
                            {branchState}.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <aside className="sr-file">
                  {!selectedRegistrar && (
                    <div className="sr-empty">
                      Select a State Registrar registration.
                    </div>
                  )}
                  {selectedRegistrar && (
                    <>
                      <div className="sr-file-head">
                        <small>STATE REGISTRAR REGISTRATION FILE</small>
                        <h3>
                          {selectedRegistrar.name ||
                            selectedRegistrar.officerName ||
                            selectedRegistrar.organizationName ||
                            "State Registrar"}
                        </h3>
                        <span>
                          {selectedRegistrar.registrationNumber ||
                            selectedRegistrar.registrationNo ||
                            selectedRegistrar.id}
                        </span>
                      </div>
                      <div className="sr-details">
                        <span>
                          State<b>{selectedRegistrar.state || branchState}</b>
                        </span>
                        <span>
                          Designation
                          <b>
                            {selectedRegistrar.designation || "State Registrar"}
                          </b>
                        </span>
                        <span>
                          Office
                          <b>
                            {selectedRegistrar.organizationName ||
                              "Registrar Office"}
                          </b>
                        </span>
                        <span>
                          Mobile
                          <b>
                            {selectedRegistrar.mobile ||
                              selectedRegistrar.phone ||
                              "-"}
                          </b>
                        </span>
                        <span>
                          Email<b>{selectedRegistrar.email || "-"}</b>
                        </span>
                        <span>
                          Current Status
                          <b>
                            {String(
                              selectedRegistrar.status || "PENDING",
                            ).replaceAll("_", " ")}
                          </b>
                        </span>
                      </div>
                      {(selectedRegistrar.stateBranchQuery ||
                        selectedRegistrar.stateBranchRejectionReason) && (
                        <div className="sr-remarks">
                          <b>Recorded Remarks</b>
                          <p>
                            {selectedRegistrar.stateBranchQuery ||
                              selectedRegistrar.stateBranchRejectionReason}
                          </p>
                        </div>
                      )}
                      <label className="sr-confirm">
                        <input
                          type="checkbox"
                          id={`confirm-${selectedRegistrar.id}`}
                        />{" "}
                        I confirm that the State Registrar identity, State
                        jurisdiction and authority documents have been verified.
                      </label>
                      <div className="sr-actions">
                        <button
                          disabled={
                            registrarBusy === selectedRegistrar.id ||
                            String(selectedRegistrar.status).toUpperCase() ===
                              "APPROVED"
                          }
                          onClick={() => {
                            const checked = document.getElementById(
                              `confirm-${selectedRegistrar.id}`,
                            )?.checked;
                            if (!checked)
                              return window.alert(
                                "Please confirm document verification first.",
                              );
                            handleStateRegistrarAction(
                              "APPROVE",
                              selectedRegistrar,
                            );
                          }}
                        >
                          {String(selectedRegistrar.status).toUpperCase() ===
                          "APPROVED"
                            ? "Approved & Login Enabled"
                            : "Approve & Enable Login"}
                        </button>
                        <button
                          className="query"
                          disabled={registrarBusy === selectedRegistrar.id}
                          onClick={() =>
                            handleStateRegistrarAction(
                              "QUERY",
                              selectedRegistrar,
                            )
                          }
                        >
                          Raise Query
                        </button>
                        <button
                          className="reject"
                          disabled={registrarBusy === selectedRegistrar.id}
                          onClick={() =>
                            handleStateRegistrarAction(
                              "REJECT",
                              selectedRegistrar,
                            )
                          }
                        >
                          Reject
                        </button>
                      </div>
                    </>
                  )}
                </aside>
              </div>
            </section>
          </div>
        </section>
      );
    }

    if (tab === "pacsAllocation") {
      return (
        <section className="module-page">
          <div className="module-head">
            <div>
              <h2>Official PACS–PMC Allocation Desk</h2>
              <p>
                Allocate or re-allocate every registered {branchState} PACS to
                an approved active PMC. All linked applications and edited PMC
                profile details update in real time without deleting any legacy
                record.
              </p>
            </div>
            <button className="back-btn" onClick={() => setTab("home")}>
              ← Back to Branch Home
            </button>
          </div>
          <div className="module-content">
            <PacsPmcAllocationDesk
              pacsUsers={stateRegisteredPacs}
              projects={statePacsProjects}
              approvedPmcs={approvedBranchPmcs}
              allocations={stateAllocations}
              branchState={branchState}
            />
          </div>
        </section>
      );
    }

    if (tab === "registrationMis") {
      return (
        <section className="module-page">
          <div className="module-head">
            <div>
              <h2>State Registration MIS</h2>
              <p>
                Live PACS, PMC and technical vendor registration register for{" "}
                {branchState}.
              </p>
            </div>
            <button className="back-btn" onClick={() => setTab("home")}>
              ← Back to Branch Home
            </button>
          </div>
          <div className="module-content">
            <BranchRegistrationMis
              pacs={stateRegisteredPacs}
              pmcs={stateRegisteredPmcs}
              vendors={stateRegisteredVendors}
              stateName={branchState}
              projects={statePacsProjects}
              allocations={stateAllocations}
            />
          </div>
        </section>
      );
    }

    if (tab === "pacsDocuments") {
      return (
        <section className="module-page">
          <div className="module-head">
            <div>
              <h2>PACS Documents &amp; PMC Scrutiny Status</h2>
              <p>
                View every PACS mandatory/additional document, missing-file
                count, PMC decision and current MOU readiness for {branchState}.
              </p>
            </div>
            <button className="back-btn" onClick={() => setTab("home")}>
              ← Back to Branch Home
            </button>
          </div>
          <div className="module-content">
            <PacsDocumentMonitoringDesk
              projects={statePacsProjects}
              onOpenMou={() => setTab("mouExecution")}
            />
          </div>
        </section>
      );
    }

    if (tab === "applications") {
      return (
        <section className="module-page">
          <div className="module-head">
            <div>
              <h2>State PACS Allocation &amp; Applications</h2>
              <p>
                Every allocated PACS, including PACS whose application is still
                awaited, grouped under the current PMC master profile for{" "}
                {branchState}.
              </p>
            </div>
            <button className="back-btn" onClick={() => setTab("home")}>
              ← Back to Branch Home
            </button>
          </div>
          <div className="module-content">
            <StatePacsApplications
              projects={statePacsProjects}
              approvedPmcs={approvedBranchPmcs}
              allocations={stateAllocations}
              pacsUsers={stateRegisteredPacs}
            />
          </div>
        </section>
      );
    }

    if (tab === "mouExecution") {
      return (
        <section className="module-page">
          <div className="module-head">
            <div>
              <h2>PMC–PACS MOU Status &amp; Copies</h2>
              <p>
                Read-only State Branch view: track PMC/PACS signatures and view
                or download the available MOU copies.
              </p>
            </div>
            <button className="back-btn" onClick={() => setTab("home")}>
              ← Back to Branch Home
            </button>
          </div>
          <div className="module-content">
            <StateBranchMouDesk
              projects={statePacsProjects}
            />
          </div>
        </section>
      );
    }

    if (tab === "hiringAssurance") {
      return (
        <section className="module-page">
          <div className="module-head">
            <div>
              <h2>Hiring Assurance Letter Desk</h2>
              <p>
                Save the authorised {branchState} State Branch signature,
                auto-generate a PACS-specific official letter and publish the
                issued copy directly to the PACS dashboard.
              </p>
            </div>
            <button className="back-btn" onClick={() => setTab("home")}>
              ← Back to Branch Home
            </button>
          </div>
          <div className="module-content">
            <HiringAssuranceDesk
              pacs={stateRegisteredPacs}
              allocations={stateAllocations}
              projects={statePacsProjects}
              approvedPmcs={approvedBranchPmcs}
              profile={profile || {}}
              stateName={branchState}
            />
          </div>
        </section>
      );
    }

    if (tab === "overview") {
      return (
        <section className="module-page">
          <div className="module-head">
            <div>
              <h2>Dashboard Overview</h2>
              <p>Live state data for {branchState}.</p>
            </div>
            <button className="back-btn" onClick={() => setTab("home")}>
              ← Back to Branch Home
            </button>
          </div>
          <div className="module-content">{renderOverview()}</div>
        </section>
      );
    }

    const active = MODULES.find((m) => m.key === tab);

    return (
      <section className="module-page">
        <div className="module-head">
          <div>
            <h2>{active?.title || "Module"}</h2>
            <p>{active?.desc || "NAFED State Branch Module"}</p>
          </div>
          <button className="back-btn" onClick={() => setTab("home")}>
            ← Back to Branch Home
          </button>
        </div>

        <div className="module-content">
          <div className="coming-panel">
            <div className={`module-icon ${active?.color || "green"}`}>
              {active?.icon || "▣"}
            </div>
            <h3>{active?.title}</h3>
            <p>
              Module shell ready. Existing working module can be connected here
              without rebuilding.
            </p>
          </div>
        </div>
      </section>
    );
  };

  if (loading) {
    return (
      <div className="branch-loader">
        <style>{`
          .branch-loader{min-height:100vh;display:grid;place-items:center;background:linear-gradient(135deg,#041329,#064e3b);color:white;font-family:Inter,Segoe UI,Arial,sans-serif}
          .loader-card{background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.18);border-radius:26px;padding:30px;text-align:center;box-shadow:0 28px 78px rgba(0,0,0,.35)}
          .loader-card img{width:145px;height:78px;object-fit:contain;background:white;border-radius:16px;padding:8px;margin-bottom:14px}
        `}</style>
        <div className="loader-card">
          <img src={LOGO_PATH} alt="NAFED" />
          <h2>Loading State Branch Dashboard...</h2>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        *{box-sizing:border-box}
        body{margin:0;font-family:Inter,Segoe UI,Arial,sans-serif;background:#061b2f;color:#071f45}
        .branch-page{min-height:100vh;position:relative;overflow-x:hidden;background:radial-gradient(circle at 8% 10%,rgba(34,197,94,.22),transparent 26%),radial-gradient(circle at 92% 20%,rgba(14,165,233,.18),transparent 26%),linear-gradient(135deg,#041329 0%,#062b55 48%,#064e3b 100%);padding:22px 0 34px}
        .branch-page:before{content:"";position:fixed;inset:0;pointer-events:none;background-image:linear-gradient(rgba(255,255,255,.045) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.045) 1px,transparent 1px);background-size:48px 48px;mask-image:linear-gradient(to bottom,rgba(0,0,0,.9),rgba(0,0,0,.18))}
        .branch-wrap{width:min(1320px,calc(100% - 44px));margin:0 auto;position:relative;z-index:2}
        .branch-header{min-height:82px;border-radius:22px;background:rgba(255,255,255,.12);color:white;display:flex;align-items:center;justify-content:space-between;padding:14px 20px;box-shadow:0 22px 60px rgba(2,6,23,.34);border:1px solid rgba(255,255,255,.16);backdrop-filter:blur(18px)}
        .brand{display:flex;align-items:center;gap:16px;min-width:0}
        .nafed-logo{width:155px;height:82px;background:#fff;border-radius:18px;display:flex;align-items:center;justify-content:center;padding:8px;overflow:hidden;box-shadow:0 10px 25px rgba(0,0,0,.18),inset 0 0 0 1px rgba(0,0,0,.04);flex-shrink:0}
        .nafed-logo-img{width:100%;height:100%;object-fit:contain;display:block}
        .brand h1{margin:0;font-size:25px;font-weight:950;letter-spacing:-.02em;white-space:nowrap}
        .brand p{margin:4px 0 0;font-size:12px;color:#d1fae5;font-weight:800;white-space:nowrap}
        .header-right{display:flex;align-items:center;gap:14px;flex:0 0 auto}
        .head-info{display:flex;align-items:center;gap:9px;font-size:11px;color:#d1fae5;font-weight:800}
        .live-indicator{display:flex;align-items:center;gap:7px;padding:8px 10px;border:1px solid rgba(134,239,172,.34);border-radius:999px;background:rgba(22,163,74,.18);color:#dcfce7;font-size:9px;font-weight:950;letter-spacing:.05em}.live-indicator i{width:8px;height:8px;border-radius:50%;background:#4ade80;box-shadow:0 0 0 4px rgba(74,222,128,.15)}.live-indicator.error{border-color:rgba(248,113,113,.5);background:rgba(185,28,28,.25);color:#fee2e2}.live-indicator.error i{background:#f87171;box-shadow:0 0 0 4px rgba(248,113,113,.15)}
        .head-info b{display:block;color:white;font-size:12px;margin-bottom:2px}
        .avatar{width:38px;height:38px;border-radius:999px;background:white;color:#064e3b;display:grid;place-items:center;font-weight:950;box-shadow:0 12px 26px rgba(0,0,0,.16)}
        .logout,.refresh{border:0;background:rgba(255,255,255,.16);color:white;border-radius:12px;padding:9px 12px;font-weight:950;cursor:pointer}
        .refresh{background:#16a34a}
        .branch-body{padding:26px 0 0}
        .welcome-row{display:flex;justify-content:space-between;align-items:center;gap:22px;margin-bottom:22px}
        .welcome h2{margin:0;color:white;text-shadow:0 5px 22px rgba(0,0,0,.35);font-size:30px;font-weight:950}
        .welcome p{margin:8px 0 0;color:#ecfdf5;text-shadow:0 5px 22px rgba(0,0,0,.24);font-size:14px;font-weight:800}
        .stats-card{background:rgba(255,255,255,.94);border:1px solid rgba(255,255,255,.58);border-radius:20px;box-shadow:0 24px 64px rgba(2,6,23,.24);display:grid;grid-template-columns:repeat(4,1fr);min-width:620px;overflow:hidden;backdrop-filter:blur(16px)}
        .stat{padding:16px 20px;display:flex;gap:12px;align-items:center;border-right:1px solid #e2e8f0;background:white}
        .stat:last-child{border-right:0}
        .stat-icon{width:38px;height:38px;border-radius:999px;display:grid;place-items:center;font-weight:950;font-size:12px;color:white;flex:0 0 auto}
        .stat label{display:block;color:#64748b;font-size:11px;font-weight:950}
        .stat b{display:block;margin-top:3px;color:#071f45;font-size:22px;font-weight:950}
        .stat small{display:block;margin-top:3px;color:#64748b;font-size:11px;font-weight:800}
        .home-card,.panel,.module-page{background:rgba(255,255,255,.95);border:1px solid rgba(255,255,255,.60);border-radius:24px;box-shadow:0 28px 78px rgba(2,6,23,.26);backdrop-filter:blur(18px)}
        .home-card{padding:22px}
        .section-title{border-left:4px solid #16a34a;padding-left:14px;margin-bottom:18px}
        .section-title h3{margin:0;font-size:23px;color:#071f45;font-weight:950}
        .section-title p{margin:6px 0 0;color:#475569;font-size:12px;font-weight:800}
        .module-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:14px}
        .module-card{border:1px solid #dbe4ef;border-radius:18px;background:rgba(255,255,255,.96);min-height:105px;padding:15px;display:grid;grid-template-columns:56px 1fr 30px;align-items:center;gap:13px;cursor:pointer;transition:.22s ease;box-shadow:0 10px 24px rgba(15,23,42,.07);text-align:left}
        .module-card:hover{transform:translateY(-4px);box-shadow:0 22px 46px rgba(15,23,42,.18);border-color:#86efac}
        .module-icon{width:54px;height:54px;border-radius:16px;display:grid;place-items:center;font-size:24px;font-weight:950}
        .module-icon.green{background:#dcfce7;color:#15803d}.module-icon.orange{background:#ffedd5;color:#ea580c}.module-icon.blue{background:#dbeafe;color:#2563eb}.module-icon.purple{background:#f3e8ff;color:#9333ea}.module-icon.yellow{background:#fef3c7;color:#ca8a04}.module-icon.teal{background:#ccfbf1;color:#0f766e}.module-icon.pink{background:#fce7f3;color:#db2777}
        .module-card h4{margin:0;color:#166534;font-size:15px;font-weight:950}.module-card p{margin:6px 0 0;color:#334155;font-size:12px;line-height:1.45;font-weight:750}
        .arrow{width:30px;height:30px;border-radius:999px;background:#eef8f0;color:#15803d;display:grid;place-items:center;font-size:19px;font-weight:950}
        .module-page{overflow:hidden}.module-head{padding:16px 20px;background:linear-gradient(105deg,rgba(6,36,74,.98),rgba(6,78,59,.98));color:white;display:flex;align-items:center;justify-content:space-between;gap:16px}.module-head h2{margin:0;font-size:22px;font-weight:950}.module-head p{margin:4px 0 0;color:#d1fae5;font-size:12px;font-weight:750}.back-btn{border:0;background:white;color:#064e3b;border-radius:12px;padding:10px 14px;font-weight:950;cursor:pointer}.module-content{padding:16px;background:#f8fafc}.coming-panel{padding:45px 20px;display:grid;place-items:center;text-align:center;background:#f8fafc}
        .overview-space{display:grid;gap:18px}.overview-kpis{display:grid;grid-template-columns:repeat(6,1fr);gap:14px}
        .analytics-grid{display:grid;grid-template-columns:1.2fr .9fr;gap:14px}.bottom-grid{display:grid;grid-template-columns:1.25fr .75fr;gap:14px}
        .panel{padding:18px}.panel-head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:16px}.panel-head h3{margin:0;color:#071f45;font-size:17px;font-weight:950}.panel-head span{color:#064e3b;font-size:11px;font-weight:950}
        .donut-wrap{display:flex;align-items:center;gap:18px}.donut{width:160px;height:160px;border-radius:50%;flex:0 0 auto;display:grid;place-items:center;background:conic-gradient(#1189d8 0 25%,#31c77b 25% 45%,#ffb21b 45% 62%,#8956d8 62% 78%,#50c5b7 78% 96%,#6bd24a 96%)}.donut-inner{width:92px;height:92px;border-radius:50%;background:white;display:grid;place-items:center;text-align:center}.donut-inner b{color:#071f45;font-size:18px;font-weight:950}.donut-inner small{color:#64748b;font-weight:850;font-size:11px}.legend{flex:1;display:grid;gap:9px}.legend-row{display:flex;align-items:center;justify-content:space-between;gap:10px;color:#334155;font-size:12px;font-weight:850}.legend-row span{display:flex;align-items:center}.legend-row i{width:9px;height:9px;border-radius:999px;margin-right:7px}.legend-row b{color:#071f45;font-weight:950}
        .bar-row{margin-top:14px}.bar-row>div:first-child{display:flex;justify-content:space-between;gap:10px;margin-bottom:7px;color:#334155;font-size:12px;font-weight:850}.bar-bg{height:12px;background:#eef2f7;border-radius:999px;overflow:hidden}.bar{height:100%;border-radius:999px}
        .table-wrap{overflow-x:auto;border:1.5px solid #111827;border-radius:14px}table{width:100%;border-collapse:collapse;font-size:12px;min-width:820px;background:white}th{text-align:left;background:#e2e8f0;color:#071f45;font-size:10px;text-transform:uppercase;letter-spacing:.08em;padding:12px;border:1.5px solid #111827;font-weight:950}td{padding:12px;border:1.5px solid #111827;font-weight:750;color:#0f172a}.status-badge{border-radius:999px;padding:6px 9px;font-size:10px;font-weight:950;display:inline-block;white-space:nowrap}.status-badge.s0,.status-badge.s3{background:#dcfce7;color:#166534;border:1px solid #16a34a}.status-badge.s1{background:#f3e8ff;color:#6b21a8;border:1px solid #c084fc}.status-badge.s2{background:#ffedd5;color:#9a3412;border:1px solid #fb923c}.status-badge.s4{background:#dbeafe;color:#1e40af;border:1px solid #93c5fd}
        .registration-mis{color:#142b46}.reg-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:14px}.reg-kpis>div{padding:15px;border:1px solid #cbd8e6;border-top:4px solid #16834f;border-radius:11px;background:#fff}.reg-kpis span,.reg-kpis b{display:block}.reg-kpis span{color:#64748b;font-size:9px;font-weight:900;text-transform:uppercase}.reg-kpis b{margin-top:7px;font-size:23px}.reg-toolbar{display:grid;grid-template-columns:auto 1fr 220px;gap:10px;margin-bottom:12px;padding:12px;border:1px solid #cbd8e6;border-radius:11px;background:#f4f7fa}.reg-tabs{display:flex;gap:6px}.reg-tabs button{padding:10px 13px;border:1px solid #b8c8d8;border-radius:8px;background:#fff;color:#23425f;font-weight:900}.reg-tabs button.active{background:#0b396c;color:#fff}.reg-toolbar input,.reg-toolbar select{height:42px;padding:0 11px;border:1px solid #b8c8d8;border-radius:8px;background:#fff}.reg-table{overflow:auto;border:1.5px solid #172b45;border-radius:10px;background:#fff}.reg-table table{width:100%;min-width:950px;border-collapse:collapse}.reg-table th,.reg-table td{padding:11px;border:1px solid #172b45;text-align:left;font-size:10px}.reg-table th{background:#dce5ee;color:#102c4d;font-size:9px;text-transform:uppercase}.reg-table td b,.reg-table td small{display:block}.reg-table td small{margin-top:4px;color:#64748b}.reg-badge{display:inline-block;padding:6px 8px;border-radius:999px;background:#fff2d8;color:#8b5700;font-size:8px;font-weight:950}.reg-badge.approved{background:#e5f7eb;color:#17653b}.reg-empty{text-align:center!important;padding:30px!important;color:#64748b}
        .alert{display:grid;grid-template-columns:42px 1fr auto;gap:12px;align-items:center;padding:13px 0;border-bottom:1px solid #edf2f7}.alert-icon{width:38px;height:38px;border-radius:13px;display:grid;place-items:center;font-weight:950}.alert-icon.amber{background:#fef3c7;color:#92400e}.alert-icon.blue{background:#dbeafe;color:#1e40af}.alert-icon.green{background:#dcfce7;color:#166534}.alert b{display:block;color:#071f45;font-size:12px;font-weight:950}.alert small{display:block;margin-top:3px;color:#64748b;font-weight:750}.alert em{color:#64748b;font-size:10px;font-style:normal;font-weight:850}
        @media(max-width:1300px){.overview-kpis{grid-template-columns:repeat(3,1fr)}.analytics-grid,.bottom-grid{grid-template-columns:1fr}}@media(max-width:1050px){.branch-header{flex-direction:column;align-items:flex-start}.header-right{width:100%;flex-wrap:wrap}.welcome-row{flex-direction:column;align-items:stretch}.stats-card{min-width:auto}}@media(max-width:760px){.branch-wrap{width:calc(100% - 24px)}.brand h1{font-size:22px;white-space:normal}.brand p{white-space:normal}.nafed-logo{width:138px;height:74px}.module-grid,.overview-kpis,.stats-card{grid-template-columns:1fr}.stat{border-right:0;border-bottom:1px solid #e2e8f0}.donut-wrap{flex-direction:column;align-items:flex-start}.module-head{flex-direction:column;align-items:flex-start}}

        /* Government Command Centre dashboard — single-file, no sidebar. */
        body{background:#f4f7fa;color:#0a2a50}
        button{font-family:inherit}
        .command-page{min-height:100vh;background:#f4f7fa;color:#0a2a50}
        .command-container{width:min(1600px,calc(100% - 72px));margin:0 auto}
        .gov-utility{height:34px;border-bottom:1px solid #dbe2e9;background:#f7f8f9;color:#303943;font-size:11px;font-weight:700}
        .gov-utility .command-container{height:100%;display:flex;align-items:center;justify-content:space-between;gap:20px}
        .gov-left,.gov-right{display:flex;align-items:center;gap:14px;white-space:nowrap}
        .gov-left span+span,.gov-right span+span{padding-left:14px;border-left:1px solid #cbd3db}
        .gov-flag{width:20px;height:14px;border:1px solid #ccd3d9;background:linear-gradient(#ff8a26 0 33%,#fff 33% 66%,#16864b 66%);box-shadow:0 1px 3px rgba(15,23,42,.12)}
        .command-header{background:linear-gradient(105deg,#052c5b 0%,#062b56 55%,#03244a 100%);color:#fff;border-bottom:1px solid rgba(255,255,255,.18);box-shadow:0 6px 22px rgba(3,34,69,.2);position:relative;z-index:5}
        .command-header-inner{min-height:84px;display:flex;align-items:center;gap:28px}
        .command-brand{display:flex;align-items:center;gap:17px;min-width:420px}
        .command-logo{width:94px;height:68px;border-radius:10px;background:#fff;padding:6px;display:grid;place-items:center;box-shadow:0 9px 22px rgba(0,0,0,.2)}
        .command-logo img{width:100%;height:100%;object-fit:contain}
        .command-brand h1{margin:0;font-size:24px;line-height:1.1;letter-spacing:-.02em;font-weight:900}
        .command-brand p{margin:7px 0 0;color:#c9d7e8;font-size:10px;font-weight:650}
        .command-nav{flex:1;display:flex;align-self:stretch;justify-content:center;gap:4px}
        .command-nav button{position:relative;display:flex;align-items:center;justify-content:center;gap:7px;min-width:70px;padding:0 10px;border:0;background:transparent;color:#dbe7f2;font-size:11px;font-weight:720;cursor:pointer}
        .command-nav button:hover,.command-nav button.active{color:#fff;background:rgba(255,255,255,.045)}
        .command-nav button.active:after{content:"";position:absolute;left:10px;right:10px;bottom:16px;height:2px;border-radius:3px;background:#3ed27e;box-shadow:0 0 9px rgba(62,210,126,.45)}
        .command-header-actions{display:flex;align-items:center;gap:12px;margin-left:auto}
        .command-live{height:38px;padding:0 13px;border:1px solid rgba(135,190,224,.35);border-radius:8px;display:flex;align-items:center;gap:8px;background:rgba(0,18,42,.28);font-size:9px;font-weight:850;text-transform:uppercase;letter-spacing:.04em;white-space:nowrap}
        .command-live i{width:7px;height:7px;border-radius:50%;background:#42d37d;box-shadow:0 0 0 4px rgba(66,211,125,.13)}
        .command-live.error i{background:#ff6b72}.command-live.error{color:#ffd9dc}
        .command-notify,.command-logout{position:relative;width:39px;height:39px;display:grid;place-items:center;border:0;background:transparent;color:#fff;cursor:pointer}
        .command-notify b{position:absolute;top:0;right:0;min-width:18px;height:18px;padding:0 4px;border-radius:9px;display:grid;place-items:center;background:#ef4b55;color:#fff;font-size:9px;border:2px solid #062b56}
        .command-profile{display:flex;align-items:center;gap:10px;padding-left:12px;border-left:1px solid rgba(255,255,255,.2);min-width:145px}
        .command-avatar{width:43px;height:43px;border-radius:50%;display:grid;place-items:center;background:#f8fbff;color:#15375e;font-weight:950;box-shadow:inset 0 0 0 1px #dbe5ef}
        .command-profile b,.command-profile small{display:block}.command-profile b{font-size:11px}.command-profile small{margin-top:4px;color:#bfd0e1;font-size:9px}
        .command-logout{border:1px solid rgba(255,255,255,.25);border-radius:8px;background:rgba(255,255,255,.05)}
        .command-hero{position:relative;isolation:isolate;overflow:hidden;background:linear-gradient(90deg,rgba(3,42,83,.99) 0%,rgba(4,53,96,.94) 42%,rgba(4,47,84,.58) 70%,rgba(4,34,67,.76) 100%);color:#fff;border-bottom:1px solid #113d69}
        .command-hero:after{content:"";position:absolute;inset:0;z-index:-1;background:linear-gradient(90deg,transparent,rgba(5,42,74,.05)),radial-gradient(circle at 18% 25%,rgba(41,147,117,.2),transparent 32%)}
        .hero-visual{position:absolute;z-index:-2;right:0;top:0;width:64%;height:205px;opacity:.83;overflow:hidden;mask-image:linear-gradient(90deg,transparent 0%,#000 22%)}
        .hero-visual svg{width:100%;height:100%;display:block}
        .command-hero-copy{padding:24px 4px 36px;position:relative;z-index:1}
        .command-hero-copy h2{margin:0;font-size:27px;letter-spacing:-.02em;font-weight:900;text-shadow:0 3px 15px rgba(0,0,0,.2)}
        .command-hero-copy p{margin:7px 0 0;color:#d5e4f0;font-size:12px;font-weight:650}
        .hero-state-mark{position:absolute;right:10px;top:9px;width:100px;height:85px;opacity:.9}
        .hero-state-mark path{fill:rgba(255,255,255,.8);stroke:#fff;stroke-width:2}
        .command-kpi-ribbon{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));margin:0 0 10px;border:1px solid rgba(154,194,224,.67);border-radius:8px;background:rgba(3,37,75,.86);box-shadow:0 12px 30px rgba(0,17,40,.25);overflow:hidden;backdrop-filter:blur(9px)}
        .command-kpi{min-height:83px;padding:13px 16px;display:grid;grid-template-columns:44px 1fr;align-items:center;gap:11px;border-right:1px solid rgba(180,207,229,.35)}
        .command-kpi:last-child{border-right:0}.command-kpi-icon{width:42px;height:42px;border-radius:50%;display:grid;place-items:center;border:1.5px solid currentColor}
        .command-kpi.green{color:#4bd07c}.command-kpi.blue{color:#4f9bff}.command-kpi.purple{color:#a56aff}.command-kpi.orange{color:#ff9d28}.command-kpi.red{color:#ff5966}
        .command-kpi label,.command-kpi b,.command-kpi small{display:block}.command-kpi label{color:#d6e0eb;font-size:10px;font-weight:720}.command-kpi b{margin-top:3px;color:#fff;font-size:22px;line-height:1;font-weight:900;white-space:nowrap}.command-kpi small{margin-top:6px;color:#bdcddd;font-size:9px;font-weight:650}
        .command-main{padding:12px 0 14px}
        .command-top-grid{display:grid;grid-template-columns:minmax(0,2.12fr) minmax(325px,1fr);gap:12px;align-items:stretch}
        .command-bottom-grid{display:grid;grid-template-columns:minmax(0,1.2fr) minmax(290px,.92fr) minmax(330px,1fr);gap:12px;margin-top:12px;align-items:stretch}
        .command-card{border:1px solid #d5dfe9;border-radius:7px;background:#fff;box-shadow:0 2px 7px rgba(9,35,65,.04);overflow:hidden}
        .command-card-head{min-height:50px;padding:11px 16px;display:flex;align-items:flex-start;justify-content:space-between;gap:12px;border-bottom:1px solid #e0e7ee}
        .command-card-head h3{margin:0;color:#092b53;font-size:14px;font-weight:900}.command-card-head p{margin:5px 0 0;color:#60748b;font-size:9px;font-weight:650}
        .command-card-head button{border:0;background:transparent;color:#0d5db8;font-size:9px;font-weight:850;cursor:pointer}
        .workflow-legend{display:flex;align-items:center;gap:16px;color:#5e7187;font-size:9px;font-weight:700}.workflow-legend span{display:flex;align-items:center;gap:6px}.workflow-legend i{width:8px;height:8px;border-radius:50%}.workflow-legend .done{background:#25a55a}.workflow-legend .active{background:#2563d8}.workflow-legend .pending{background:#a9b4c0}
        .workflow-track{display:grid;grid-template-columns:repeat(9,minmax(76px,1fr));padding:18px 14px 17px;overflow-x:auto}
        .workflow-stage{position:relative;min-width:78px;text-align:center;color:#78899b}
        .workflow-stage:before{content:"";position:absolute;z-index:0;top:17px;left:0;right:0;height:2px;background:#d2d9e0}.workflow-stage:first-child:before{left:50%}.workflow-stage:last-child:before{right:50%}.workflow-stage.complete:before{background:#30a65f}.workflow-stage.active:before{background:linear-gradient(90deg,#30a65f 50%,#d2d9e0 50%)}
        .workflow-number{position:relative;z-index:1;width:36px;height:36px;margin:0 auto 11px;border:1.5px solid #b3bdc8;border-radius:50%;display:grid;place-items:center;background:#f7f9fb;color:#526579;font-size:13px;font-weight:900;box-shadow:0 0 0 4px #fff}
        .workflow-stage.complete .workflow-number{border-color:#34a75f;background:#eff9f2;color:#21894b}.workflow-stage.active .workflow-number{border-color:#2668d7;background:#edf4ff;color:#1557c0;box-shadow:0 0 0 4px #fff,0 4px 12px rgba(37,99,216,.22)}
        .workflow-check{position:absolute;right:-1px;bottom:-1px;width:13px;height:13px;border-radius:50%;display:grid;place-items:center;background:#2ca25b;color:#fff;font-size:8px}
        .workflow-stage strong{display:block;min-height:29px;padding:0 5px;color:#193a5f;font-size:9px;line-height:1.35;font-weight:820}.workflow-stage.active strong{color:#1557c0}.workflow-stage>small{display:block;margin-top:7px;color:#294866;font-size:11px;font-weight:900}
        .command-map-card{display:flex;flex-direction:column;min-width:0}.command-map-card .command-card-head{border-bottom:1px solid #e3ebf2}.command-map-graphic{padding:11px 13px 13px;background:linear-gradient(180deg,#fbfdff,#f6fafc)}
        .map-live-strip{display:grid;grid-template-columns:.75fr .85fr 1.4fr;margin-bottom:10px;border:1px solid #d9e5ed;border-radius:8px;background:#fff;overflow:hidden}.map-live-strip>div{min-width:0;padding:8px 10px;border-right:1px solid #e1e9ef}.map-live-strip>div:last-child{border-right:0}.map-live-strip span,.map-live-strip b{display:block}.map-live-strip span{color:#70849a;font-size:7px;font-weight:850;text-transform:uppercase;letter-spacing:.045em}.map-live-strip b{margin-top:4px;overflow:hidden;color:#0d355d;font-size:12px;font-weight:950;text-overflow:ellipsis;white-space:nowrap}.map-live-strip>div:nth-child(-n+2) b{font-size:16px}
        .map-workspace{display:grid;grid-template-columns:minmax(0,1.75fr) minmax(150px,.72fr);gap:10px;min-height:286px}.gujarat-map-canvas{position:relative;min-width:0;border:1px solid #d9e5ed;border-radius:9px;background:linear-gradient(145deg,#f9fcfe,#f1f8fa);overflow:hidden}.gujarat-map-canvas:before{content:"LIVE GIS VIEW";position:absolute;z-index:2;left:10px;top:9px;padding:4px 6px;border:1px solid #c9dbe5;border-radius:4px;background:rgba(255,255,255,.88);color:#51728c;font-size:6px;font-weight:950;letter-spacing:.1em}.gujarat-map-canvas svg{display:block;width:100%;height:247px}.gujarat-land{transition:filter .2s ease}.map-boundaries path{fill:none;stroke:#fff;stroke-width:1.25;stroke-linecap:round;opacity:.85}.map-north path{fill:none;stroke:#0b4c78;stroke-width:1.4}.map-north text{fill:#0b4c78;font-size:8px;font-weight:950}.district-point{cursor:pointer;outline:none}.district-point circle{transition:r .16s ease,opacity .16s ease}.district-point:not(.has-projects){opacity:.72}.district-point:hover circle,.district-point:focus circle{opacity:1;stroke:#0b3e6b;stroke-width:2}.district-count{pointer-events:none;fill:#fff;font-size:7px;font-weight:950}.selected-map-callout path{fill:none;stroke:#143e67;stroke-width:1;stroke-dasharray:2 2}.selected-map-callout rect{fill:#fff;stroke:#aec7d7;stroke-width:1;filter:url(#districtGlowV8)}.selected-map-callout text{fill:#0b3157;font-size:7px;font-weight:950}.selected-map-callout text.value{fill:#268153;font-size:5.5px;letter-spacing:.045em}.map-water-label{fill:#85a4b5;font-size:8px;font-style:italic;letter-spacing:.08em}.map-legend{position:absolute;left:9px;right:9px;bottom:8px;display:grid;grid-template-columns:repeat(4,auto);justify-content:space-between;gap:5px;padding:6px 8px;border:1px solid rgba(197,215,226,.95);border-radius:6px;background:rgba(255,255,255,.92);box-shadow:0 3px 9px rgba(13,53,93,.05);color:#516a81;font-size:6px;font-weight:800;backdrop-filter:blur(5px)}.map-legend span{display:flex;align-items:center;gap:4px;white-space:nowrap}.map-legend i{width:8px;height:8px;border:1px solid rgba(28,74,104,.14);border-radius:2px}.map-legend i.none{background:#cbd8e2}.map-legend i.low{background:#4fb989}.map-legend i.medium{background:#149ca8}.map-legend i.high{background:#245fca}
        .map-district-panel{min-width:0;padding:9px;border:1px solid #d9e5ed;border-radius:9px;background:#fff}.selected-district-card{padding:10px;border:1px solid #cfe1ea;border-radius:8px;background:linear-gradient(145deg,#f5fbf8,#edf7f3)}.selected-district-card>span,.selected-district-card>strong{display:block}.selected-district-card>span{color:#618075;font-size:7px;font-weight:900;text-transform:uppercase;letter-spacing:.06em}.selected-district-card>strong{margin-top:4px;overflow:hidden;color:#0b4f3c;font-size:11px;font-weight:950;text-overflow:ellipsis;white-space:nowrap}.selected-district-card>div{display:flex;align-items:flex-end;gap:6px;margin-top:7px}.selected-district-card>div b{color:#0a3157;font-size:23px;line-height:1}.selected-district-card>div small{padding-bottom:2px;color:#62778d;font-size:7px;font-weight:750}.map-ranking-head{display:flex;align-items:center;justify-content:space-between;margin:12px 2px 6px}.map-ranking-head b{color:#173b5e;font-size:8px}.map-ranking-head span{padding:3px 6px;border-radius:10px;background:#e4f7eb;color:#198449;font-size:6px;font-weight:950;text-transform:uppercase}.map-ranking-list{display:grid;gap:5px}.map-ranking-list button{width:100%;min-width:0;padding:6px;border:1px solid #e1e8ee;border-radius:6px;background:#fff;display:grid;grid-template-columns:20px minmax(0,1fr) auto;align-items:center;gap:5px;color:#244764;text-align:left;cursor:pointer}.map-ranking-list button:hover,.map-ranking-list button.active{border-color:#78b99c;background:#f3faf6}.map-ranking-list em{color:#8093a4;font-size:6px;font-style:normal;font-weight:900}.map-ranking-list span,.map-ranking-list span b{display:block;min-width:0}.map-ranking-list span b{overflow:hidden;font-size:7px;text-overflow:ellipsis;white-space:nowrap}.map-ranking-list span i{display:block;height:3px;margin-top:4px;border-radius:3px;background:#e5edf2;overflow:hidden}.map-ranking-list span i u{display:block;height:100%;border-radius:inherit;background:linear-gradient(90deg,#42b77e,#1f77ca);text-decoration:none}.map-ranking-list strong{min-width:19px;color:#0c3c66;font-size:9px;text-align:right}.map-data-note{margin:8px 0 0;padding:7px;border-radius:6px;background:#fff4dc;color:#855b12;font-size:6px;font-weight:750;line-height:1.4}
        .activity-table-wrap{overflow:auto;padding:0 14px 11px}.activity-table{width:100%;min-width:540px;border-collapse:collapse;background:#fff;font-size:9px}.activity-table th{padding:8px 7px;border:0;border-bottom:1px solid #dbe4ec;background:#f8fafc;color:#54708b;font-size:8px;letter-spacing:0;text-transform:none}.activity-table td{padding:9px 7px;border:0;border-bottom:1px solid #e1e8ee;color:#35506c;font-size:8px;font-weight:650}.activity-table tr:last-child td{border-bottom:0}.activity-dot{display:inline-block;width:6px;height:6px;margin-right:7px;border-radius:50%;background:#2ca65d}.activity-dot.progress{background:#2f76de}.activity-status{display:inline-block;padding:5px 8px;border-radius:5px;background:#edf5ff;color:#2665bb;font-size:7px;font-weight:850}.activity-status.done{background:#e8f7ec;color:#25834a}.empty-activity{text-align:center!important;padding:30px!important;color:#718297!important}
        .priority-list{padding:7px 14px 13px}.priority-row{width:100%;min-height:42px;padding:5px 8px;border:1px solid #e0e7ee;border-radius:7px;background:#fff;display:grid;grid-template-columns:31px 1fr auto;align-items:center;gap:9px;color:#24425f;text-align:left;cursor:pointer;margin-bottom:6px}.priority-row:last-child{margin-bottom:0}.priority-row:hover{border-color:#91b7de;background:#f8fbff}.priority-icon,.quick-icon{display:grid;place-items:center;border-radius:7px}.priority-icon{width:29px;height:29px}.priority-row span{font-size:9px;font-weight:760}.priority-row b{min-width:29px;padding:4px 8px;border:1px solid #bec8d3;border-radius:999px;color:#173b60;font-size:9px;text-align:center}
        .tone-green{background:#e6f7eb;color:#239450}.tone-blue{background:#e7f0ff;color:#2d6bd2}.tone-purple{background:#f1e9ff;color:#7c42d3}.tone-orange{background:#fff0df;color:#e77b18}.tone-red{background:#ffebed;color:#e04451}
        .quick-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:9px;padding:10px 14px 14px}.quick-button{min-height:54px;padding:8px;border:1px solid #d4e0e9;border-radius:7px;background:#fbfdff;display:flex;align-items:center;gap:8px;color:#163a60;text-align:left;cursor:pointer}.quick-button:hover{transform:translateY(-1px);box-shadow:0 4px 12px rgba(16,58,99,.09)}.quick-button.wide{grid-column:auto}.quick-icon{width:31px;height:31px;flex:0 0 auto}.quick-button span{font-size:8px;line-height:1.25;font-weight:820}.quick-button em{margin-left:auto;color:#688096;font-size:15px;font-style:normal}
        .command-module-shell{padding:16px 0 20px}.command-module-shell .module-page{border:1px solid #d5dfe9;border-radius:8px;box-shadow:0 8px 25px rgba(9,35,65,.08);background:#fff}.command-module-shell .module-head{border-radius:0;background:linear-gradient(105deg,#06325f,#0b5949)}
        .command-footer{min-height:56px;background:linear-gradient(90deg,#052d5a,#03254d);color:#d6e3ef;border-top:3px solid #2da15e}.command-footer .command-container{min-height:56px;display:grid;grid-template-columns:1fr auto 1fr;align-items:center;gap:20px;font-size:9px}.footer-brand{display:flex;align-items:center;gap:13px}.footer-brand b{color:#4bd077;font-size:17px;letter-spacing:.05em}.footer-links{display:flex;justify-content:flex-end;gap:0}.footer-links span{padding:0 12px;border-left:1px solid rgba(255,255,255,.25)}
        @media(max-width:1400px){.command-container{width:min(1280px,calc(100% - 36px))}.command-brand{min-width:350px}.command-brand h1{font-size:21px}.command-nav button{min-width:60px;padding:0 7px;font-size:10px}.command-kpi{padding:11px 10px;grid-template-columns:38px 1fr}.command-kpi-icon{width:37px;height:37px}.command-kpi b{font-size:18px}.command-bottom-grid{grid-template-columns:1.15fr .85fr 1fr}}
        @media(max-width:1120px){.command-header-inner{flex-wrap:wrap;padding:11px 0}.command-brand{min-width:0;flex:1}.command-nav{order:3;flex-basis:100%;height:48px;border-top:1px solid rgba(255,255,255,.12)}.command-nav button.active:after{bottom:0}.command-kpi-ribbon{grid-template-columns:repeat(3,1fr)}.command-kpi:nth-child(3){border-right:0}.command-kpi:nth-child(-n+3){border-bottom:1px solid rgba(180,207,229,.35)}.command-top-grid,.command-bottom-grid{grid-template-columns:1fr 1fr}.command-map-card{grid-column:1/-1}.command-activity-card{grid-column:1/-1}.command-footer .command-container{grid-template-columns:1fr auto}.footer-links{display:none}}
        @media(max-width:760px){.command-container{width:calc(100% - 22px)}.gov-left span:last-child,.gov-right span:not(:last-child){display:none}.command-brand p,.command-profile>div:last-child{display:none}.command-logo{width:68px;height:52px}.command-brand h1{font-size:17px}.command-header-actions{gap:5px}.command-live{padding:0 8px}.command-profile{min-width:auto;padding-left:6px}.command-avatar{width:36px;height:36px}.command-nav{justify-content:flex-start;overflow:auto}.command-nav button{min-width:69px}.command-nav button span{display:none}.command-hero-copy{padding:20px 2px 24px}.command-hero-copy h2{font-size:23px}.hero-visual{width:100%;opacity:.45;mask-image:linear-gradient(90deg,transparent,#000 50%)}.hero-state-mark{display:none}.command-kpi-ribbon{grid-template-columns:1fr 1fr}.command-kpi:nth-child(odd){border-right:1px solid rgba(180,207,229,.35)}.command-kpi:nth-child(even){border-right:0}.command-kpi:nth-child(-n+4){border-bottom:1px solid rgba(180,207,229,.35)}.command-top-grid,.command-bottom-grid{grid-template-columns:1fr}.command-activity-card{grid-column:auto}.workflow-track{grid-template-columns:repeat(9,94px)}.workflow-legend{display:none}.map-live-strip{grid-template-columns:1fr 1fr}.map-live-strip>div:nth-child(2){border-right:0}.map-live-strip>div:last-child{grid-column:1/-1;border-top:1px solid #e1e9ef}.map-workspace{grid-template-columns:1fr}.gujarat-map-canvas svg{height:auto;min-height:250px}.map-district-panel{display:grid;grid-template-columns:1fr 1fr;gap:9px}.map-ranking-head{display:none}.map-ranking-list{align-content:start}.map-data-note{grid-column:1/-1}.quick-grid{grid-template-columns:1fr 1fr}.command-footer .command-container{grid-template-columns:1fr;text-align:center;padding:12px 0}.footer-brand{justify-content:center}.command-footer .command-container>span{display:none}.module-head{flex-direction:column;align-items:flex-start}}

        /* Unified State Branch visual system — government-grade, clean and consistent. */
        .command-page{background:#eef3f7}
        .command-container{width:min(1520px,calc(100% - 48px))}
        .gov-utility{height:32px;background:#fff;color:#465464;font-size:10px;box-shadow:0 1px 0 #dbe3ea}
        .command-header{background:#0a2f5d;border-bottom:4px solid #17814d;box-shadow:0 5px 17px rgba(7,34,65,.16)}
        .command-header-inner{min-height:78px;gap:18px}
        .command-brand{min-width:340px;gap:14px}
        .command-logo{width:82px;height:60px;border-radius:8px;box-shadow:none}
        .command-brand h1{font-size:21px;letter-spacing:-.015em}
        .command-brand p{margin-top:5px;color:#c8d6e5;font-size:9px}
        .command-nav{gap:1px}
        .command-nav button{min-width:62px;padding:0 8px;color:#d5e0eb;font-size:9px;font-weight:800}
        .command-nav button:hover,.command-nav button.active{background:rgba(255,255,255,.065)}
        .command-nav button.active:after{left:8px;right:8px;bottom:0;height:3px;background:#49cf83;box-shadow:none}
        .command-header-actions{gap:8px}
        .command-live{height:36px;padding:0 10px;border-radius:7px;font-size:8px}
        .command-notify,.command-logout{width:36px;height:36px}
        .command-profile{min-width:130px;max-width:165px;padding-left:9px}
        .command-profile>div:last-child{min-width:0}.command-profile b,.command-profile small{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .command-avatar{width:39px;height:39px;flex:0 0 auto}
        .command-module-shell{padding:20px 0 32px}
        .command-module-shell .module-page{overflow:visible;border:1px solid #d5e0ea;border-radius:14px;background:#fff;box-shadow:0 10px 30px rgba(9,35,65,.07)}
        .command-module-shell .module-head{position:relative;padding:20px 22px 18px;border:0;border-bottom:1px solid #d9e3ec;border-radius:14px 14px 0 0;background:#fff;color:#0b315e}
        .command-module-shell .module-head:before{content:"";position:absolute;left:0;top:17px;bottom:17px;width:5px;border-radius:0 5px 5px 0;background:#17814d}
        .command-module-shell .module-head h2{font-size:22px;letter-spacing:-.015em}
        .command-module-shell .module-head p{margin-top:6px;color:#65788b;font-size:10px;font-weight:650}
        .command-module-shell .back-btn{min-height:40px;padding:9px 14px;border:1px solid #c8d6e3;border-radius:8px;background:#f8fafc;color:#20486d;font-size:10px;box-shadow:none}
        .command-module-shell .back-btn:hover{border-color:#7fa0bd;background:#f2f6f9}
        .command-module-shell .module-content{padding:18px;background:#f3f6f9;border-radius:0 0 14px 14px}
        @media(max-width:1350px){.command-container{width:calc(100% - 32px)}.command-brand{min-width:300px}.command-brand h1{font-size:19px}.command-nav button{min-width:55px;padding:0 6px}.command-profile{min-width:115px;max-width:135px}}
        @media(max-width:760px){.command-container{width:calc(100% - 20px)}.command-module-shell{padding:12px 0 22px}.command-module-shell .module-head{padding:17px 17px 15px}.command-module-shell .module-head h2{font-size:19px}.command-module-shell .module-content{padding:11px}.command-profile{min-width:auto}.command-logo{width:66px;height:50px}}
      `}</style>

      <div className="command-page">
        <div className="gov-utility">
          <div className="command-container">
            <div className="gov-left">
              <i className="gov-flag" aria-hidden="true" />
              <span>Government of India</span>
              <span>Ministry of Agriculture &amp; Farmers Welfare</span>
            </div>
            <div className="gov-right">
              <span>Skip to Main Content</span>
              <span>Screen Reader Access</span>
              <span>A− &nbsp; A &nbsp; A+</span>
              <span>◐</span>
              <span>English⌄</span>
            </div>
          </div>
        </div>

        <header className="command-header">
          <div className="command-container command-header-inner">
            <div className="command-brand">
              <div className="command-logo">
                <img src={LOGO_PATH} alt="NAFED" />
              </div>
              <div>
                <h1>NAFED State Branch Portal</h1>
                <p>
                  National Agricultural Cooperative Marketing Federation of
                  India Ltd.
                </p>
              </div>
            </div>

            <nav className="command-nav" aria-label="State Branch navigation">
              <button
                className={tab === "home" ? "active" : ""}
                type="button"
                onClick={() => setTab("home")}
              >
                <PortalIcon name="home" size={17} />
                <span>Home</span>
              </button>
              <button
                className={tab === "applications" ? "active" : ""}
                type="button"
                onClick={() => setTab("applications")}
              >
                <PortalIcon name="projects" size={17} />
                <span>Projects</span>
              </button>
              <button
                className={tab === "pacsDocuments" ? "active" : ""}
                type="button"
                onClick={() => setTab("pacsDocuments")}
              >
                <PortalIcon name="documents" size={17} />
                <span>Documents</span>
              </button>
              <button
                className={tab === "mouExecution" ? "active" : ""}
                type="button"
                onClick={() => setTab("mouExecution")}
              >
                <PortalIcon name="documents" size={17} />
                <span>MOU</span>
              </button>
              <button
                className={tab === "registrationMis" ? "active" : ""}
                type="button"
                onClick={() => setTab("registrationMis")}
              >
                <PortalIcon name="pacs" size={17} />
                <span>PACS</span>
              </button>
              <button
                className={tab === "pmcApproval" ? "active" : ""}
                type="button"
                onClick={() =>
                  openModule(MODULES.find((item) => item.key === "pmcApproval"))
                }
              >
                <PortalIcon name="pmcs" size={17} />
                <span>PMCs</span>
              </button>
              <button
                className={tab === "stateRegistrarApproval" ? "active" : ""}
                type="button"
                onClick={() => setTab("stateRegistrarApproval")}
              >
                <PortalIcon name="approvals" size={17} />
                <span>Approvals</span>
              </button>
              <button
                className={tab === "overview" ? "active" : ""}
                type="button"
                onClick={() => setTab("overview")}
              >
                <PortalIcon name="monitoring" size={17} />
                <span>Monitoring</span>
              </button>
              <button
                type="button"
                onClick={() =>
                  openModule(MODULES.find((item) => item.key === "mis"))
                }
              >
                <PortalIcon name="reports" size={17} />
                <span>Reports &amp; MIS</span>
              </button>
            </nav>

            <div className="command-header-actions">
              <button
                type="button"
                className={`command-live ${liveError ? "error" : ""}`}
                onClick={() => loadDashboard(branchState)}
                title="Refresh dashboard data"
              >
                <i />
                {liveError
                  ? "Sync issue"
                  : `Live ${lastLiveUpdate ? lastLiveUpdate.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "connecting"}`}
              </button>
              <button
                type="button"
                className="command-notify"
                title="Notifications"
                onClick={() =>
                  openModule(
                    MODULES.find((item) => item.key === "notifications"),
                  )
                }
              >
                <PortalIcon name="bell" size={21} />
                <b>
                  {priorityActions.reduce(
                    (sum, item) => sum + Number(item.value || 0),
                    0,
                  )}
                </b>
              </button>
              <div className="command-profile">
                <div className="command-avatar">SB</div>
                <div>
                  <b>
                    {profile?.name || profile?.officerName || "Branch User"}
                  </b>
                  <small>{branchState} State Branch⌄</small>
                </div>
              </div>
              <button
                type="button"
                className="command-logout"
                onClick={logout}
                title="Logout"
              >
                <PortalIcon name="logout" size={19} />
              </button>
            </div>
          </div>
        </header>

        {tab === "home" ? (
          <>
            <section className="command-hero">
              <div className="hero-visual">
                <StorageHeroGraphic />
              </div>
              <div className="command-container">
                <div className="command-hero-copy">
                  <h2>{branchState} Command Centre</h2>
                  <p>
                    Real-time overview of storage infrastructure and project
                    execution
                  </p>
                  <svg
                    className="hero-state-mark"
                    viewBox="0 0 120 100"
                    aria-hidden="true"
                  >
                    <path d="M8 28 36 11l26 5 17-11 25 8 8 18-10 15 11 14-13 13 3 16-18 10-15-10-2-17-16-6-14-14-18-5-5-16-21-7 10-13-13-8z" />
                  </svg>
                </div>

                <div className="command-kpi-ribbon">
                  {commandKpis.map((item) => (
                    <div
                      className={`command-kpi ${item.tone}`}
                      key={item.label}
                    >
                      <div className="command-kpi-icon">
                        <PortalIcon name={item.icon} size={22} />
                      </div>
                      <div>
                        <label>{item.label}</label>
                        <b>{item.value}</b>
                        <small>{item.sub}</small>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <main className="command-main command-container">
              <div className="command-top-grid">
                <section className="command-card command-workflow-card">
                  <div className="command-card-head">
                    <div>
                      <h3>Project Approval &amp; Execution Workflow</h3>
                      <p>End-to-end pipeline status</p>
                    </div>
                    <div className="workflow-legend">
                      <span>
                        <i className="done" />
                        Completed
                      </span>
                      <span>
                        <i className="active" />
                        In Progress
                      </span>
                      <span>
                        <i className="pending" />
                        Pending
                      </span>
                    </div>
                  </div>
                  <div className="workflow-track">
                    {workflowStages.map((stage, index) => (
                      <div
                        className={`workflow-stage ${stage.state}`}
                        key={stage.label}
                      >
                        <div className="workflow-number">
                          {index + 1}
                          {stage.state === "complete" && (
                            <i className="workflow-check">✓</i>
                          )}
                        </div>
                        <strong>{stage.label}</strong>
                        <small>{formatMetricNumber(stage.value || 0)}</small>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="command-card command-map-card">
                  <div className="command-card-head">
                    <div>
                      <h3>{branchState} Operations Map</h3>
                      <p>District-wise project distribution</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setTab("overview")}
                      title="Open monitoring overview"
                    >
                      <PortalIcon name="expand" size={16} />
                    </button>
                  </div>
                  <GujaratOperationsMap projectRows={statePacsProjects} />
                </section>
              </div>

              <div className="command-bottom-grid">
                <section className="command-card command-activity-card">
                  <div className="command-card-head">
                    <div>
                      <h3>Live Activity Feed</h3>
                      <p>Real-time updates from across {branchState}</p>
                    </div>
                    <button type="button" onClick={() => setTab("overview")}>
                      View All
                    </button>
                  </div>
                  <div className="activity-table-wrap">
                    <table className="activity-table">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Activity</th>
                          <th>Entity</th>
                          <th>Location</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activityRows.length ? (
                          activityRows.map((row) => (
                            <tr key={row.id}>
                              <td>
                                <i
                                  className={`activity-dot ${row.complete ? "" : "progress"}`}
                                />
                                {row.time}
                              </td>
                              <td>{row.activity}</td>
                              <td>{row.entity}</td>
                              <td>{row.location}</td>
                              <td>
                                <span
                                  className={`activity-status ${row.complete ? "done" : ""}`}
                                >
                                  {row.complete ? "Completed" : "In Progress"}
                                </span>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td className="empty-activity" colSpan="5">
                              No live project activity is available for{" "}
                              {branchState}.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="command-card">
                  <div className="command-card-head">
                    <div>
                      <h3>Priority Actions</h3>
                      <p>Focus on critical pending tasks</p>
                    </div>
                    <button type="button" onClick={() => setTab("overview")}>
                      View All
                    </button>
                  </div>
                  <div className="priority-list">
                    {priorityActions.map((item) => (
                      <button
                        type="button"
                        className="priority-row"
                        key={item.label}
                        onClick={() => item.module && openModule(item.module)}
                      >
                        <i className={`priority-icon tone-${item.tone}`}>
                          <PortalIcon name={item.icon} size={16} />
                        </i>
                        <span>{item.label}</span>
                        <b>{formatMetricNumber(item.value || 0)}</b>
                      </button>
                    ))}
                  </div>
                </section>

                <section className="command-card">
                  <div className="command-card-head">
                    <div>
                      <h3>Quick Access</h3>
                      <p>Go to key modules</p>
                    </div>
                  </div>
                  <div className="quick-grid">
                    {quickAccess.map((item) => (
                      <button
                        type="button"
                        className="quick-button"
                        key={item.key}
                        onClick={() => item.module && openModule(item.module)}
                      >
                        <i className={`quick-icon tone-${item.tone}`}>
                          <PortalIcon name={item.icon} size={17} />
                        </i>
                        <span>{item.label}</span>
                        <em>›</em>
                      </button>
                    ))}
                  </div>
                </section>
              </div>
            </main>
          </>
        ) : (
          <main className="command-container command-module-shell">
            {renderModule()}
          </main>
        )}

        <footer className="command-footer">
          <div className="command-container">
            <div className="footer-brand">
              <b>NAFED</b>
              <span>
                National Agricultural Cooperative Marketing Federation of India
                Ltd.
              </span>
            </div>
            <span>
              © {new Date().getFullYear()} NAFED. All Rights Reserved.
            </span>
            <div className="footer-links">
              <span>About NAFED</span>
              <span>Terms &amp; Conditions</span>
              <span>Privacy Policy</span>
              <span>Contact Us</span>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
